import React, { useState, useEffect, useCallback, useRef } from "react";
import "../styles/MacroCalculator.css";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Select from "react-select";

const MacroCalculator = () => {
  // Tab management
  const [activeTab, setActiveTab] = useState('daily'); // 'daily' | 'templates' | 'meals'

  // Daily Tracking State (existing)
  const [searchType, setSearchType] = useState("ingredient");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isMetric, setIsMetric] = useState(true);
  const [savedCombinations, setSavedCombinations] = useState([]);
  const [combinationName, setCombinationName] = useState("");
  const [showSaved, setShowSaved] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [recipeIngredients, setRecipeIngredients] = useState({});
  const [expandedRecipes, setExpandedRecipes] = useState({});
  const [loadingRecipeIngredients, setLoadingRecipeIngredients] = useState({});
  const [mealItemsCache, setMealItemsCache] = useState({}); // Cache meal items similar to recipeIngredients
  const [expandedMeals, setExpandedMeals] = useState({});
  const [loadingMealItems, setLoadingMealItems] = useState({});
  const [sections, setSections] = useState([
    { id: 'default', name: 'General', isDefault: true },
    { id: 'breakfast1', name: 'Breakfast 1', isDefault: false },
    { id: 'breakfast2', name: 'Breakfast 2', isDefault: false },
    { id: 'snack1', name: 'Snack 1', isDefault: false },
    { id: 'lunch', name: 'Lunch', isDefault: false },
    { id: 'snack2', name: 'Snack 2', isDefault: false },
    { id: 'dinner1', name: 'Dinner 1', isDefault: false },
    { id: 'dinner2', name: 'Dinner 2', isDefault: false },
    { id: 'dessert', name: 'Dessert', isDefault: false },
    { id: 'snack3', name: 'Snack 3', isDefault: false }
  ]);
  const [currentSection, setCurrentSection] = useState('default');
  const [newSectionName, setNewSectionName] = useState('');
  const [showSectionInput, setShowSectionInput] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [draggedSection, setDraggedSection] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showDetailedMacros, setShowDetailedMacros] = useState(true);
  const [includeRecipeIngredients, setIncludeRecipeIngredients] = useState(false);
  const [combinationDescription, setCombinationDescription] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderInput, setShowFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [collapsedFolders, setCollapsedFolders] = useState({});
  const [draggedFolder, setDraggedFolder] = useState(null);
  const [draggedFolderIndex, setDraggedFolderIndex] = useState(null);
  const [dragOverFolderIndex, setDragOverFolderIndex] = useState(null);
  const [loadedCombinationId, setLoadedCombinationId] = useState(null);
  const [showSaveOptions, setShowSaveOptions] = useState(false);
  const [draggedCombination, setDraggedCombination] = useState(null);
  const [draggedCombinationIndex, setDraggedCombinationIndex] = useState(null);
  const [dragOverCombinationIndex, setDragOverCombinationIndex] = useState(null);

  // Edit mode state - tracks which items are currently being edited
  const [editingItems, setEditingItems] = useState({}); // Daily tab
  const [editingTemplateItems, setEditingTemplateItems] = useState({}); // Templates tab
  const [loadingUnitsFor, setLoadingUnitsFor] = useState({}); // Daily tab - tracks which items are loading units
  const [loadingTemplateUnitsFor, setLoadingTemplateUnitsFor] = useState({}); // Templates tab - tracks which items are loading units

  // Template Editing State (new - for Templates tab)
  const [templateItems, setTemplateItems] = useState([]);
  const [templateSections, setTemplateSections] = useState([
    { id: 'default', name: 'General', isDefault: true },
    { id: 'breakfast1', name: 'Breakfast 1', isDefault: false },
    { id: 'breakfast2', name: 'Breakfast 2', isDefault: false },
    { id: 'snack1', name: 'Snack 1', isDefault: false },
    { id: 'lunch', name: 'Lunch', isDefault: false },
    { id: 'snack2', name: 'Snack 2', isDefault: false },
    { id: 'dinner1', name: 'Dinner 1', isDefault: false },
    { id: 'dinner2', name: 'Dinner 2', isDefault: false },
    { id: 'dessert', name: 'Dessert', isDefault: false },
    { id: 'snack3', name: 'Snack 3', isDefault: false }
  ]);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateFolder, setTemplateFolder] = useState(null);
  const [templateExpandedRecipes, setTemplateExpandedRecipes] = useState({});
  const [templateCollapsedSections, setTemplateCollapsedSections] = useState({});
  const [templateCurrentSection, setTemplateCurrentSection] = useState('default');
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [showTemplateDetailedMacros, setShowTemplateDetailedMacros] = useState(true);

  // Template loading modal (new - for Daily tab)
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Pending item form state (for manual add workflow)
  const [pendingItem, setPendingItem] = useState(null);
  const [pendingQuantity, setPendingQuantity] = useState('1');
  const [pendingSection, setPendingSection] = useState('default');
  const [pendingUnit, setPendingUnit] = useState('');

  // Weekly Plan selector state
  const [weeklyPlanDay, setWeeklyPlanDay] = useState(''); // Selected day
  const [weeklyPlanRecipes, setWeeklyPlanRecipes] = useState([]); // Recipes for selected day
  const [isLoadingWeeklyPlan, setIsLoadingWeeklyPlan] = useState(false);

  // Draft mode state
  const [draftId, setDraftId] = useState(null);
  const [draftLastSaved, setDraftLastSaved] = useState(null);
  const [isDraftMode, setIsDraftMode] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Macro goals state
  const [macroGoals, setMacroGoals] = useState(null); // { protein, carbs, fat, fiber } or null
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [showGoalsView, setShowGoalsView] = useState(false); // Toggle between views
  const [tempGoals, setTempGoals] = useState({ protein: '', carbs: '', fat: '', fiber: '' });

  // Goal Presets State
  const [goalPresets, setGoalPresets] = useState([]); // Array of {id, name, goals}
  const [selectedPresetId, setSelectedPresetId] = useState(null); // Currently loaded preset ID
  const [showPresetSaveModal, setShowPresetSaveModal] = useState(false); // Modal for naming preset
  const [presetName, setPresetName] = useState(''); // Input for preset name
  const [isEditingPreset, setIsEditingPreset] = useState(false); // Update vs create flag
  const [draggedPreset, setDraggedPreset] = useState(null); // Dragged preset for reordering
  const [draggedPresetIndex, setDraggedPresetIndex] = useState(null); // Index of dragged preset
  const [dragOverPresetIndex, setDragOverPresetIndex] = useState(null); // Index of drag over position
  const [editingPresetId, setEditingPresetId] = useState(null); // ID of preset being renamed
  const [editingPresetName, setEditingPresetName] = useState(''); // Name being edited
  const [showPresetManagement, setShowPresetManagement] = useState(false); // Show preset management section

  // Daily Log (Save Day) State
  const [showSaveDayModal, setShowSaveDayModal] = useState(false);
  const [saveModalDate, setSaveModalDate] = useState('');
  const [saveModalNotes, setSaveModalNotes] = useState('');
  const [saveModalGoalType, setSaveModalGoalType] = useState('maintenance');
  const [logExistsForDate, setLogExistsForDate] = useState(false);
  const [isSavingDayLog, setIsSavingDayLog] = useState(false);

  // History Tab State
  const [historyLogs, setHistoryLogs] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Edit Log Date State
  const [editingLogId, setEditingLogId] = useState(null);

  // Edit Goal Type State
  const [showEditGoalTypeModal, setShowEditGoalTypeModal] = useState(false);
  const [editingGoalTypeLogId, setEditingGoalTypeLogId] = useState(null);
  const [editingGoalType, setEditingGoalType] = useState('maintenance');
  const [editLogDate, setEditLogDate] = useState('');
  const [showEditDateModal, setShowEditDateModal] = useState(false);

  // Sticky macro bar state (for mobile)
  const [showStickyBar, setShowStickyBar] = useState(false);
  const macrosDisplayRef = React.useRef(null);

  // Meals Tab State (new - for Meals tab)
  const [mealItems, setMealItems] = useState([]);
  const [editingMealId, setEditingMealId] = useState(null);
  const [mealName, setMealName] = useState('');
  const [mealDescription, setMealDescription] = useState('');
  const [isCreatingMeal, setIsCreatingMeal] = useState(false);
  const [savedMeals, setSavedMeals] = useState([]);
  const [mealExpandedRecipes, setMealExpandedRecipes] = useState({});
  const [editingMealItems, setEditingMealItems] = useState({}); // Inline editing for meal items
  const [loadingMealUnitsFor, setLoadingMealUnitsFor] = useState({}); // Unit loading for meal items
  const [mealCurrentSection, setMealCurrentSection] = useState('default'); // For adding items in meal editor

  // Pending Meal Addition State (for Daily tab - when adding a meal to daily tracking)
  const [pendingMeal, setPendingMeal] = useState(null);
  const [pendingMealQuantity, setPendingMealQuantity] = useState('1');
  const [pendingMealSection, setPendingMealSection] = useState('default');

  // Quick Macro State (for quick add macros feature)
  const [pendingQuickMacro, setPendingQuickMacro] = useState(null);
  const [quickMacroName, setQuickMacroName] = useState('');
  const [quickMacroQuantity, setQuickMacroQuantity] = useState('1');
  const [quickMacroUnit, setQuickMacroUnit] = useState('serving');
  const [quickMacroProtein, setQuickMacroProtein] = useState('');
  const [quickMacroCarbs, setQuickMacroCarbs] = useState('');
  const [quickMacroFat, setQuickMacroFat] = useState('');
  const [quickMacroFiber, setQuickMacroFiber] = useState('');
  const [quickMacroSugar, setQuickMacroSugar] = useState('');
  const [quickMacroSection, setQuickMacroSection] = useState('default');

  // Helper function to get today's date in local timezone (YYYY-MM-DD format)
  const getTodayLocalDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Macro Logs Tab State (History + Analytics)
  const [macroLogsViewMode, setMacroLogsViewMode] = useState('analytics'); // 'history' | 'analytics'

  // Weight Tab State
  const [weightLogs, setWeightLogs] = useState([]);
  const [weightGoals, setWeightGoals] = useState([]);
  const [isLoadingWeight, setIsLoadingWeight] = useState(false);
  const [selectedWeightPeriod, setSelectedWeightPeriod] = useState('30');
  const [weightViewMode, setWeightViewMode] = useState('logs'); // Move viewMode to parent

  // Weight log form state
  const [showWeightLogModal, setShowWeightLogModal] = useState(false);
  const [editingWeightLogId, setEditingWeightLogId] = useState(null);
  const [weightLogDate, setWeightLogDate] = useState(() => getTodayLocalDate());
  const [weightValue, setWeightValue] = useState('');
  const [weightUnit, setWeightUnit] = useState('lbs');
  const [weightTimeOfDay, setWeightTimeOfDay] = useState('morning');
  const [weightNotes, setWeightNotes] = useState('');

  // Weight goal form state
  const [showWeightGoalModal, setShowWeightGoalModal] = useState(false);
  const [editingWeightGoalId, setEditingWeightGoalId] = useState(null);
  const [goalWeight, setGoalWeight] = useState('');
  const [goalTargetDate, setGoalTargetDate] = useState('');
  const [goalStartDate, setGoalStartDate] = useState(() => getTodayLocalDate());
  const [goalStartWeight, setGoalStartWeight] = useState('');
  const [goalNotes, setGoalNotes] = useState('');

  // Weight analytics state
  const [weightAnalytics, setWeightAnalytics] = useState(null);
  const [isLoadingWeightAnalytics, setIsLoadingWeightAnalytics] = useState(false);
  const isFetchingWeightAnalytics = useRef(false); // Track if fetch is in progress
  const [customWeightStartDate, setCustomWeightStartDate] = useState('');
  const [customWeightEndDate, setCustomWeightEndDate] = useState(() => getTodayLocalDate());
  const [macroCalorieData, setMacroCalorieData] = useState(null);
  const [isLoadingMacroCalories, setIsLoadingMacroCalories] = useState(false);

  // Helper function to format time ago
  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);

    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Helper function to get today's date in YYYY-MM-DD format (local timezone)
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format date in local timezone (avoids UTC conversion issues)
  // Takes a date string like "2025-12-28" and returns formatted local date
  const formatLocalDate = React.useCallback((dateString, options = {}) => {
    if (!dateString) return '';

    // Parse date string as local date (not UTC)
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const localDate = new Date(year, month - 1, day);

    const defaultOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    return localDate.toLocaleDateString('en-US', { ...defaultOptions, ...options });
  }, []);

  // Helper function to format goal type for display
  const formatGoalType = (type) => {
    const types = {
      fat_loss: 'Fat Loss',
      performance: 'Performance',
      bulking: 'Bulking',
      maintenance: 'Maintenance'
    };
    return types[type] || 'Maintenance';
  };

  // Save draft to server
  const saveDraft = async () => {
    if (selectedItems.length === 0) {
      return; // Don't save empty drafts
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const draftData = {
        items: selectedItems.map(item => ({
          id: item.id,
          type: item.type,
          item_id: item.type === 'ingredient' ? item.ingredient_id :
                   item.type === 'recipe' ? item.recipe_id :
                   item.type === 'meal' ? item.meal_id : null,
          ingredient_id: item.ingredient_id,
          recipe_id: item.recipe_id,
          meal_id: item.meal_id,
          name: item.name || item.ingredient_name, // Save name for all item types
          description: item.description, // Save description for meals/recipes
          custom_ingredient: item.custom_ingredient || 0,
          section: item.section,
          quantity: item.quantity,
          unit: item.unit || (item.type === 'meal' ? 'serving' : undefined), // Default unit for meals
          conversionFactor: item.conversionFactor, // Save conversion factor
          fromRecipe: item.fromRecipe,
          fromMeal: item.fromMeal,
          mealServings: item.mealServings,
          originalQuantity: item.originalQuantity,
          originalServings: item.originalServings,
          // Add default macros for meal parents (they don't have their own macros)
          protein: item.protein || (item.type === 'meal' ? 0 : undefined),
          carbs: item.carbs || (item.type === 'meal' ? 0 : undefined),
          fat: item.fat || (item.type === 'meal' ? 0 : undefined),
          calories: item.calories || (item.type === 'meal' ? 0 : undefined),
          fiber: item.fiber || (item.type === 'meal' ? 0 : undefined),
          sugar: item.sugar || (item.type === 'meal' ? 0 : undefined)
        })),
        sections: sections,
        goals: macroGoals
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/draft`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(draftData)
        }
      );

      if (response.ok) {
        const result = await response.json();
        setDraftId(result.id);
        setDraftLastSaved(new Date(result.timestamp));
        setSaveError(null);
      } else {
        setSaveError('Failed to save draft');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      setSaveError('Network error');
    } finally {
      setIsSaving(false);
    }
  };

  // Load draft from server
  const loadDraft = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/draft`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const { draft } = await response.json();

        if (draft) {
          // Load sections
          const defaultSections = [
            { id: 'default', name: 'General', isDefault: true },
            { id: 'breakfast1', name: 'Breakfast 1', isDefault: false },
            { id: 'breakfast2', name: 'Breakfast 2', isDefault: false },
            { id: 'snack1', name: 'Snack 1', isDefault: false },
            { id: 'lunch', name: 'Lunch', isDefault: false },
            { id: 'snack2', name: 'Snack 2', isDefault: false },
            { id: 'dinner1', name: 'Dinner 1', isDefault: false },
            { id: 'dinner2', name: 'Dinner 2', isDefault: false },
            { id: 'dessert', name: 'Dessert', isDefault: false },
            { id: 'snack3', name: 'Snack 3', isDefault: false }
          ];

          if (draft.sections && draft.sections.length > 0) {
            setSections(draft.sections);
          } else {
            setSections(defaultSections);
          }

          // Load items
          if (draft.items && draft.items.length > 0) {
            // Map items without fetching units (units will be fetched on-demand when user tries to change them)
            const loadedItems = draft.items.map(item => {
              const itemType = item.item_type || item.type;
              const baseItem = {
                ...item,
                id: `${itemType}-${item.item_id}-${Date.now()}-${Math.random()}`,
                type: itemType,
                name: item.name, // Preserve name for all items
                ingredient_name: item.name, // For ingredients
                availableUnits: [], // Will be populated when user interacts with unit selector
                default_unit_name: item.default_unit_name,
                section: item.section || 'default',
                fromRecipe: item.from_recipe || null,
                fromMeal: item.from_meal || null,
                mealServings: item.meal_servings || item.mealServings,
                conversionFactor: item.conversionFactor || item.conversion_factor,
                originalQuantity: item.originalQuantity || item.original_quantity,
                originalServings: item.originalServings || item.original_servings,
                recipeServings: item.recipeServings || item.recipe_servings,
                description: item.description // Preserve description for meals/recipes
              };

              // Set type-specific IDs
              if (itemType === 'ingredient') {
                baseItem.ingredient_id = item.item_id;
              } else if (itemType === 'recipe') {
                baseItem.recipe_id = item.item_id;
              } else if (itemType === 'meal') {
                baseItem.meal_id = item.item_id;
              }

              return baseItem;
            });

            setSelectedItems(loadedItems);

            // Auto-expand recipes
            const recipeIds = loadedItems
              .filter(item => item.type === 'recipe')
              .map(item => item.recipe_id);
            if (recipeIds.length > 0) {
              const expandedState = {};
              recipeIds.forEach(id => { expandedState[id] = true; });
              setExpandedRecipes(expandedState);
            }

            // Auto-expand meals
            const mealIds = loadedItems
              .filter(item => item.type === 'meal')
              .map(item => item.meal_id);
            if (mealIds.length > 0) {
              const expandedMealState = {};
              mealIds.forEach(id => { expandedMealState[id] = true; });
              setExpandedMeals(expandedMealState);
            }
          }

          // Load goals
          if (draft.goals) {
            setMacroGoals(draft.goals);
            setShowGoalsView(true); // Auto-show goals when loading draft with goals
          } else {
            setMacroGoals(null);
            setShowGoalsView(false);
          }

          setDraftId(draft.id);
          setDraftLastSaved(new Date(draft.last_accessed));
          setIsDraftMode(true);
        }
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  };

  // Clear draft from server
  const clearDraft = async () => {
    if (!window.confirm('Clear your current daily tracking? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/draft`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        // Reset all state
        setSelectedItems([]);
        setSections([
          { id: 'default', name: 'General', isDefault: true },
          { id: 'breakfast1', name: 'Breakfast 1', isDefault: false },
          { id: 'breakfast2', name: 'Breakfast 2', isDefault: false },
          { id: 'snack1', name: 'Snack 1', isDefault: false },
          { id: 'lunch', name: 'Lunch', isDefault: false },
          { id: 'snack2', name: 'Snack 2', isDefault: false },
          { id: 'dinner1', name: 'Dinner 1', isDefault: false },
          { id: 'dinner2', name: 'Dinner 2', isDefault: false },
          { id: 'dessert', name: 'Dessert', isDefault: false },
          { id: 'snack3', name: 'Snack 3', isDefault: false }
        ]);
        setCurrentSection('default');
        setExpandedRecipes({});
        setDraftId(null);
        setDraftLastSaved(null);
        setIsDraftMode(true);
        setRecipeIngredients({});
        setMacroGoals(null);
        setShowGoalsView(false);
        setTempGoals({ protein: '', carbs: '', fat: '', fiber: '' });

        alert('Draft cleared! Starting fresh.');
      }
    } catch (error) {
      console.error('Error clearing draft:', error);
      alert('Failed to clear draft');
    }
  };

  useEffect(() => {
    fetchUserPreferences();
    fetchSavedCombinations();
    fetchAvailableUnits();
    fetchFolders();
    fetchGoalPresets(); // Fetch goal presets
    loadDraft(); // Load draft on component mount
    loadMeals(); // Load meals for Meals tab
  }, []);

  useEffect(() => {
    // Search as user types with debounce
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length > 0) {
        handleSearch();
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType]);

  // Fetch weekly plan recipes when day is selected
  useEffect(() => {
    const fetchWeeklyPlanForDay = async () => {
      if (!weeklyPlanDay || weeklyPlanDay === '' || weeklyPlanDay === 'none') {
        setWeeklyPlanRecipes([]);
        return;
      }

      setIsLoadingWeeklyPlan(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/weekly_plan_recipes?plan_type=current`,
          { credentials: "include" }
        );
        const allRecipes = await response.json();

        // Filter for selected day
        const dayRecipes = allRecipes.filter(r =>
          r.day.toLowerCase() === weeklyPlanDay.toLowerCase()
        );

        setWeeklyPlanRecipes(dayRecipes);
      } catch (error) {
        console.error("Error fetching weekly plan:", error);
        setWeeklyPlanRecipes([]);
      } finally {
        setIsLoadingWeeklyPlan(false);
      }
    };

    fetchWeeklyPlanForDay();
  }, [weeklyPlanDay]);

  // Auto-save draft when items change (debounced by 500ms for near-instant saves)
  // Only save when in Daily tab
  useEffect(() => {
    if (activeTab !== 'daily' || !isDraftMode || selectedItems.length === 0) return;

    const timeoutId = setTimeout(() => {
      saveDraft();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [selectedItems, sections, currentSection, expandedRecipes, isDraftMode, activeTab, macroGoals]);

  // Scroll listener for sticky macro bar (mobile)
  useEffect(() => {
    const handleScroll = () => {
      if (macrosDisplayRef.current) {
        const rect = macrosDisplayRef.current.getBoundingClientRect();
        // Show sticky bar when macros section scrolls out of view (top of section is above viewport)
        setShowStickyBar(rect.bottom < rect.height / 2);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if log exists when save modal date changes
  useEffect(() => {
    if (saveModalDate && showSaveDayModal) {
      checkLogExists(saveModalDate);
    }
  }, [saveModalDate, showSaveDayModal]);

  const fetchUserPreferences = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/getUser`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setIsMetric(data.user.default_unit_of_measurement === "metric");
      }
    } catch (error) {
      console.error("Error fetching user preferences:", error);
    }
  };

  const fetchSavedCombinations = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/saved-combinations`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setSavedCombinations(data);
      }
    } catch (error) {
      console.error("Error fetching saved combinations:", error);
    }
  };

  const fetchAvailableUnits = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/units`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableUnits(data);
      }
    } catch (error) {
      console.error("Error fetching available units:", error);
    }
  };

  const fetchUnitsForIngredient = async (ingredientId, customIngredient) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/ingredient-units/${ingredientId}?custom=${customIngredient ? 1 : 0}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching units for ingredient:", error);
      return [];
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/folders`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setFolders(data);
      }
    } catch (error) {
      console.error("Error fetching folders:", error);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/folders`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: newFolderName.trim() })
        }
      );
      if (response.ok) {
        const data = await response.json();
        setFolders([...folders, { id: data.id, name: data.name }]);
        setNewFolderName('');
        setShowFolderInput(false);
      }
    } catch (error) {
      console.error("Error creating folder:", error);
    }
  };

  const updateFolder = async (folderId, name) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/folders/${folderId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name })
        }
      );
      if (response.ok) {
        setFolders(folders.map(f => f.id === folderId ? { ...f, name } : f));
        setEditingFolderId(null);
        setEditingFolderName('');
      }
    } catch (error) {
      console.error("Error updating folder:", error);
    }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm("Delete this folder? Combinations will be moved to 'Uncategorized'.")) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/folders/${folderId}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      );
      if (response.ok) {
        setFolders(folders.filter(f => f.id !== folderId));
        fetchSavedCombinations(); // Refresh to show updated folder assignments
      }
    } catch (error) {
      console.error("Error deleting folder:", error);
    }
  };

  const moveCombination = async (combinationId, newFolderId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/combination/${combinationId}/move`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ folderId: newFolderId })
        }
      );
      if (response.ok) {
        // Update the combination in state
        setSavedCombinations(savedCombinations.map(c =>
          c.id === combinationId ? { ...c, folder_id: newFolderId } : c
        ));
      }
    } catch (error) {
      console.error("Error moving combination:", error);
    }
  };

  const toggleFolderCollapse = (folderId) => {
    setCollapsedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const handleFolderDragStart = (e, folder, index) => {
    setDraggedFolder(folder);
    setDraggedFolderIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFolderDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolderIndex(index);
  };

  const handleFolderDragEnd = () => {
    setDraggedFolder(null);
    setDraggedFolderIndex(null);
    setDragOverFolderIndex(null);
  };

  const handleFolderDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedFolderIndex === null || draggedFolderIndex === dropIndex) {
      handleFolderDragEnd();
      return;
    }

    const newFolders = [...folders];
    const [movedFolder] = newFolders.splice(draggedFolderIndex, 1);
    newFolders.splice(dropIndex, 0, movedFolder);

    setFolders(newFolders);
    handleFolderDragEnd();

    // Save new order to backend
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/folders/reorder`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ folderIds: newFolders.map(f => f.id) })
        }
      );
    } catch (error) {
      console.error("Error reordering folders:", error);
      // Revert on error
      fetchFolders();
    }
  };

  const handleCombinationDragStart = (e, combination, index, folderId) => {
    setDraggedCombination({ combination, folderId });
    setDraggedCombinationIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCombinationDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCombinationIndex(index);
  };

  const handleCombinationDragEnd = () => {
    setDraggedCombination(null);
    setDraggedCombinationIndex(null);
    setDragOverCombinationIndex(null);
  };

  const handleCombinationDrop = async (e, dropIndex, targetFolderId) => {
    e.preventDefault();

    if (draggedCombinationIndex === null ||
        (draggedCombinationIndex === dropIndex && draggedCombination?.folderId === targetFolderId)) {
      handleCombinationDragEnd();
      return;
    }

    // Get combinations in the source and target folders
    const sourceFolderId = draggedCombination.folderId;
    const targetCombinations = savedCombinations.filter(c =>
      (c.folder_id || null) === targetFolderId
    );

    // If moving within the same folder
    if (sourceFolderId === targetFolderId) {
      const newOrder = [...targetCombinations];
      const [moved] = newOrder.splice(draggedCombinationIndex, 1);
      newOrder.splice(dropIndex, 0, moved);

      // Update state optimistically - we need to rebuild the entire array with the new order
      const otherFolderCombinations = savedCombinations.filter(c =>
        (c.folder_id || null) !== targetFolderId
      );

      // Add sort_order to the reordered combinations
      const reorderedWithSortOrder = newOrder.map((c, index) => ({
        ...c,
        sort_order: index
      }));

      // Combine and set the new state
      setSavedCombinations([...otherFolderCombinations, ...reorderedWithSortOrder]);

      // Save to backend
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/combinations/reorder`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              combinationIds: newOrder.map(c => c.id),
              folderId: targetFolderId
            })
          }
        );

        // Refresh from backend to ensure consistency
        if (response.ok) {
          fetchSavedCombinations();
        }
      } catch (error) {
        console.error("Error reordering combinations:", error);
        fetchSavedCombinations(); // Revert on error
      }
    }

    handleCombinationDragEnd();
  };

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      let endpoint;
      let searchUrl;

      if (searchType === "ingredient") {
        endpoint = `/api/macro-calculator/search-ingredients`;
        searchUrl = `${process.env.REACT_APP_API_URL}${endpoint}?search=${searchQuery}`;
      } else if (searchType === "recipe") {
        endpoint = `/api/macro-calculator/search-recipes`;
        searchUrl = `${process.env.REACT_APP_API_URL}${endpoint}?search=${searchQuery}`;
      } else if (searchType === "meal") {
        // For meals, fetch all meals and filter client-side
        endpoint = `/api/macro-calculator/meals`;
        searchUrl = `${process.env.REACT_APP_API_URL}${endpoint}`;
      }

      const response = await fetch(searchUrl, { credentials: "include" });

      if (response.ok) {
        let data = await response.json();

        // For meals, filter by name client-side
        if (searchType === "meal") {
          data = data.filter(meal =>
            meal.name.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }

        setSearchResults(data);
        setShowDropdown(true);
      }
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const fetchRecipeIngredients = async (recipeId) => {
    setLoadingRecipeIngredients(prev => ({ ...prev, [recipeId]: true }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-ingredients/${recipeId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const ingredients = await response.json();
        setRecipeIngredients(prev => ({ ...prev, [recipeId]: ingredients }));
      }
    } catch (error) {
      console.error("Error fetching recipe ingredients:", error);
    } finally {
      setLoadingRecipeIngredients(prev => ({ ...prev, [recipeId]: false }));
    }
  };

  const toggleRecipeExpanded = async (recipeId) => {
    const isExpanded = expandedRecipes[recipeId];
    setExpandedRecipes(prev => ({ ...prev, [recipeId]: !isExpanded }));

    // Fetch ingredients if not already loaded
    if (!isExpanded && !recipeIngredients[recipeId]) {
      await fetchRecipeIngredients(recipeId);
    }
  };

  const fetchMealItems = async (mealId) => {
    setLoadingMealItems(prev => ({ ...prev, [mealId]: true }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/meals/${mealId}/items`,
        { credentials: "include" }
      );
      if (response.ok) {
        const mealData = await response.json();
        setMealItemsCache(prev => ({ ...prev, [mealId]: mealData.items }));
      }
    } catch (error) {
      console.error("Error fetching meal items:", error);
    } finally {
      setLoadingMealItems(prev => ({ ...prev, [mealId]: false }));
    }
  };

  const toggleMealExpanded = async (mealId) => {
    const isExpanded = expandedMeals[mealId];
    setExpandedMeals(prev => ({ ...prev, [mealId]: !isExpanded }));

    // Fetch items if not already loaded
    if (!isExpanded && !mealItemsCache[mealId]) {
      await fetchMealItems(mealId);
    }
  };


  // Select an item to populate the add form (doesn't add yet)
  const selectItem = (item) => {
    // If selecting a meal, use pending meal instead
    if (searchType === "meal") {
      setPendingMeal(item);
      setPendingMealQuantity('1');
      setPendingMealSection(currentSection);
      setSearchQuery("");
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setPendingItem(item);
    setPendingQuantity('1'); // Always default to 1 for both recipes and ingredients
    setPendingSection(currentSection);

    // Set default unit for ingredients
    if (searchType === "ingredient") {
      const defaultUnit = item.default_unit_name ||
        (item.availableUnits && item.availableUnits.length > 0
          ? item.availableUnits[0].unit_name
          : (isMetric ? "g" : "oz"));
      setPendingUnit(defaultUnit);
    } else {
      setPendingUnit('servings');
    }

    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  // Select a recipe from the weekly plan
  const selectWeeklyPlanRecipe = async (wpRecipe) => {
    try {
      // Fetch full recipe macro data using existing endpoint
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/search-recipes?search=${encodeURIComponent(wpRecipe.name)}`,
        { credentials: "include" }
      );
      const searchResults = await response.json();

      // Find exact match by recipe_id
      const fullRecipe = searchResults.find(r => r.recipe_id === wpRecipe.recipe_id);

      if (fullRecipe) {
        // Use existing selectItem function to set pending state
        // This ensures same workflow as regular recipe search
        selectItem(fullRecipe);

        // Pre-fill quantity from weekly plan
        setPendingQuantity(wpRecipe.meal_qty?.toString() || "1");

        // Clear search input and hide regular search results
        setSearchQuery('');
        setSearchResults([]);
        setShowDropdown(false);

        // Clear weekly plan selection
        setWeeklyPlanDay('');
        setWeeklyPlanRecipes([]);
      } else {
        console.error("Could not find recipe data for:", wpRecipe.name);
      }
    } catch (error) {
      console.error("Error loading weekly plan recipe:", error);
    }
  };

  // Add the pending item to the list
  const addPendingItem = async () => {
    if (!pendingItem) return;

    const item = pendingItem;
    const quantity = parseFloat(pendingQuantity) || 1;
    const section = pendingSection;

    if (searchType === "recipe") {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-macros/${item.recipe_id || item.id}`,
          { credentials: "include" }
        );
        if (response.ok) {
          const macroData = await response.json();
          const recipeId = item.recipe_id || item.id;

          // Add the recipe with the specified quantity (servings)
          const recipeServings = macroData.servings || 1;

          setSelectedItems([...selectedItems, {
            ...item,
            ...macroData,
            type: "recipe",
            recipe_id: recipeId,
            quantity: quantity,
            originalServings: recipeServings,
            id: `recipe-${recipeId}-${Date.now()}`,
            section: section
          }]);

          // Fetch and add recipe ingredients
          let ingredients = recipeIngredients[recipeId];
          if (!ingredients) {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-ingredients/${recipeId}`,
              { credentials: "include" }
            );
            if (response.ok) {
              ingredients = await response.json();
              setRecipeIngredients(prev => ({ ...prev, [recipeId]: ingredients }));
            }
          }

          if (ingredients) {
            // Calculate per-serving quantities (always divide by original servings)
            const perServingFactor = 1 / recipeServings;
            const newItems = ingredients.map(ing => ({
              ...ing,
              type: "ingredient",
              fromRecipe: recipeId,
              id: `recipe-ingredient-${recipeId}-${ing.ingredient_id}-${Date.now()}`,
              ingredient_name: ing.ingredient_name,
              ingredient_id: ing.ingredient_id,
              custom_ingredient: ing.custom_ingredient,
              quantity: (ing.quantity || 1) * perServingFactor, // Store per-serving amount
              unit: ing.unit || ing.unit_name,
              availableUnits: ing.availableUnits || [],
              recipeServings: quantity, // Store current recipe servings as multiplier
              originalQuantity: (ing.quantity || 1) * perServingFactor, // Backup per-serving amount
              section: section
            }));
            setSelectedItems(prev => [...prev, ...newItems]);
          }

          if (!expandedRecipes[recipeId]) {
            setExpandedRecipes(prev => ({ ...prev, [recipeId]: true }));
          }
        }
      } catch (error) {
        console.error("Error fetching recipe macros:", error);
      }
    } else {
      // Add ingredient
      const defaultUnit = item.default_unit_name ||
        (item.availableUnits && item.availableUnits.length > 0
          ? item.availableUnits[0].unit_name
          : (isMetric ? "g" : "oz"));

      const finalUnit = pendingUnit || defaultUnit;

      // Find conversion factor for the selected unit
      const selectedUnitData = item.availableUnits?.find(u => u.unit_name === finalUnit);
      const conversionFactor = selectedUnitData?.conversion_factor;

      setSelectedItems([...selectedItems, {
        ...item,
        type: "ingredient",
        custom_ingredient: item.type === 'custom' ? 1 : 0,
        quantity: quantity,
        unit: finalUnit,
        unit_id: selectedUnitData?.unit_id || item.default_unit_id,
        conversionFactor: conversionFactor, // Set conversion factor
        availableUnits: item.availableUnits || [],
        id: `ingredient-${item.ingredient_id}-${Date.now()}`,
        section: section
      }]);
    }

    // Remember the section that was just used
    setCurrentSection(section);

    // Reset form
    setPendingItem(null);
    setPendingQuantity('1');
    setPendingSection(section);
    setPendingUnit('');
  };

  // Add meal to daily tracking (with section picker and quantity multiplier)
  const confirmPendingMeal = async () => {
    if (!pendingMeal) return;

    const mealServings = parseFloat(pendingMealQuantity) || 1;
    const targetSection = pendingMealSection;

    try {
      // Fetch full meal details with items
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/meals/${pendingMeal.id}/items`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        alert('Failed to load meal');
        return;
      }

      const meal = await response.json();

      if (!meal.items || meal.items.length === 0) {
        alert('This meal has no items');
        return;
      }

      const mealId = pendingMeal.id;

      // 1. Add the MEAL as a parent item
      const mealParentItem = {
        type: "meal",
        meal_id: mealId,
        id: `meal-${mealId}-${Date.now()}`,
        name: meal.name,
        description: meal.description,
        quantity: mealServings,
        section: targetSection
      };

      // 2. Add nested items (ingredients/recipes)
      const nestedItems = [];

      for (const item of meal.items) {
        const baseId = item.type === 'ingredient'
          ? item.ingredient_id || item.item_id
          : item.recipe_id || item.item_id;

        if (item.type === 'recipe') {
          // Add recipe as nested item
          const recipeId = item.recipe_id || item.item_id;

          // Fetch recipe macros
          const recipeResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-macros/${recipeId}`,
            { credentials: "include" }
          );

          if (recipeResponse.ok) {
            const macroData = await recipeResponse.json();
            const recipeServings = macroData.servings || 1;

            const recipeItem = {
              ...item,
              ...macroData,
              type: "recipe",
              recipe_id: recipeId,
              fromMeal: mealId,
              mealServings: mealServings,
              quantity: item.quantity || 1, // Per-serving recipe quantity from meal
              originalServings: recipeServings,
              id: `meal-recipe-${mealId}-${recipeId}-${Date.now()}`,
              section: targetSection
            };
            nestedItems.push(recipeItem);

            // Fetch and add recipe ingredients
            const ingredientsResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-ingredients/${recipeId}`,
              { credentials: "include" }
            );

            if (ingredientsResponse.ok) {
              const ingredients = await ingredientsResponse.json();
              setRecipeIngredients(prev => ({ ...prev, [recipeId]: ingredients }));

              // Calculate per-serving quantities for recipe ingredients
              const perServingFactor = 1 / recipeServings;
              const recipeIngItems = ingredients.map(ing => ({
                ...ing,
                type: "ingredient",
                fromRecipe: recipeId,
                id: `meal-recipe-ingredient-${mealId}-${recipeId}-${ing.ingredient_id}-${Date.now()}`,
                ingredient_name: ing.ingredient_name,
                ingredient_id: ing.ingredient_id,
                custom_ingredient: ing.custom_ingredient,
                quantity: (ing.quantity || 1) * perServingFactor,
                unit: ing.unit || ing.unit_name,
                availableUnits: ing.availableUnits || [],
                recipeServings: (item.quantity || 1) * mealServings, // Recipe qty * meal servings
                originalQuantity: (ing.quantity || 1) * perServingFactor,
                section: targetSection
              }));
              nestedItems.push(...recipeIngItems);
            }
          }
        } else {
          // Add ingredient as nested item
          const ingredientItem = {
            ...item,
            type: "ingredient",
            fromMeal: mealId,
            id: `meal-ingredient-${mealId}-${baseId}-${Date.now()}`,
            quantity: item.quantity || 1, // Per-serving quantity
            mealServings: mealServings,   // Multiplier
            originalQuantity: item.quantity || 1,
            section: targetSection
          };
          nestedItems.push(ingredientItem);
        }
      }

      // Add meal parent + all nested items
      setSelectedItems(prev => [...prev, mealParentItem, ...nestedItems]);

      // Cache meal items for expand/collapse
      setMealItemsCache(prev => ({ ...prev, [mealId]: meal.items }));

      // Auto-expand meal
      setExpandedMeals(prev => ({ ...prev, [mealId]: true }));

      // Auto-expand recipes within meal
      const recipeIds = nestedItems
        .filter(i => i.type === 'recipe' && i.fromMeal === mealId)
        .map(i => i.recipe_id);
      if (recipeIds.length > 0) {
        const newExpandedState = { ...expandedRecipes };
        recipeIds.forEach(id => { newExpandedState[id] = true; });
        setExpandedRecipes(newExpandedState);
      }

      // Clear pending meal form
      setPendingMeal(null);
      setPendingMealQuantity('1');
      setPendingMealSection('default');

    } catch (error) {
      console.error('Error adding meal to daily:', error);
      alert('Failed to add meal. Please try again.');
    }
  };

  // Quick Macro Functions
  const addQuickMacro = () => {
    // Validate required fields
    if (!quickMacroName.trim()) {
      alert('Please enter a name for this item');
      return;
    }

    const protein = parseFloat(quickMacroProtein) || 0;
    const carbs = parseFloat(quickMacroCarbs) || 0;
    const fat = parseFloat(quickMacroFat) || 0;

    if (protein === 0 && carbs === 0 && fat === 0) {
      alert('Please enter at least one macro value (protein, carbs, or fat)');
      return;
    }

    const quantity = parseFloat(quickMacroQuantity) || 1;
    const fiber = parseFloat(quickMacroFiber) || 0;
    const sugar = parseFloat(quickMacroSugar) || 0;

    // Calculate calories from macros
    const calories = (protein * 4) + (carbs * 4) + (fat * 9);

    // Create quick macro item
    const quickMacroItem = {
      id: `quick-macro-${Date.now()}`,
      type: 'quick_macro',
      name: quickMacroName.trim(),
      quantity: quantity,
      unit: quickMacroUnit.trim() || 'serving',
      section: quickMacroSection,

      // Per-serving macros (NOT per 100g)
      protein: protein,
      carbs: carbs,
      fat: fat,
      fiber: fiber,
      sugar: sugar,
      calories: calories,

      // Quick macro specific flags
      isQuickMacro: true,
      perServingMacros: true
    };

    // Add to selected items
    setSelectedItems([...selectedItems, quickMacroItem]);

    // Remember the section that was just used
    setCurrentSection(quickMacroSection);

    // Reset form but keep the quick macro mode active
    setQuickMacroName('');
    setQuickMacroQuantity('1');
    setQuickMacroUnit('serving');
    setQuickMacroProtein('');
    setQuickMacroCarbs('');
    setQuickMacroFat('');
    setQuickMacroFiber('');
    setQuickMacroSugar('');
    // Keep section the same for convenience
    // Keep pendingQuickMacro active so form stays open
  };

  const cancelQuickMacro = () => {
    setPendingQuickMacro(null);
    // Reset all quick macro fields
    setQuickMacroName('');
    setQuickMacroQuantity('1');
    setQuickMacroUnit('serving');
    setQuickMacroProtein('');
    setQuickMacroCarbs('');
    setQuickMacroFat('');
    setQuickMacroFiber('');
    setQuickMacroSugar('');
    setQuickMacroSection('default');
    setSearchType('ingredient'); // Switch back to ingredient search
  };

  const addItem = async (item) => {
    if (searchType === "recipe") {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-macros/${item.recipe_id || item.id}`,
          { credentials: "include" }
        );
        if (response.ok) {
          const macroData = await response.json();
          const recipeId = item.recipe_id || item.id;
          
          console.log('Recipe macro data from API:', {
            name: item.name,
            macroData,
            servings: macroData.servings,
            caloriesPerServing: macroData.calories,
            totalCalories: macroData.calories * (macroData.servings || 1)
          });
          
          // Add the recipe with quantity of 1
          const recipeServings = macroData.servings || 1;

          // API already returns per-serving macros, so use them directly
          setSelectedItems([...selectedItems, {
            ...item,
            ...macroData,
            type: "recipe",
            recipe_id: recipeId, // Explicitly set recipe_id for proper saving
            quantity: 1, // Default to 1 serving
            originalServings: recipeServings, // Store original servings for reference
            id: `recipe-${recipeId}-${Date.now()}`,
            section: currentSection
          }]);
          
          // Fetch recipe ingredients
          let ingredients = recipeIngredients[recipeId];
          if (!ingredients) {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-ingredients/${recipeId}`,
              { credentials: "include" }
            );
            if (response.ok) {
              ingredients = await response.json();
              setRecipeIngredients(prev => ({ ...prev, [recipeId]: ingredients }));
            }
          }
          
          // Auto-add all recipe ingredients (per-serving quantities)
          if (ingredients) {
            // Calculate per-serving quantities (always divide by original servings)
            const perServingFactor = 1 / recipeServings;
            const newItems = ingredients.map(ing => ({
              ...ing,
              type: "ingredient",
              fromRecipe: recipeId,
              id: `recipe-ingredient-${recipeId}-${ing.ingredient_id}-${Date.now()}`,
              ingredient_name: ing.ingredient_name,
              ingredient_id: ing.ingredient_id,
              custom_ingredient: ing.custom_ingredient,
              quantity: (ing.quantity || 1) * perServingFactor, // Store per-serving amount
              unit: ing.unit || ing.unit_name,
              availableUnits: ing.availableUnits || [],
              recipeServings: 1, // Store current recipe servings as multiplier
              originalQuantity: (ing.quantity || 1) * perServingFactor, // Backup per-serving amount
              section: currentSection
            }));
            setSelectedItems(prev => [...prev, ...newItems]);
          }
          
          // Auto-expand the recipe to show ingredients
          if (!expandedRecipes[recipeId]) {
            setExpandedRecipes(prev => ({ ...prev, [recipeId]: true }));
          }
        }
      } catch (error) {
        console.error("Error fetching recipe macros:", error);
      }
    } else {
      // For ingredients, use the default unit or first available unit
      const defaultUnit = item.default_unit_name ||
        (item.availableUnits && item.availableUnits.length > 0
          ? item.availableUnits[0].unit_name
          : (isMetric ? "g" : "oz"));

      // Find conversion factor for the default unit (should be 1 or undefined for default unit)
      const selectedUnitData = item.availableUnits?.find(u => u.unit_name === defaultUnit);
      const conversionFactor = selectedUnitData?.conversion_factor;

      console.log('Adding ingredient:', {
        name: item.ingredient_name,
        defaultUnit: item.default_unit_name,
        protein: item.protein,
        carbs: item.carbs,
        fat: item.fat,
        availableUnits: item.availableUnits,
        type: item.type,
        conversionFactor: conversionFactor
      });

      setSelectedItems([...selectedItems, {
        ...item,
        type: "ingredient",
        custom_ingredient: item.type === 'custom' ? 1 : 0, // Backend returns type: 'custom' or 'default'
        quantity: 1, // Start with quantity 1 instead of 100
        unit: defaultUnit,
        unit_id: selectedUnitData?.unit_id || item.default_unit_id,
        conversionFactor: conversionFactor, // Set conversion factor
        availableUnits: item.availableUnits || [],
        id: `ingredient-${item.ingredient_id}-${Date.now()}`,
        section: currentSection
      }]);
    }
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  const removeItem = (itemId) => {
    const itemToRemove = selectedItems.find(item => item.id === itemId);

    // Confirmation popup
    const itemName = itemToRemove?.ingredient_name || itemToRemove?.name || 'this item';
    if (!window.confirm(`Remove "${itemName}"?`)) {
      return;
    }

    // If removing a recipe, also remove all its ingredients
    if (itemToRemove?.type === 'recipe') {
      const recipeId = itemToRemove.recipe_id || itemToRemove.id;
      setSelectedItems(selectedItems.filter(item =>
        item.id !== itemId && item.fromRecipe !== recipeId
      ));
    }
    // If removing a meal, also remove all nested items
    else if (itemToRemove?.type === 'meal') {
      const mealId = itemToRemove.meal_id;
      setSelectedItems(selectedItems.filter(item =>
        item.id !== itemId && item.fromMeal !== mealId
      ));
    }
    else {
      setSelectedItems(selectedItems.filter(item => item.id !== itemId));
    }
  };

  const updateRecipeIngredientQuantity = (itemId, quantity) => {
    setSelectedItems(selectedItems.map(item => 
      item.id === itemId ? { ...item, quantity: quantity === '' ? '' : parseFloat(quantity) || 0 } : item
    ));
  };

  const updateRecipeIngredientUnit = (itemId, unitName) => {
    setSelectedItems(selectedItems.map(item => {
      if (item.id === itemId) {
        const selectedUnit = item.availableUnits?.find(u => u.unit_name === unitName);
        return { 
          ...item, 
          unit: unitName,
          unit_id: selectedUnit?.unit_id,
          conversionFactor: selectedUnit?.conversion_factor || 1
        };
      }
      return item;
    }));
  };

  const updateItemQuantity = (itemId, quantity) => {
    const newQuantity = quantity === '' ? '' : parseFloat(quantity) || 0;

    setSelectedItems(prevItems => {
      // First update the item itself
      const updatedItems = prevItems.map(item => {
        if (item.id === itemId) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });

      const updatedItem = updatedItems.find(item => item.id === itemId);

      // If it's a recipe, update recipeServings on its ingredients
      if (updatedItem?.type === 'recipe') {
        // DON'T change ingredient quantities - they stay per-serving
        // Only update the recipeServings field on ingredients for macro calculation
        return updatedItems.map(ing => {
          if (ing.fromRecipe === (updatedItem.recipe_id || updatedItem.id)) {
            return {
              ...ing,
              recipeServings: newQuantity // Update the multiplier for macro calculations
            };
          }
          return ing;
        });
      }

      // If it's a meal, update mealServings on ALL nested items (including grandchildren)
      if (updatedItem?.type === 'meal') {
        const mealId = updatedItem.meal_id;
        return updatedItems.map(item => {
          // Update direct children (items with fromMeal)
          if (item.fromMeal === mealId) {
            return {
              ...item,
              mealServings: newQuantity
            };
          }
          // Update grandchildren (recipe ingredients within meal)
          // Check if parent recipe is within this meal
          if (item.fromRecipe) {
            const parentRecipe = updatedItems.find(
              r => r.type === 'recipe' &&
                   r.fromMeal === mealId &&
                   (r.recipe_id || r.id) === item.fromRecipe
            );
            if (parentRecipe) {
              // Update: recipeServings = parentRecipe.quantity * newMealQuantity
              return {
                ...item,
                recipeServings: (parentRecipe.quantity || 1) * newQuantity
              };
            }
          }
          return item;
        });
      }

      return updatedItems;
    });
  };

  const updateItemUnit = (itemId, unitName) => {
    setSelectedItems(selectedItems.map(item => {
      if (item.id === itemId) {
        // Find the conversion factor for the selected unit
        const selectedUnit = item.availableUnits?.find(u => u.unit_name === unitName);

        console.log('Changing unit:', {
          ingredient: item.ingredient_name,
          fromUnit: item.unit,
          toUnit: unitName,
          conversionFactor: selectedUnit?.conversion_factor,
          availableUnits: item.availableUnits
        });

        return {
          ...item,
          unit: unitName,
          unit_id: selectedUnit?.unit_id,
          conversionFactor: selectedUnit?.conversion_factor || 1
        };
      }
      return item;
    }));
  };

  // ==================== UPDATE ITEM SECTION ====================

  const updateItemSection = (itemId, newSectionId) => {
    setSelectedItems(prevItems => {
      const targetItem = prevItems.find(item => item.id === itemId);
      if (!targetItem) return prevItems;

      // Collect items that need to move
      const itemsToMove = [];
      itemsToMove.push(targetItem);

      // If it's a recipe, collect all its ingredients
      if (targetItem.type === 'recipe') {
        const recipeId = targetItem.recipe_id || targetItem.id;
        const recipeIngredients = prevItems.filter(item =>
          item.fromRecipe === recipeId
        );
        itemsToMove.push(...recipeIngredients);
      }

      // If it's a meal, collect ALL nested items (including recipes and their ingredients)
      if (targetItem.type === 'meal') {
        const mealId = targetItem.meal_id;

        // Get direct children (items with fromMeal)
        const directChildren = prevItems.filter(item => item.fromMeal === mealId);
        itemsToMove.push(...directChildren);

        // Get grandchildren (recipe ingredients within meal recipes)
        directChildren.forEach(child => {
          if (child.type === 'recipe') {
            const recipeId = child.recipe_id || child.id;
            const recipeIngredients = prevItems.filter(item =>
              item.fromRecipe === recipeId
            );
            itemsToMove.push(...recipeIngredients);
          }
        });
      }

      // Get IDs of items to move
      const idsToMove = new Set(itemsToMove.map(item => item.id));

      // Remove items that are moving
      const remainingItems = prevItems.filter(item => !idsToMove.has(item.id));

      // Update section on items that are moving
      const updatedMovingItems = itemsToMove.map(item => ({
        ...item,
        section: newSectionId
      }));

      // Find insertion point: after the last item in the target section
      let insertIndex = remainingItems.length;

      for (let i = remainingItems.length - 1; i >= 0; i--) {
        if (remainingItems[i].section === newSectionId && !remainingItems[i].fromRecipe && !remainingItems[i].fromMeal) {
          insertIndex = i + 1;
          break;
        }
      }

      // If we found items in the target section, also include their nested items
      if (insertIndex < remainingItems.length) {
        for (let i = insertIndex; i < remainingItems.length; i++) {
          if (remainingItems[i].fromRecipe || remainingItems[i].fromMeal) {
            const parentRecipe = remainingItems.find(r =>
              r.type === 'recipe' &&
              r.section === newSectionId &&
              (r.recipe_id === remainingItems[i].fromRecipe || r.id === remainingItems[i].fromRecipe)
            );
            if (parentRecipe) {
              insertIndex = i + 1;
            } else {
              break;
            }
          } else {
            break;
          }
        }
      }

      // Insert at the position
      return [
        ...remainingItems.slice(0, insertIndex),
        ...updatedMovingItems,
        ...remainingItems.slice(insertIndex)
      ];
    });
  };

  // ==================== EDIT MODE HANDLERS ====================

  const startEditingItem = async (itemId) => {
    const item = selectedItems.find(i => i.id === itemId);

    if (item && item.type === 'ingredient') {
      // If item doesn't have available units, fetch them
      if (!item.availableUnits || item.availableUnits.length === 0) {
        setLoadingUnitsFor(prev => ({ ...prev, [itemId]: true }));

        const units = await fetchUnitsForIngredient(item.ingredient_id, item.custom_ingredient);

        // Ensure current unit is in the list (even if not in database)
        const currentUnitExists = units.some(u => u.unit_name === item.unit);
        const finalUnits = currentUnitExists
          ? units
          : [
              {
                unit_id: item.unit_id || null,
                unit_name: item.unit,
                conversion_factor: item.conversionFactor || 1
              },
              ...units
            ];

        setSelectedItems(prevItems => prevItems.map(i =>
          i.id === itemId ? { ...i, availableUnits: finalUnits } : i
        ));

        setLoadingUnitsFor(prev => ({ ...prev, [itemId]: false }));
      } else {
        // Item has availableUnits but check if current unit is in the list
        const currentUnitExists = item.availableUnits.some(u => u.unit_name === item.unit);
        if (!currentUnitExists) {
          // Add current unit to the list
          const updatedUnits = [
            {
              unit_id: item.unit_id || null,
              unit_name: item.unit,
              conversion_factor: item.conversionFactor || 1
            },
            ...item.availableUnits
          ];
          setSelectedItems(prevItems => prevItems.map(i =>
            i.id === itemId ? { ...i, availableUnits: updatedUnits } : i
          ));
        }
      }
    }

    setEditingItems(prev => ({ ...prev, [itemId]: true }));
  };

  const saveItemEdit = (itemId) => {
    setEditingItems(prev => ({ ...prev, [itemId]: false }));
    // Draft auto-save happens via existing useEffect (line 294)
  };

  const startEditingTemplateItem = async (itemId) => {
    const item = templateItems.find(i => i.id === itemId);

    if (item && item.type === 'ingredient') {
      // If item doesn't have available units, fetch them
      if (!item.availableUnits || item.availableUnits.length === 0) {
        setLoadingTemplateUnitsFor(prev => ({ ...prev, [itemId]: true }));

        const units = await fetchUnitsForIngredient(item.ingredient_id, item.custom_ingredient);

        // Ensure current unit is in the list (even if not in database)
        const currentUnitExists = units.some(u => u.unit_name === item.unit);
        const finalUnits = currentUnitExists
          ? units
          : [
              {
                unit_id: item.unit_id || null,
                unit_name: item.unit,
                conversion_factor: item.conversionFactor || 1
              },
              ...units
            ];

        setTemplateItems(prevItems => prevItems.map(i =>
          i.id === itemId ? { ...i, availableUnits: finalUnits } : i
        ));

        setLoadingTemplateUnitsFor(prev => ({ ...prev, [itemId]: false }));
      } else {
        // Item has availableUnits but check if current unit is in the list
        const currentUnitExists = item.availableUnits.some(u => u.unit_name === item.unit);
        if (!currentUnitExists) {
          // Add current unit to the list
          const updatedUnits = [
            {
              unit_id: item.unit_id || null,
              unit_name: item.unit,
              conversion_factor: item.conversionFactor || 1
            },
            ...item.availableUnits
          ];
          setTemplateItems(prevItems => prevItems.map(i =>
            i.id === itemId ? { ...i, availableUnits: updatedUnits } : i
          ));
        }
      }
    }

    setEditingTemplateItems(prev => ({ ...prev, [itemId]: true }));
  };

  const saveTemplateItemEdit = (itemId) => {
    setEditingTemplateItems(prev => ({ ...prev, [itemId]: false }));
  };

  // Helper function to render inline macros for compact display
  const renderInlineMacros = (item, itemsArray = selectedItems) => {
    // For recipes, calculate macros from their ingredients
    if (item.type === 'recipe') {
      const recipeIngredients = itemsArray.filter(si =>
        si.fromRecipe === (item.recipe_id || item.id) && si.type === 'ingredient'
      );

      const recipeMacros = recipeIngredients.reduce((acc, ing) => {
        const perServingQty = ing.quantity === '' ? 0 : ing.quantity;
        const servings = ing.recipeServings || 1; // Multiply by recipe servings
        const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
          ? perServingQty * servings
          : perServingQty * servings * ing.conversionFactor;

        return {
          calories: acc.calories + (parseFloat(ing.calories) || 0) * multiplier,
          protein: acc.protein + (parseFloat(ing.protein) || 0) * multiplier,
          carbs: acc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
          fat: acc.fat + (parseFloat(ing.fat) || 0) * multiplier,
          sugar: acc.sugar + (parseFloat(ing.sugar) || 0) * multiplier,
          fiber: acc.fiber + (parseFloat(ing.fiber) || 0) * multiplier,
        };
      }, { calories: 0, protein: 0, carbs: 0, fat: 0, sugar: 0, fiber: 0 });

      return (
        <>
          <strong>{recipeMacros.calories.toFixed(0)}</strong>cal <strong>{recipeMacros.protein.toFixed(0)}</strong>p <strong>{recipeMacros.carbs.toFixed(0)}</strong>c <strong>{recipeMacros.fat.toFixed(0)}</strong>f <strong>{recipeMacros.sugar.toFixed(0)}</strong>sug <strong>{recipeMacros.fiber.toFixed(0)}</strong>fib
        </>
      );
    }

    // For meals, calculate macros from ALL nested items (recursive sum)
    if (item.type === 'meal') {
      const mealId = item.meal_id;

      // Get direct children that are ingredients
      const directIngredients = itemsArray.filter(si =>
        si.fromMeal === mealId && si.type === 'ingredient'
      );

      // Get direct children that are recipes
      const directRecipes = itemsArray.filter(si =>
        si.fromMeal === mealId && si.type === 'recipe'
      );

      // Sum ingredient macros
      let mealMacros = directIngredients.reduce((acc, ing) => {
        const perServingQty = ing.quantity === '' ? 0 : ing.quantity;
        const servings = ing.mealServings || 1;
        const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
          ? perServingQty * servings
          : perServingQty * servings * ing.conversionFactor;

        return {
          calories: acc.calories + (parseFloat(ing.calories) || 0) * multiplier,
          protein: acc.protein + (parseFloat(ing.protein) || 0) * multiplier,
          carbs: acc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
          fat: acc.fat + (parseFloat(ing.fat) || 0) * multiplier,
          sugar: acc.sugar + (parseFloat(ing.sugar) || 0) * multiplier,
          fiber: acc.fiber + (parseFloat(ing.fiber) || 0) * multiplier,
        };
      }, { calories: 0, protein: 0, carbs: 0, fat: 0, sugar: 0, fiber: 0 });

      // Add recipe macros (recursively calculated)
      directRecipes.forEach(recipe => {
        const recipeIngredients = itemsArray.filter(si =>
          si.fromRecipe === (recipe.recipe_id || recipe.id) && si.type === 'ingredient'
        );

        recipeIngredients.forEach(ing => {
          const perServingQty = ing.quantity === '' ? 0 : ing.quantity;
          const servings = ing.recipeServings || 1;
          const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
            ? perServingQty * servings
            : perServingQty * servings * ing.conversionFactor;

          mealMacros.calories += (parseFloat(ing.calories) || 0) * multiplier;
          mealMacros.protein += (parseFloat(ing.protein) || 0) * multiplier;
          mealMacros.carbs += (parseFloat(ing.carbs) || 0) * multiplier;
          mealMacros.fat += (parseFloat(ing.fat) || 0) * multiplier;
          mealMacros.sugar += (parseFloat(ing.sugar) || 0) * multiplier;
          mealMacros.fiber += (parseFloat(ing.fiber) || 0) * multiplier;
        });
      });

      return (
        <>
          <strong>{mealMacros.calories.toFixed(0)}</strong>cal <strong>{mealMacros.protein.toFixed(0)}</strong>p <strong>{mealMacros.carbs.toFixed(0)}</strong>c <strong>{mealMacros.fat.toFixed(0)}</strong>f <strong>{mealMacros.sugar.toFixed(0)}</strong>sug <strong>{mealMacros.fiber.toFixed(0)}</strong>fib
        </>
      );
    }

    // For ingredients, use the item's own macros
    // DON'T multiply by recipeServings when displaying individual ingredients
    // (they always show per-serving amounts in the recipe-ingredients-dropdown)
    const perServingQty = item.quantity === '' ? 0 : item.quantity;
    const factor = (item.conversionFactor || 1);
    const multiplier = perServingQty * factor; // Always per-serving for individual display

    const calories = ((item.calories || 0) * multiplier).toFixed(0);
    const protein = ((item.protein || 0) * multiplier).toFixed(0);
    const carbs = ((item.carbs || 0) * multiplier).toFixed(0);
    const fat = ((item.fat || 0) * multiplier).toFixed(0);
    const sugar = ((item.sugar || 0) * multiplier).toFixed(0);
    const fiber = ((item.fiber || 0) * multiplier).toFixed(0);

    return (
      <>
        <strong>{calories}</strong>cal <strong>{protein}</strong>p <strong>{carbs}</strong>c <strong>{fat}</strong>f <strong>{sugar}</strong>sug <strong>{fiber}</strong>fib
      </>
    );
  };

  // ==================== TEMPLATE-SPECIFIC FUNCTIONS ====================

  const addItemToTemplate = async (item) => {
    if (searchType === "recipe") {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-macros/${item.recipe_id || item.id}`,
          { credentials: "include" }
        );
        if (response.ok) {
          const macroData = await response.json();
          const recipeId = item.recipe_id || item.id;
          const recipeServings = macroData.servings || 1;

          setTemplateItems([...templateItems, {
            ...item,
            ...macroData,
            type: "recipe",
            recipe_id: recipeId,
            quantity: 1, // Default to 1 serving
            originalServings: recipeServings,
            id: `recipe-${recipeId}-${Date.now()}`,
            section: templateCurrentSection
          }]);

          // Fetch recipe ingredients
          let ingredients = recipeIngredients[recipeId];
          if (!ingredients) {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-ingredients/${recipeId}`,
              { credentials: "include" }
            );
            if (response.ok) {
              ingredients = await response.json();
              setRecipeIngredients(prev => ({ ...prev, [recipeId]: ingredients }));
            }
          }

          if (ingredients) {
            // Calculate per-serving quantities (always divide by original servings)
            const perServingFactor = 1 / recipeServings;
            const newItems = ingredients.map(ing => ({
              ...ing,
              type: "ingredient",
              fromRecipe: recipeId,
              id: `recipe-ingredient-${recipeId}-${ing.ingredient_id}-${Date.now()}`,
              ingredient_name: ing.ingredient_name,
              ingredient_id: ing.ingredient_id,
              custom_ingredient: ing.custom_ingredient,
              quantity: (ing.quantity || 1) * perServingFactor, // Store per-serving amount
              unit: ing.unit || ing.unit_name,
              availableUnits: ing.availableUnits || [],
              recipeServings: 1, // Store current recipe servings as multiplier
              originalQuantity: (ing.quantity || 1) * perServingFactor, // Backup per-serving amount
              section: templateCurrentSection
            }));
            setTemplateItems(prev => [...prev, ...newItems]);
          }

          if (!templateExpandedRecipes[recipeId]) {
            setTemplateExpandedRecipes(prev => ({ ...prev, [recipeId]: true }));
          }
        }
      } catch (error) {
        console.error("Error fetching recipe macros:", error);
      }
    } else {
      const defaultUnit = item.default_unit_name ||
        (item.availableUnits && item.availableUnits.length > 0
          ? item.availableUnits[0].unit_name
          : (isMetric ? "g" : "oz"));

      // Find conversion factor for the default unit (should be 1 or undefined for default unit)
      const selectedUnitData = item.availableUnits?.find(u => u.unit_name === defaultUnit);
      const conversionFactor = selectedUnitData?.conversion_factor;

      setTemplateItems([...templateItems, {
        ...item,
        type: "ingredient",
        custom_ingredient: item.type === 'custom' ? 1 : 0,
        quantity: 1,
        unit: defaultUnit,
        unit_id: selectedUnitData?.unit_id || item.default_unit_id,
        conversionFactor: conversionFactor, // Set conversion factor
        availableUnits: item.availableUnits || [],
        id: `ingredient-${item.ingredient_id}-${Date.now()}`,
        section: templateCurrentSection
      }]);
    }
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  const removeTemplateItem = (itemId) => {
    const itemToRemove = templateItems.find(item => item.id === itemId);

    // Confirmation popup
    const itemName = itemToRemove?.ingredient_name || itemToRemove?.name || 'this item';
    if (!window.confirm(`Remove "${itemName}"?`)) {
      return;
    }

    if (itemToRemove?.type === 'recipe') {
      const recipeId = itemToRemove.recipe_id || itemToRemove.id;
      setTemplateItems(templateItems.filter(item =>
        item.id !== itemId && item.fromRecipe !== recipeId
      ));
    } else {
      setTemplateItems(templateItems.filter(item => item.id !== itemId));
    }
  };

  const updateTemplateItemQuantity = (itemId, quantity) => {
    const newQuantity = quantity === '' ? '' : parseFloat(quantity) || 0;

    setTemplateItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.id === itemId) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });

      const updatedItem = updatedItems.find(item => item.id === itemId);
      if (updatedItem?.type === 'recipe') {
        // DON'T change ingredient quantities - they stay per-serving
        // Only update the recipeServings field on ingredients for macro calculation
        return updatedItems.map(ing => {
          if (ing.fromRecipe === (updatedItem.recipe_id || updatedItem.id)) {
            return {
              ...ing,
              recipeServings: newQuantity // Update the multiplier for macro calculations
            };
          }
          return ing;
        });
      }

      return updatedItems;
    });
  };

  const updateTemplateItemUnit = (itemId, unitName) => {
    setTemplateItems(templateItems.map(item => {
      if (item.id === itemId) {
        const selectedUnit = item.availableUnits?.find(u => u.unit_name === unitName);
        return {
          ...item,
          unit: unitName,
          unit_id: selectedUnit?.unit_id,
          conversionFactor: selectedUnit?.conversion_factor || 1
        };
      }
      return item;
    }));
  };

  const updateTemplateItemSection = (itemId, newSectionId) => {
    setTemplateItems(prevItems => {
      const targetItem = prevItems.find(item => item.id === itemId);
      if (!targetItem) return prevItems;

      // Collect items that need to move (the item + its recipe ingredients if it's a recipe)
      const itemsToMove = [];
      itemsToMove.push(targetItem);

      // If it's a recipe, also collect all its ingredients
      if (targetItem.type === 'recipe') {
        const recipeId = targetItem.recipe_id || targetItem.id;
        const recipeIngredients = prevItems.filter(item =>
          item.fromRecipe === recipeId
        );
        itemsToMove.push(...recipeIngredients);
      }

      // Get IDs of items to move for easy filtering
      const idsToMove = new Set(itemsToMove.map(item => item.id));

      // Remove items that are moving
      const remainingItems = prevItems.filter(item => !idsToMove.has(item.id));

      // Update section on items that are moving
      const updatedMovingItems = itemsToMove.map(item => ({
        ...item,
        section: newSectionId
      }));

      // Find insertion point: after the last item in the target section
      let insertIndex = remainingItems.length; // Default to end

      for (let i = remainingItems.length - 1; i >= 0; i--) {
        if (remainingItems[i].section === newSectionId && !remainingItems[i].fromRecipe) {
          insertIndex = i + 1;
          break;
        }
      }

      // If we found items in the target section, also include their recipe ingredients
      if (insertIndex < remainingItems.length) {
        // Check if there are recipe ingredients after the insert point that belong to recipes in this section
        for (let i = insertIndex; i < remainingItems.length; i++) {
          if (remainingItems[i].fromRecipe) {
            const parentRecipe = remainingItems.find(r =>
              r.type === 'recipe' &&
              r.section === newSectionId &&
              (r.recipe_id === remainingItems[i].fromRecipe || r.id === remainingItems[i].fromRecipe)
            );
            if (parentRecipe) {
              insertIndex = i + 1;
            } else {
              break;
            }
          } else {
            break;
          }
        }
      }

      // Insert items at the calculated position
      const result = [
        ...remainingItems.slice(0, insertIndex),
        ...updatedMovingItems,
        ...remainingItems.slice(insertIndex)
      ];

      return result;
    });
  };

  const addTemplateSection = () => {
    if (newSectionName.trim()) {
      const newSection = {
        id: `section-${Date.now()}`,
        name: newSectionName.trim(),
        isDefault: false
      };
      setTemplateSections([...templateSections, newSection]);
      setTemplateCurrentSection(newSection.id);
      setNewSectionName('');
      setShowSectionInput(false);
    }
  };

  const calculateTemplateSectionMacros = (sectionId) => {
    const section = templateSections.find(s => s.id === sectionId);
    const sectionItems = templateItems.filter(item => item.section === sectionId);

    const totals = sectionItems.reduce((acc, item) => {
      if (item.fromRecipe) return acc;

      let itemTotal = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 };

      if (item.type === "quick_macro") {
        // Quick macros are already per-serving, just multiply by quantity
        const quantity = parseFloat(item.quantity) || 1;
        itemTotal = {
          calories: (parseFloat(item.calories) || 0) * quantity,
          protein: (parseFloat(item.protein) || 0) * quantity,
          carbs: (parseFloat(item.carbs) || 0) * quantity,
          fat: (parseFloat(item.fat) || 0) * quantity,
          fiber: (parseFloat(item.fiber) || 0) * quantity,
          sugar: (parseFloat(item.sugar) || 0) * quantity,
        };
      } else if (item.type === "ingredient") {
        const perServingQty = item.quantity === '' ? 0 : item.quantity;
        const servings = item.recipeServings || 1; // Multiply by recipe servings if from recipe
        let quantityInDefaultUnit;
        if (item.unit === item.default_unit_name || !item.conversionFactor || item.conversionFactor === 1) {
          quantityInDefaultUnit = perServingQty * servings;
        } else {
          quantityInDefaultUnit = perServingQty * servings * item.conversionFactor;
        }

        itemTotal = {
          calories: (parseFloat(item.calories) || 0) * quantityInDefaultUnit,
          protein: (parseFloat(item.protein) || 0) * quantityInDefaultUnit,
          carbs: (parseFloat(item.carbs) || 0) * quantityInDefaultUnit,
          fat: (parseFloat(item.fat) || 0) * quantityInDefaultUnit,
          fiber: (parseFloat(item.fiber) || 0) * quantityInDefaultUnit,
          sugar: (parseFloat(item.sugar) || 0) * quantityInDefaultUnit,
        };
      } else if (item.type === "recipe") {
        const recipeIngredients = templateItems.filter(si =>
          si.fromRecipe === (item.recipe_id || item.id) && si.type === "ingredient"
        );

        itemTotal = recipeIngredients.reduce((recAcc, ing) => {
          const perServingQty = ing.quantity === '' ? 0 : ing.quantity;
          const servings = ing.recipeServings || 1; // Multiply by recipe servings
          const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
            ? perServingQty * servings
            : perServingQty * servings * ing.conversionFactor;

          return {
            calories: recAcc.calories + (parseFloat(ing.calories) || 0) * multiplier,
            protein: recAcc.protein + (parseFloat(ing.protein) || 0) * multiplier,
            carbs: recAcc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
            fat: recAcc.fat + (parseFloat(ing.fat) || 0) * multiplier,
            fiber: recAcc.fiber + (parseFloat(ing.fiber) || 0) * multiplier,
            sugar: recAcc.sugar + (parseFloat(ing.sugar) || 0) * multiplier,
          };
        }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
      }

      return {
        calories: acc.calories + itemTotal.calories,
        protein: acc.protein + itemTotal.protein,
        carbs: acc.carbs + itemTotal.carbs,
        fat: acc.fat + itemTotal.fat,
        fiber: acc.fiber + itemTotal.fiber,
        sugar: acc.sugar + itemTotal.sugar,
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });

    return totals;
  };

  const calculateTemplateTotalMacros = () => {
    return templateSections.reduce((acc, section) => {
      const sectionMacros = calculateTemplateSectionMacros(section.id);
      return {
        calories: acc.calories + sectionMacros.calories,
        protein: acc.protein + sectionMacros.protein,
        carbs: acc.carbs + sectionMacros.carbs,
        fat: acc.fat + sectionMacros.fat,
        fiber: acc.fiber + sectionMacros.fiber,
        sugar: acc.sugar + sectionMacros.sugar,
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
  };

  const toggleTemplateSectionCollapse = (sectionId) => {
    setTemplateCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const toggleTemplateRecipeExpanded = async (recipeId) => {
    const isExpanded = templateExpandedRecipes[recipeId];
    setTemplateExpandedRecipes(prev => ({ ...prev, [recipeId]: !isExpanded }));

    if (!isExpanded && !recipeIngredients[recipeId]) {
      await fetchRecipeIngredients(recipeId);
    }
  };

  const handleAddUnit = (item) => {
    // Redirect to the user ingredients page to configure units for this ingredient
    if (item.type === 'custom') {
      window.open(`/user_ingredients?highlight=${item.ingredient_id}`, '_blank');
    } else {
      alert('To add units for this ingredient, please add it to your custom ingredients first.');
    }
  };

  const addSection = () => {
    if (newSectionName.trim()) {
      const newSection = {
        id: `section-${Date.now()}`,
        name: newSectionName.trim(),
        isDefault: false
      };
      setSections([...sections, newSection]);
      setCurrentSection(newSection.id);
      setNewSectionName('');
      setShowSectionInput(false);
    }
  };

  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleDragStart = (e, section, index) => {
    setDraggedSection({ section, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedSection(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (!draggedSection) return;

    const newSections = [...sections];
    const [movedSection] = newSections.splice(draggedSection.index, 1);
    newSections.splice(dropIndex, 0, movedSection);
    setSections(newSections);
    setDraggedSection(null);
    setDragOverIndex(null);
  };

  // ==================== SECTION REORDERING WITH ARROW BUTTONS ====================

  const moveSectionUp = (index) => {
    // Get sections with items (visible sections)
    const sectionsWithItems = sections.filter((s) => {
      const items = selectedItems.filter(item => !item.fromRecipe && !item.fromMeal && item.section === s.id);
      return items.length > 0;
    });

    // Find current section in the filtered list
    const currentSection = sections[index];
    const visibleIndex = sectionsWithItems.findIndex(s => s.id === currentSection.id);

    if (visibleIndex <= 0) return; // Already at top of visible sections

    // Get the section to swap with (previous visible section)
    const previousSection = sectionsWithItems[visibleIndex - 1];

    // Find their indices in the full sections array
    const currentGlobalIndex = sections.findIndex(s => s.id === currentSection.id);
    const previousGlobalIndex = sections.findIndex(s => s.id === previousSection.id);

    // Swap them
    const newSections = [...sections];
    [newSections[currentGlobalIndex], newSections[previousGlobalIndex]] =
      [newSections[previousGlobalIndex], newSections[currentGlobalIndex]];

    setSections(newSections);
  };

  const moveSectionDown = (index) => {
    // Get sections with items (visible sections)
    const sectionsWithItems = sections.filter((s) => {
      const items = selectedItems.filter(item => !item.fromRecipe && !item.fromMeal && item.section === s.id);
      return items.length > 0;
    });

    // Find current section in the filtered list
    const currentSection = sections[index];
    const visibleIndex = sectionsWithItems.findIndex(s => s.id === currentSection.id);

    if (visibleIndex === -1 || visibleIndex >= sectionsWithItems.length - 1) {
      return; // Already at bottom of visible sections
    }

    // Get the section to swap with (next visible section)
    const nextSection = sectionsWithItems[visibleIndex + 1];

    // Find their indices in the full sections array
    const currentGlobalIndex = sections.findIndex(s => s.id === currentSection.id);
    const nextGlobalIndex = sections.findIndex(s => s.id === nextSection.id);

    // Swap them
    const newSections = [...sections];
    [newSections[currentGlobalIndex], newSections[nextGlobalIndex]] =
      [newSections[nextGlobalIndex], newSections[currentGlobalIndex]];

    setSections(newSections);
  };

  const calculateSectionMacros = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    const sectionItems = selectedItems.filter(item => item.section === sectionId);

    const totals = sectionItems.reduce((acc, item) => {
      // Skip nested items - they're counted with their parent
      if (item.fromRecipe || item.fromMeal) return acc;

      let itemTotal = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 };

      if (item.type === "quick_macro") {
        // Quick macros are already per-serving, just multiply by quantity
        const quantity = parseFloat(item.quantity) || 1;
        itemTotal = {
          calories: (parseFloat(item.calories) || 0) * quantity,
          protein: (parseFloat(item.protein) || 0) * quantity,
          carbs: (parseFloat(item.carbs) || 0) * quantity,
          fat: (parseFloat(item.fat) || 0) * quantity,
          fiber: (parseFloat(item.fiber) || 0) * quantity,
          sugar: (parseFloat(item.sugar) || 0) * quantity,
        };
      } else if (item.type === "ingredient") {
        const perServingQty = item.quantity === '' ? 0 : item.quantity;
        const servings = item.recipeServings || item.mealServings || 1;
        let quantityInDefaultUnit;
        if (item.unit === item.default_unit_name || !item.conversionFactor || item.conversionFactor === 1) {
          quantityInDefaultUnit = perServingQty * servings;
        } else {
          quantityInDefaultUnit = perServingQty * servings * item.conversionFactor;
        }

        itemTotal = {
          calories: (parseFloat(item.calories) || 0) * quantityInDefaultUnit,
          protein: (parseFloat(item.protein) || 0) * quantityInDefaultUnit,
          carbs: (parseFloat(item.carbs) || 0) * quantityInDefaultUnit,
          fat: (parseFloat(item.fat) || 0) * quantityInDefaultUnit,
          fiber: (parseFloat(item.fiber) || 0) * quantityInDefaultUnit,
          sugar: (parseFloat(item.sugar) || 0) * quantityInDefaultUnit,
        };
      } else if (item.type === "recipe") {
        // For recipes, sum up their ingredients
        const recipeIngredients = selectedItems.filter(si =>
          si.fromRecipe === (item.recipe_id || item.id) && si.type === "ingredient"
        );

        itemTotal = recipeIngredients.reduce((recAcc, ing) => {
          const perServingQty = ing.quantity === '' ? 0 : ing.quantity;
          const servings = ing.recipeServings || 1; // Multiply by recipe servings
          const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
            ? perServingQty * servings
            : perServingQty * servings * ing.conversionFactor;

          return {
            calories: recAcc.calories + (parseFloat(ing.calories) || 0) * multiplier,
            protein: recAcc.protein + (parseFloat(ing.protein) || 0) * multiplier,
            carbs: recAcc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
            fat: recAcc.fat + (parseFloat(ing.fat) || 0) * multiplier,
            fiber: recAcc.fiber + (parseFloat(ing.fiber) || 0) * multiplier,
            sugar: recAcc.sugar + (parseFloat(ing.sugar) || 0) * multiplier,
          };
        }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
      } else if (item.type === "meal") {
        // For meals, sum ALL nested items (ingredients + recipes)
        const mealId = item.meal_id;

        // Get direct ingredient children
        const directIngredients = selectedItems.filter(si =>
          si.fromMeal === mealId && si.type === 'ingredient'
        );

        // Get direct recipe children
        const directRecipes = selectedItems.filter(si =>
          si.fromMeal === mealId && si.type === 'recipe'
        );

        // Sum ingredient macros
        itemTotal = directIngredients.reduce((mealAcc, ing) => {
          const perServingQty = ing.quantity === '' ? 0 : ing.quantity;
          const servings = ing.mealServings || 1;
          const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
            ? perServingQty * servings
            : perServingQty * servings * ing.conversionFactor;

          return {
            calories: mealAcc.calories + (parseFloat(ing.calories) || 0) * multiplier,
            protein: mealAcc.protein + (parseFloat(ing.protein) || 0) * multiplier,
            carbs: mealAcc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
            fat: mealAcc.fat + (parseFloat(ing.fat) || 0) * multiplier,
            fiber: mealAcc.fiber + (parseFloat(ing.fiber) || 0) * multiplier,
            sugar: mealAcc.sugar + (parseFloat(ing.sugar) || 0) * multiplier,
          };
        }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });

        // Add recipe macros
        directRecipes.forEach(recipe => {
          const recipeIngredients = selectedItems.filter(si =>
            si.fromRecipe === (recipe.recipe_id || recipe.id) && si.type === 'ingredient'
          );

          recipeIngredients.forEach(ing => {
            const perServingQty = ing.quantity === '' ? 0 : ing.quantity;
            const servings = ing.recipeServings || 1;
            const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
              ? perServingQty * servings
              : perServingQty * servings * ing.conversionFactor;

            itemTotal.calories += (parseFloat(ing.calories) || 0) * multiplier;
            itemTotal.protein += (parseFloat(ing.protein) || 0) * multiplier;
            itemTotal.carbs += (parseFloat(ing.carbs) || 0) * multiplier;
            itemTotal.fat += (parseFloat(ing.fat) || 0) * multiplier;
            itemTotal.fiber += (parseFloat(ing.fiber) || 0) * multiplier;
            itemTotal.sugar += (parseFloat(ing.sugar) || 0) * multiplier;
          });
        });
      }

      return {
        calories: acc.calories + itemTotal.calories,
        protein: acc.protein + itemTotal.protein,
        carbs: acc.carbs + itemTotal.carbs,
        fat: acc.fat + itemTotal.fat,
        fiber: acc.fiber + itemTotal.fiber,
        sugar: acc.sugar + itemTotal.sugar,
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });

    return totals;
  };

  const removeSection = (sectionId) => {
    if (sections.find(s => s.id === sectionId)?.isDefault) return;
    
    // Move all items from this section to default section
    setSelectedItems(selectedItems.map(item => 
      item.section === sectionId ? { ...item, section: 'default' } : item
    ));
    
    // Remove the section
    setSections(sections.filter(s => s.id !== sectionId));
    
    // If current section was removed, switch to default
    if (currentSection === sectionId) {
      setCurrentSection('default');
    }
  };

  const calculateTotalMacros = () => {
    // Sum up per-serving macros from all sections
    return sections.reduce((acc, section) => {
      const sectionMacros = calculateSectionMacros(section.id);
      return {
        calories: acc.calories + sectionMacros.calories,
        protein: acc.protein + sectionMacros.protein,
        carbs: acc.carbs + sectionMacros.carbs,
        fat: acc.fat + sectionMacros.fat,
        fiber: acc.fiber + sectionMacros.fiber,
        sugar: acc.sugar + sectionMacros.sugar,
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
  };

  // ==================== MACRO GOALS HELPER FUNCTIONS ====================

  // Calculate calories from P/C/F macros (4-4-9 formula)
  const calculateCaloriesFromMacros = (protein, carbs, fat) => {
    return (protein * 4) + (carbs * 4) + (fat * 9);
  };

  const openGoalsModal = () => {
    setTempGoals(macroGoals || { protein: '', carbs: '', fat: '', fiber: '' });

    // Check if current goals match a preset
    if (macroGoals) {
      const matchingPreset = goalPresets.find(preset =>
        preset.goals.protein === macroGoals.protein &&
        preset.goals.carbs === macroGoals.carbs &&
        preset.goals.fat === macroGoals.fat &&
        preset.goals.fiber === macroGoals.fiber
      );
      setSelectedPresetId(matchingPreset?.id || null);
    } else {
      setSelectedPresetId(null);
    }

    setShowGoalsModal(true);
  };

  const saveGoals = () => {
    const goals = {
      protein: parseFloat(tempGoals.protein) || 0,
      carbs: parseFloat(tempGoals.carbs) || 0,
      fat: parseFloat(tempGoals.fat) || 0,
      fiber: parseFloat(tempGoals.fiber) || 0
    };
    setMacroGoals(goals);
    setShowGoalsView(true);
    setShowGoalsModal(false);
  };

  const clearGoals = () => {
    if (window.confirm('Clear your macro goals? This will remove them from your daily tracking.')) {
      setMacroGoals(null);
      setShowGoalsView(false);
    }
  };

  // ==================== DAILY LOG (SAVE DAY) FUNCTIONS ====================

  // Check if a log exists for a given date
  const checkLogExists = async (date) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/daily-log/${date}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setLogExistsForDate(!!data.log);
      } else {
        setLogExistsForDate(false);
      }
    } catch (error) {
      setLogExistsForDate(false);
    }
  };

  // Handle saving daily log
  const handleSaveDayLog = async () => {
    setIsSavingDayLog(true);

    try {
      // Calculate totals
      const totals = calculateTotalMacros();

      // Prepare payload
      const payload = {
        log_date: saveModalDate,
        notes: saveModalNotes.trim() || null,
        items: selectedItems.map((item, index) => ({
          type: item.type,
          item_id: item.ingredient_id || item.recipe_id || item.meal_id || null,
          custom_ingredient: item.custom_ingredient || 0,
          quantity: item.quantity,
          unit: item.unit,
          conversion_factor: item.conversionFactor || null,
          section: item.section,
          from_recipe: item.fromRecipe || null,
          from_meal: item.fromMeal || null,
          sort_order: index
        })),
        sections: sections,
        goals: macroGoals ? {
          ...macroGoals,
          goal_type: saveModalGoalType
        } : null,
        totals: totals
      };

      // Call API
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/save-daily-log`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Success!
        alert(`Log saved for ${saveModalDate}`);

        // Clear draft (per user preference)
        setSelectedItems([]);
        setDraftId(null);
        setDraftLastSaved(null);

        // Reset modal
        setSaveModalNotes('');
        setSaveModalDate(getLocalDateString());
        setSaveModalGoalType('maintenance'); // Reset to default
        setShowSaveDayModal(false);

        // Refresh history if on macro logs tab with history view
        if (activeTab === 'macroLogs' && macroLogsViewMode === 'history') {
          fetchHistoryLogs();
        }
      } else {
        alert(data.error || 'Failed to save log');
      }
    } catch (error) {
      console.error('Error saving log:', error);
      alert('Failed to save log');
    } finally {
      setIsSavingDayLog(false);
    }
  };

  // ==================== HISTORY TAB FUNCTIONS ====================

  // Fetch all daily logs
  const fetchHistoryLogs = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/daily-logs`,
        { credentials: 'include' }
      );
      const data = await response.json();
      setHistoryLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Fetch most recent log's goal type to pre-fill save modal
  const fetchMostRecentGoalType = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/daily-logs?limit=1`,
        { credentials: 'include' }
      );
      const data = await response.json();

      if (data.logs && data.logs.length > 0 && data.logs[0].goals?.goal_type) {
        setSaveModalGoalType(data.logs[0].goals.goal_type);
      } else {
        // No logs or no goal type, default to maintenance
        setSaveModalGoalType('maintenance');
      }
    } catch (error) {
      console.error('Error fetching most recent goal type:', error);
      // On error, default to maintenance
      setSaveModalGoalType('maintenance');
    }
  };

  // Load a log into daily tracker
  const loadLogIntoDaily = async (logDate) => {
    try {
      // Convert date to YYYY-MM-DD format (handle both Date objects and strings)
      const dateStr = typeof logDate === 'string' && logDate.includes('T')
        ? logDate.split('T')[0]
        : (logDate instanceof Date ? logDate.toISOString().split('T')[0] : logDate);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/daily-log/${dateStr}`,
        { credentials: 'include' }
      );
      const data = await response.json();

      if (data.log) {
        // Convert items back to selectedItems format
        const loadedItems = data.log.items.map(item => ({
          id: `loaded-${item.id}`,
          type: item.item_type,
          ingredient_id: item.item_type === 'ingredient' ? item.item_id : null,
          recipe_id: item.item_type === 'recipe' ? item.item_id : null,
          meal_id: item.item_type === 'meal' ? item.item_id : null,
          ingredient_name: item.name,
          name: item.name,
          custom_ingredient: item.custom_ingredient,
          quantity: item.quantity,
          unit: item.unit,
          conversionFactor: item.conversion_factor,
          section: item.section,
          fromRecipe: item.from_recipe,
          fromMeal: item.from_meal,
          // Store raw macro data from database for ingredients
          protein: item.ingredient_protein || 0,
          carbs: item.ingredient_carbs || 0,
          fat: item.ingredient_fat || 0,
          fiber: item.ingredient_fiber || 0,
          sugar: item.ingredient_sugar || 0
          // Note: Calories and full macros will be calculated by the app's existing logic
        }));

        setSelectedItems(loadedItems);
        setSections(data.log.sections || sections);
        setMacroGoals(data.log.goals);

        // Pre-fill goal type if it exists
        if (data.log.goals?.goal_type) {
          setSaveModalGoalType(data.log.goals.goal_type);
        }

        // Switch to Daily tab
        setActiveTab('daily');

        alert(`Loaded log from ${formatLocalDate(logDate)}`);
      }
    } catch (error) {
      console.error('Error loading log:', error);
      alert('Failed to load log');
    }
  };

  // Delete a log
  const deleteHistoryLog = async (logId, logDate) => {
    if (!window.confirm(`Delete log from ${formatLocalDate(logDate)}? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/daily-log/${logId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Log deleted successfully');
        // Refresh history list
        fetchHistoryLogs();
      } else {
        alert('Failed to delete log');
      }
    } catch (error) {
      console.error('Error deleting log:', error);
      alert('Failed to delete log');
    }
  };

  // Open edit date modal for a log
  const openEditDateModal = (logId, currentDate) => {
    setEditingLogId(logId);
    setEditLogDate(currentDate.split('T')[0]); // Ensure YYYY-MM-DD format
    setShowEditDateModal(true);
  };

  // Update a log's date
  const handleUpdateLogDate = async () => {
    if (!editingLogId || !editLogDate) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/daily-log/${editingLogId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ log_date: editLogDate })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        alert(`Log date updated to ${formatLocalDate(editLogDate)}`);
        setShowEditDateModal(false);
        setEditingLogId(null);
        setEditLogDate('');
        // Refresh history list
        fetchHistoryLogs();
      } else {
        alert(data.error || 'Failed to update log date');
      }
    } catch (error) {
      console.error('Error updating log date:', error);
      alert('Failed to update log date');
    }
  };

  // Open edit goal type modal
  const openEditGoalTypeModal = (logId, currentGoalType) => {
    setEditingGoalTypeLogId(logId);
    setEditingGoalType(currentGoalType || 'maintenance');
    setShowEditGoalTypeModal(true);
  };

  // Update a log's goal type
  const handleUpdateGoalType = async () => {
    if (!editingGoalTypeLogId) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/daily-log/${editingGoalTypeLogId}/goal-type`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ goal_type: editingGoalType })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        alert(`Goal type updated to ${formatGoalType(editingGoalType)}`);
        setShowEditGoalTypeModal(false);
        setEditingGoalTypeLogId(null);
        setEditingGoalType('maintenance');
        // Refresh history list
        fetchHistoryLogs();
      } else {
        alert(data.error || 'Failed to update goal type');
      }
    } catch (error) {
      console.error('Error updating goal type:', error);
      alert('Failed to update goal type');
    }
  };

  // ==================== GOAL PRESETS FUNCTIONS ====================

  // Fetch goal presets on mount
  const fetchGoalPresets = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/goal-presets`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setGoalPresets(data);
      }
    } catch (error) {
      console.error("Error fetching goal presets:", error);
    }
  };

  // Load preset into form (doesn't save to macroGoals yet)
  const loadPreset = (presetId) => {
    if (!presetId) {
      // "None" selected - clear selection but keep current form values
      setSelectedPresetId(null);
      return;
    }

    const preset = goalPresets.find(p => p.id === parseInt(presetId));
    if (preset) {
      setTempGoals({
        protein: preset.goals.protein.toString(),
        carbs: preset.goals.carbs.toString(),
        fat: preset.goals.fat.toString(),
        fiber: preset.goals.fiber.toString()
      });
      setSelectedPresetId(preset.id);
    }
  };

  // Open save preset modal
  const openSavePresetModal = () => {
    // Check if tempGoals has values
    if (!tempGoals.protein && !tempGoals.carbs && !tempGoals.fat && !tempGoals.fiber) {
      alert('Please enter macro values before saving a preset');
      return;
    }

    // If a preset is selected, pre-fill the name for updating
    if (selectedPresetId) {
      const preset = goalPresets.find(p => p.id === selectedPresetId);
      setPresetName(preset?.name || '');
      setIsEditingPreset(true);
    } else {
      setPresetName('');
      setIsEditingPreset(false);
    }

    setShowPresetSaveModal(true);
  };

  // Save preset (create or update)
  const savePreset = async () => {
    if (!presetName.trim()) {
      alert('Please enter a preset name');
      return;
    }

    const goals = {
      protein: parseFloat(tempGoals.protein) || 0,
      carbs: parseFloat(tempGoals.carbs) || 0,
      fat: parseFloat(tempGoals.fat) || 0,
      fiber: parseFloat(tempGoals.fiber) || 0
    };

    try {
      let response;

      if (isEditingPreset && selectedPresetId) {
        // Update existing preset
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/goal-presets/${selectedPresetId}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: presetName.trim(), goals })
          }
        );
      } else {
        // Create new preset
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/goal-presets`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: presetName.trim(), goals })
          }
        );
      }

      if (response.ok) {
        const result = await response.json();

        if (isEditingPreset) {
          // Update preset in list
          setGoalPresets(goalPresets.map(p =>
            p.id === selectedPresetId
              ? { ...p, name: presetName.trim(), goals }
              : p
          ));
        } else {
          // Add new preset to list
          setGoalPresets([...goalPresets, {
            id: result.id,
            name: result.name,
            goals: result.goals
          }]);
          setSelectedPresetId(result.id); // Select the newly created preset
        }

        setShowPresetSaveModal(false);
        setPresetName('');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to save preset');
      }
    } catch (error) {
      console.error('Error saving preset:', error);
      alert('Network error while saving preset');
    }
  };

  // Delete preset
  const deletePreset = async (presetId) => {
    const preset = goalPresets.find(p => p.id === presetId);
    if (!preset) return;

    if (!window.confirm(`Delete preset "${preset.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/goal-presets/${presetId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        setGoalPresets(goalPresets.filter(p => p.id !== presetId));

        // Clear selection if deleted preset was selected
        if (selectedPresetId === presetId) {
          setSelectedPresetId(null);
        }
      } else {
        alert('Failed to delete preset');
      }
    } catch (error) {
      console.error('Error deleting preset:', error);
      alert('Network error while deleting preset');
    }
  };

  // Rename preset (inline editing)
  const renamePreset = async (presetId, newName) => {
    if (!newName.trim()) {
      alert('Please enter a preset name');
      return;
    }

    try {
      const preset = goalPresets.find(p => p.id === presetId);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/goal-presets/${presetId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName.trim(), goals: preset.goals })
        }
      );

      if (response.ok) {
        setGoalPresets(goalPresets.map(p =>
          p.id === presetId ? { ...p, name: newName.trim() } : p
        ));
        setEditingPresetId(null);
        setEditingPresetName('');
      } else {
        alert('Failed to rename preset');
      }
    } catch (error) {
      console.error('Error renaming preset:', error);
      alert('Network error while renaming preset');
    }
  };

  // Preset drag handlers
  const handlePresetDragStart = (e, preset, index) => {
    setDraggedPreset(preset);
    setDraggedPresetIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePresetDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverPresetIndex(index);
  };

  const handlePresetDragEnd = () => {
    setDraggedPreset(null);
    setDraggedPresetIndex(null);
    setDragOverPresetIndex(null);
  };

  const handlePresetDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedPresetIndex === null || draggedPresetIndex === dropIndex) {
      handlePresetDragEnd();
      return;
    }

    const newPresets = [...goalPresets];
    const [moved] = newPresets.splice(draggedPresetIndex, 1);
    newPresets.splice(dropIndex, 0, moved);

    setGoalPresets(newPresets);
    handlePresetDragEnd();

    // Save new order to backend
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/goal-presets/reorder`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ presetIds: newPresets.map(p => p.id) })
        }
      );
    } catch (error) {
      console.error('Error reordering presets:', error);
      // Revert on error
      fetchGoalPresets();
    }
  };

  // ==================== END GOAL PRESETS FUNCTIONS ====================

  // Helper to determine if a macro value needs visual feedback
  const getMacroStatus = (current, goal) => {
    if (!goal) return '';
    const percentage = (current / goal) * 100;
    if (percentage > 100) return 'over-goal';
    if (percentage < 90) return 'under-goal';
    return ''; // Within 90-100% range
  };

  // ==================== END MACRO GOALS HELPER FUNCTIONS ====================

  const calculateCombinationMacros = (combination) => {
    if (!combination.items || !combination.sections) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    }

    // Calculate macros for each section in the combination
    const totalMacros = combination.sections.reduce((acc, section) => {
      const sectionItems = combination.items.filter(item =>
        !item.fromRecipe && !item.fromMeal && item.section === section.id
      );

      const sectionMacros = sectionItems.reduce((secAcc, item) => {
        if (item.fromRecipe || item.fromMeal) return secAcc;

        let itemTotal = { calories: 0, protein: 0, carbs: 0, fat: 0 };

        if (item.type === "ingredient" || item.item_type === "ingredient") {
          const qty = item.quantity === '' ? 0 : item.quantity;
          const multiplier = item.unit === item.default_unit_name || !item.conversionFactor || item.conversionFactor === 1
            ? qty
            : qty * item.conversionFactor;

          itemTotal = {
            calories: (parseFloat(item.calories) || 0) * multiplier,
            protein: (parseFloat(item.protein) || 0) * multiplier,
            carbs: (parseFloat(item.carbs) || 0) * multiplier,
            fat: (parseFloat(item.fat) || 0) * multiplier,
          };
        } else if (item.type === "recipe" || item.item_type === "recipe") {
          // For recipes, sum up their ingredients
          const recipeIngredients = combination.items.filter(si =>
            si.fromRecipe === (item.recipe_id || item.item_id) && (si.type === "ingredient" || si.item_type === "ingredient")
          );

          itemTotal = recipeIngredients.reduce((recAcc, ing) => {
            const qty = ing.quantity === '' ? 0 : ing.quantity;
            const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
              ? qty
              : qty * ing.conversionFactor;

            return {
              calories: recAcc.calories + (parseFloat(ing.calories) || 0) * multiplier,
              protein: recAcc.protein + (parseFloat(ing.protein) || 0) * multiplier,
              carbs: recAcc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
              fat: recAcc.fat + (parseFloat(ing.fat) || 0) * multiplier,
            };
          }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
        } else if (item.type === "meal" || item.item_type === "meal") {
          // For meals, sum up all nested items (ingredients + recipes)
          const mealId = item.meal_id || item.item_id;
          const mealIngredients = combination.items.filter(si =>
            si.fromMeal === mealId && (si.type === "ingredient" || si.item_type === "ingredient")
          );
          const mealRecipes = combination.items.filter(si =>
            si.fromMeal === mealId && (si.type === "recipe" || si.item_type === "recipe")
          );

          // Sum direct ingredients
          itemTotal = mealIngredients.reduce((mealAcc, ing) => {
            const qty = ing.quantity === '' ? 0 : ing.quantity;
            const servings = ing.mealServings || ing.meal_servings || 1;
            const multiplier = (ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
              ? qty
              : qty * ing.conversionFactor) * servings;

            return {
              calories: mealAcc.calories + (parseFloat(ing.calories) || 0) * multiplier,
              protein: mealAcc.protein + (parseFloat(ing.protein) || 0) * multiplier,
              carbs: mealAcc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
              fat: mealAcc.fat + (parseFloat(ing.fat) || 0) * multiplier,
            };
          }, { calories: 0, protein: 0, carbs: 0, fat: 0 });

          // Add recipes within meal
          mealRecipes.forEach(recipe => {
            const recipeIngredients = combination.items.filter(si =>
              si.fromRecipe === (recipe.recipe_id || recipe.item_id) && (si.type === "ingredient" || si.item_type === "ingredient")
            );

            recipeIngredients.forEach(ing => {
              const qty = ing.quantity === '' ? 0 : ing.quantity;
              const recipeServings = ing.recipeServings || ing.recipe_servings || 1;
              const multiplier = (ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
                ? qty
                : qty * ing.conversionFactor) * recipeServings;

              itemTotal.calories += (parseFloat(ing.calories) || 0) * multiplier;
              itemTotal.protein += (parseFloat(ing.protein) || 0) * multiplier;
              itemTotal.carbs += (parseFloat(ing.carbs) || 0) * multiplier;
              itemTotal.fat += (parseFloat(ing.fat) || 0) * multiplier;
            });
          });
        }

        return {
          calories: secAcc.calories + itemTotal.calories,
          protein: secAcc.protein + itemTotal.protein,
          carbs: secAcc.carbs + itemTotal.carbs,
          fat: secAcc.fat + itemTotal.fat,
        };
      }, { calories: 0, protein: 0, carbs: 0, fat: 0 });

      return {
        calories: acc.calories + sectionMacros.calories,
        protein: acc.protein + sectionMacros.protein,
        carbs: acc.carbs + sectionMacros.carbs,
        fat: acc.fat + sectionMacros.fat,
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });

    return totalMacros;
  };

  const saveCombination = async (overwrite = false) => {
    // Check which tab we're in and use appropriate state
    const isDaily = activeTab === 'daily';
    const name = isDaily ? combinationName : templateName;
    const description = isDaily ? combinationDescription : templateDescription;
    const folder = isDaily ? selectedFolder : templateFolder;
    const items = isDaily ? selectedItems : templateItems;
    const sectionsToSave = isDaily ? sections : templateSections;

    if (!name.trim() || items.length === 0) {
      alert("Please enter a name and add at least one item");
      return;
    }

    // For Daily tab: Always create new (snapshot behavior)
    // For Templates tab: Update existing if editingTemplateId is set
    let targetId = null;
    if (!isDaily && editingTemplateId) {
      // Templates tab: updating existing template
      targetId = editingTemplateId;
    }

    // Check if a combination with this name already exists (for Daily tab only)
    if (isDaily) {
      const existingCombination = savedCombinations.find(c =>
        c.name.toLowerCase() === name.trim().toLowerCase()
      );
      // If not forcing overwrite and a combination exists
      if (!overwrite && existingCombination) {
        setShowSaveOptions(true);
        return;
      }
      if (overwrite && existingCombination) {
        targetId = existingCombination.id;
      }
    }

    try {
      const combinationData = {
        name: name,
        description: description,
        folderId: folder,
        items: items.map(item => ({
          id: item.id,
          type: item.type,
          item_id: item.type === 'ingredient' ? item.ingredient_id :
                   item.type === 'recipe' ? item.recipe_id :
                   item.type === 'meal' ? item.meal_id : null,
          ingredient_id: item.ingredient_id,
          recipe_id: item.recipe_id,
          meal_id: item.meal_id,
          custom_ingredient: item.custom_ingredient || 0,
          section: item.section,
          quantity: item.quantity,
          unit: item.unit || (item.type === 'meal' ? 'serving' : undefined),
          unit_id: item.unit_id,
          conversionFactor: item.conversionFactor, // Save conversion factor
          originalServings: item.originalServings,
          fromRecipe: item.fromRecipe,
          fromMeal: item.fromMeal,
          mealServings: item.mealServings,
          originalQuantity: item.originalQuantity,
          // Include all macro data for proper loading
          protein: item.protein || (item.type === 'meal' ? 0 : undefined),
          carbs: item.carbs || (item.type === 'meal' ? 0 : undefined),
          fat: item.fat || (item.type === 'meal' ? 0 : undefined),
          fiber: item.fiber || (item.type === 'meal' ? 0 : undefined),
          sugar: item.sugar || (item.type === 'meal' ? 0 : undefined),
          calories: item.calories || (item.type === 'meal' ? 0 : undefined),
          ingredient_name: item.ingredient_name,
          name: item.name || item.ingredient_name, // Ensure name is saved for all types
          description: item.description, // Save description for meals/recipes
          availableUnits: item.availableUnits
        })),
        sections: sectionsToSave // Save all sections
      };

      let response;

      if (targetId) {
        // Update existing combination
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/combination/${targetId}`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(combinationData),
          }
        );
      } else {
        // Create new combination
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/save-combination`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(combinationData),
          }
        );
      }

      if (response.ok) {
        const result = await response.json();

        if (isDaily) {
          // Daily tab: saved as new template
          alert("Template saved! Your daily tracking continues.");
          setCombinationName('');
          setCombinationDescription('');
          setSelectedFolder(null);
        } else {
          // Templates tab: saved/updated template
          alert(targetId ? "Template updated successfully!" : "Template saved successfully!");
          // Reset template editing state
          setIsCreatingTemplate(false);
          setEditingTemplateId(null);
          setTemplateName('');
          setTemplateDescription('');
          setTemplateFolder(null);
          setTemplateItems([]);
          setTemplateSections([
            { id: 'default', name: 'General', isDefault: true },
            { id: 'breakfast1', name: 'Breakfast 1', isDefault: false },
            { id: 'breakfast2', name: 'Breakfast 2', isDefault: false },
            { id: 'snack1', name: 'Snack 1', isDefault: false },
            { id: 'lunch', name: 'Lunch', isDefault: false },
            { id: 'snack2', name: 'Snack 2', isDefault: false },
            { id: 'dinner1', name: 'Dinner 1', isDefault: false },
            { id: 'dinner2', name: 'Dinner 2', isDefault: false },
            { id: 'dessert', name: 'Dessert', isDefault: false },
            { id: 'snack3', name: 'Snack 3', isDefault: false }
          ]);
          setTemplateExpandedRecipes({});
          setTemplateCollapsedSections({});
        }

        setShowSaveOptions(false);
        fetchSavedCombinations();
      }
    } catch (error) {
      console.error("Error saving combination:", error);
      alert("Error saving combination");
    }
  };

  const saveAsNew = () => {
    setLoadedCombinationId(null);
    setShowSaveOptions(false);
    saveCombination(false);
  };

  // Load template for editing in Templates tab
  const loadTemplateForEditing = (combination) => {
    setIsCreatingTemplate(true);
    setEditingTemplateId(combination.id);
    setTemplateName(combination.name || '');
    setTemplateDescription(combination.description || '');
    setTemplateFolder(combination.folder_id || null);

    // Load sections
    if (combination.sections && combination.sections.length > 0) {
      setTemplateSections(combination.sections);
    } else {
      setTemplateSections([
        { id: 'default', name: 'General', isDefault: true },
        { id: 'breakfast1', name: 'Breakfast 1', isDefault: false },
        { id: 'breakfast2', name: 'Breakfast 2', isDefault: false },
        { id: 'snack1', name: 'Snack 1', isDefault: false },
        { id: 'lunch', name: 'Lunch', isDefault: false },
        { id: 'snack2', name: 'Snack 2', isDefault: false },
        { id: 'dinner1', name: 'Dinner 1', isDefault: false },
        { id: 'dinner2', name: 'Dinner 2', isDefault: false },
        { id: 'dessert', name: 'Dessert', isDefault: false },
        { id: 'snack3', name: 'Snack 3', isDefault: false }
      ]);
    }

    // Load items
    if (combination.items && combination.items.length > 0) {
      const validItems = combination.items
        .filter(item => {
          if (!item.item_id || item.item_id === 0) return false;
          const itemType = item.type || item.item_type;
          if (itemType === 'ingredient' && !item.ingredient_name && !item.name) return false;
          if (itemType === 'recipe' && !item.name) return false;
          return true;
        })
        .map(item => {
          const itemType = item.type || item.item_type;
          const processedItem = {
            ...item,
            type: itemType,
            name: item.name, // Preserve name for all items
            ingredient_name: item.ingredient_name || item.name, // For ingredients
            description: item.description, // Preserve description for meals/recipes
            section: item.section || 'default',
            quantity: item.quantity === undefined ? 1 : item.quantity,
            fromRecipe: item.fromRecipe || item.from_recipe,
            fromMeal: item.fromMeal || item.from_meal,
            mealServings: item.mealServings || item.meal_servings,
            // Restore conversion factor (backend may use snake_case)
            conversionFactor: item.conversionFactor || item.conversion_factor
          };
          if (itemType === 'recipe') {
            processedItem.recipe_id = item.item_id;
            processedItem.originalServings = item.quantity;
          }
          if (itemType === 'ingredient') {
            processedItem.ingredient_id = item.item_id;
          }
          if (itemType === 'meal') {
            processedItem.meal_id = item.item_id;
          }
          return processedItem;
        });

      // Auto-expand recipes
      const recipeIds = validItems
        .filter(item => item.type === 'recipe')
        .map(item => item.recipe_id || item.item_id);
      const expandedState = {};
      recipeIds.forEach(id => {
        expandedState[id] = true;
      });
      setTemplateExpandedRecipes(expandedState);

      // Auto-expand meals (using daily state since templates don't have separate expandedMeals)
      const mealIds = validItems
        .filter(item => item.type === 'meal')
        .map(item => item.meal_id || item.item_id);
      if (mealIds.length > 0) {
        const expandedMealState = {};
        mealIds.forEach(id => { expandedMealState[id] = true; });
        setExpandedMeals(expandedMealState);
      }

      setTemplateItems(validItems);
    } else {
      setTemplateItems([]);
    }
  };

  // Add template items to daily tracking (merge, don't replace)
  const addTemplateToDaily = (combination) => {
    if (!combination.items || combination.items.length === 0) {
      alert('This template has no items to add.');
      return;
    }

    // First, check if template has sections that don't exist in current sections
    if (combination.sections && combination.sections.length > 0) {
      const templateSectionsToAdd = combination.sections.filter(templateSection =>
        !sections.find(s => s.id === templateSection.id)
      );
      // Add missing sections to preserve template structure
      if (templateSectionsToAdd.length > 0) {
        setSections([...sections, ...templateSectionsToAdd]);
      }
    }

    // Generate new unique IDs for all items to avoid collisions
    const generateNewId = (item) => {
      const baseId = item.type === 'ingredient' ? item.ingredient_id || item.item_id : item.recipe_id || item.item_id;
      return `${item.type}-${baseId}-${Date.now()}-${Math.random()}`;
    };

    // Map template items to new items with fresh IDs
    const newItems = combination.items
      .filter(item => {
        if (!item.item_id || item.item_id === 0) return false;
        const itemType = item.type || item.item_type;
        if (itemType === 'ingredient' && !item.ingredient_name && !item.name) return false;
        if (itemType === 'recipe' && !item.name) return false;
        return true;
      })
      .map(item => {
        const itemType = item.type || item.item_type;
        const newItem = {
          ...item,
          id: generateNewId(item),
          type: itemType,
          section: item.section || 'default',
          quantity: item.quantity === undefined ? 1 : item.quantity,
          fromRecipe: item.fromRecipe || item.from_recipe,
          // Restore conversion factor (backend may use snake_case)
          conversionFactor: item.conversionFactor || item.conversion_factor
        };
        if (itemType === 'recipe') {
          newItem.recipe_id = item.item_id;
          newItem.originalServings = item.quantity;
        }
        if (itemType === 'ingredient') {
          newItem.ingredient_id = item.item_id;
        }
        return newItem;
      });

    // Merge with existing items
    setSelectedItems([...selectedItems, ...newItems]);

    // Auto-expand any recipes that were added
    const recipeIds = newItems
      .filter(item => item.type === 'recipe')
      .map(item => item.recipe_id || item.item_id);
    if (recipeIds.length > 0) {
      const newExpandedState = { ...expandedRecipes };
      recipeIds.forEach(id => {
        newExpandedState[id] = true;
      });
      setExpandedRecipes(newExpandedState);
    }

    // Close the modal and show success message
    setShowTemplateModal(false);
    alert(`Added ${newItems.length} items from "${combination.name}" to your daily tracking!`);
  };

  const loadCombination = (combination) => {
    // Load the name, description, and folder
    setCombinationName(combination.name || '');
    setCombinationDescription(combination.description || '');
    setSelectedFolder(combination.folder_id || null);
    setLoadedCombinationId(combination.id);

    // Clear current items first
    setSelectedItems([]);

    // Load sections
    if (combination.sections && combination.sections.length > 0) {
      // Ensure sections have required fields
      const validSections = combination.sections.map(section => ({
        id: section.id || `section-${Date.now()}`,
        name: section.name || 'Unnamed Section',
        isDefault: section.isDefault || false
      }));
      setSections(validSections);
    } else {
      // For older saved combinations, use default sections
      setSections([
        { id: 'default', name: 'General', isDefault: true }
      ]);
    }

    // Load items - ensure they have all required fields
    if (combination.items && combination.items.length > 0) {
      const validItems = combination.items
        .filter(item => {
          // Filter out items with missing critical data
          if (!item.item_id || item.item_id === 0) {
            console.warn('Skipping item with invalid item_id:', item);
            return false;
          }

          const itemType = item.type || item.item_type;
          if (itemType === 'ingredient' && !item.ingredient_name && !item.name) {
            console.warn('Skipping ingredient with no name:', item);
            return false;
          }

          if (itemType === 'recipe' && !item.name) {
            console.warn('Skipping recipe with no name:', item);
            return false;
          }

          return true;
        })
        .map(item => {
          // Ensure type is set correctly (backend returns item_type)
          const itemType = item.type || item.item_type;

          const processedItem = {
            ...item,
            type: itemType,
            name: item.name, // Preserve name for all items
            ingredient_name: item.ingredient_name || item.name, // For ingredients
            description: item.description, // Preserve description for meals/recipes
            // Keep the ID from backend (which now includes the database row ID)
            section: item.section || 'default',
            quantity: item.quantity === undefined ? 1 : item.quantity,
            // Preserve fromRecipe and fromMeal if they exist
            fromRecipe: item.fromRecipe || item.from_recipe,
            fromMeal: item.fromMeal || item.from_meal,
            mealServings: item.mealServings || item.meal_servings,
            // Restore conversion factor (backend may use snake_case)
            conversionFactor: item.conversionFactor || item.conversion_factor
          };

          // For recipes, ensure recipe_id is set
          if (itemType === 'recipe') {
            processedItem.recipe_id = item.item_id;
            processedItem.originalServings = item.quantity; // Store original servings
          }

          // For ingredients, ensure ingredient_id is set
          if (itemType === 'ingredient') {
            processedItem.ingredient_id = item.item_id;
          }

          // For meals, ensure meal_id is set
          if (itemType === 'meal') {
            processedItem.meal_id = item.item_id;
          }

          return processedItem;
        });

      // Find all recipe IDs and set them as expanded
      const recipeIds = validItems
        .filter(item => item.type === 'recipe')
        .map(item => item.recipe_id || item.item_id);

      const expandedState = {};
      recipeIds.forEach(id => {
        expandedState[id] = true;
      });
      setExpandedRecipes(expandedState);

      // Find all meal IDs and set them as expanded
      const mealIds = validItems
        .filter(item => item.type === 'meal')
        .map(item => item.meal_id || item.item_id);

      const expandedMealState = {};
      mealIds.forEach(id => {
        expandedMealState[id] = true;
      });
      setExpandedMeals(expandedMealState);

      // Set items after a small delay to ensure sections are set first
      setTimeout(() => {
        setSelectedItems(validItems);
      }, 100);
    }

    setShowSaved(false);
  };

  const deleteCombination = async (id) => {
    if (!window.confirm("Are you sure you want to delete this combination?")) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/combination/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        fetchSavedCombinations();
      }
    } catch (error) {
      console.error("Error deleting combination:", error);
    }
  };

  // ========================================
  // MEALS TAB FUNCTIONS
  // ========================================

  // Load all meals from backend
  const loadMeals = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/meals`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const meals = await response.json();
        setSavedMeals(meals);
      }
    } catch (error) {
      console.error('Error loading meals:', error);
    }
  };

  // Save meal (create or update)
  const saveMeal = async () => {
    if (!mealName.trim()) {
      alert('Please enter a meal name');
      return;
    }

    if (mealItems.length === 0) {
      alert('Please add at least one ingredient or recipe to the meal');
      return;
    }

    const mealData = {
      name: mealName.trim(),
      description: mealDescription.trim() || null,
      items: mealItems.map(item => ({
        type: item.type,
        ingredient_id: item.ingredient_id,
        recipe_id: item.recipe_id,
        custom_ingredient: item.custom_ingredient || 0,
        quantity: item.quantity,
        unit: item.unit,
        conversionFactor: item.conversionFactor,
        section: 'default',
        fromRecipe: item.fromRecipe
      }))
    };

    try {
      const url = editingMealId
        ? `${process.env.REACT_APP_API_URL}/api/macro-calculator/meals/${editingMealId}`
        : `${process.env.REACT_APP_API_URL}/api/macro-calculator/meals`;
      const method = editingMealId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mealData)
      });

      if (response.ok) {
        alert(editingMealId ? 'Meal updated successfully!' : 'Meal saved successfully!');
        setIsCreatingMeal(false);
        setEditingMealId(null);
        setMealName('');
        setMealDescription('');
        setMealItems([]);
        loadMeals(); // Refresh list
      } else {
        const error = await response.json();
        alert(`Error saving meal: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving meal:', error);
      alert('Failed to save meal. Please try again.');
    }
  };

  // Delete meal
  const deleteMeal = async (mealId) => {
    if (!window.confirm('Are you sure you want to delete this meal?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/meals/${mealId}`,
        { method: 'DELETE', credentials: 'include' }
      );

      if (response.ok) {
        alert('Meal deleted!');
        loadMeals();
      } else {
        alert('Failed to delete meal');
      }
    } catch (error) {
      console.error('Error deleting meal:', error);
      alert('Failed to delete meal. Please try again.');
    }
  };

  // Load meal for editing
  const loadMealForEditing = (meal) => {
    setIsCreatingMeal(true);
    setEditingMealId(meal.id);
    setMealName(meal.name);
    setMealDescription(meal.description || '');

    // Map items with proper structure
    const validItems = meal.items.map(item => ({
      ...item,
      id: `${item.type}-${item.item_id || item.ingredient_id || item.recipe_id}-${Date.now()}-${Math.random()}`,
      section: 'default'
    }));

    setMealItems(validItems);

    // Auto-expand recipes
    const recipeIds = validItems
      .filter(item => item.type === 'recipe')
      .map(item => item.recipe_id || item.item_id);
    const expandedState = {};
    recipeIds.forEach(id => { expandedState[id] = true; });
    setMealExpandedRecipes(expandedState);
  };

  // Calculate macros for a meal (used in meal browser display)
  const calculateMealMacros = (meal) => {
    if (!meal.items || meal.items.length === 0) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 };
    }

    return meal.items.reduce((acc, item) => {
      // Skip recipe ingredients to avoid double counting
      if (item.fromRecipe) return acc;

      const quantity = parseFloat(item.quantity) || 0;
      const conversionFactor = item.conversionFactor || 1;
      const totalQuantity = quantity * conversionFactor;

      // For recipes, sum their ingredient macros
      if (item.type === 'recipe') {
        const recipeIngredients = meal.items.filter(ing =>
          ing.fromRecipe === (item.recipe_id || item.item_id)
        );
        recipeIngredients.forEach(ing => {
          const ingQuantity = parseFloat(ing.quantity) || 0;
          const ingConversionFactor = ing.conversionFactor || 1;
          const ingTotalQuantity = ingQuantity * ingConversionFactor;

          acc.calories += (ing.calories || 0) * ingTotalQuantity;
          acc.protein += (ing.protein || 0) * ingTotalQuantity;
          acc.carbs += (ing.carbs || 0) * ingTotalQuantity;
          acc.fat += (ing.fat || 0) * ingTotalQuantity;
          acc.fiber += (ing.fiber || 0) * ingTotalQuantity;
          acc.sugar += (ing.sugar || 0) * ingTotalQuantity;
        });
      } else {
        // Regular ingredient
        acc.calories += (item.calories || 0) * totalQuantity;
        acc.protein += (item.protein || 0) * totalQuantity;
        acc.carbs += (item.carbs || 0) * totalQuantity;
        acc.fat += (item.fat || 0) * totalQuantity;
        acc.fiber += (item.fiber || 0) * totalQuantity;
        acc.sugar += (item.sugar || 0) * totalQuantity;
      }

      return acc;
    }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
  };

  // Calculate macros for currently editing meal (used in editor)
  const calculateCurrentMealMacros = () => {
    if (mealItems.length === 0) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 };
    }

    return mealItems.reduce((acc, item) => {
      // Skip recipe ingredients to avoid double counting
      if (item.fromRecipe) return acc;

      const quantity = parseFloat(item.quantity) || 0;
      const conversionFactor = item.conversionFactor || 1;
      const totalQuantity = quantity * conversionFactor;

      // For recipes, sum their ingredient macros
      if (item.type === 'recipe') {
        const recipeIngredients = mealItems.filter(ing =>
          ing.fromRecipe === (item.recipe_id || item.id)
        );
        recipeIngredients.forEach(ing => {
          const ingQuantity = parseFloat(ing.quantity) || 0;
          const ingConversionFactor = ing.conversionFactor || 1;
          const ingTotalQuantity = ingQuantity * ingConversionFactor;

          acc.calories += (ing.calories || 0) * ingTotalQuantity;
          acc.protein += (ing.protein || 0) * ingTotalQuantity;
          acc.carbs += (ing.carbs || 0) * ingTotalQuantity;
          acc.fat += (ing.fat || 0) * ingTotalQuantity;
          acc.fiber += (ing.fiber || 0) * ingTotalQuantity;
          acc.sugar += (ing.sugar || 0) * ingTotalQuantity;
        });
      } else {
        // Regular ingredient
        acc.calories += (item.calories || 0) * totalQuantity;
        acc.protein += (item.protein || 0) * totalQuantity;
        acc.carbs += (item.carbs || 0) * totalQuantity;
        acc.fat += (item.fat || 0) * totalQuantity;
        acc.fiber += (item.fiber || 0) * totalQuantity;
        acc.sugar += (item.sugar || 0) * totalQuantity;
      }

      return acc;
    }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
  };

  // Remove item from meal
  const removeMealItem = (itemId) => {
    const item = mealItems.find(i => i.id === itemId);
    if (!item) return;

    // If removing a recipe, also remove its ingredients
    if (item.type === 'recipe') {
      const recipeId = item.recipe_id || item.id;
      setMealItems(mealItems.filter(i =>
        i.id !== itemId && i.fromRecipe !== recipeId
      ));
    } else {
      setMealItems(mealItems.filter(i => i.id !== itemId));
    }
  };

  // Update meal item quantity
  const updateMealItemQuantity = (itemId, newQuantity) => {
    setMealItems(mealItems.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    ));
  };

  // Update meal item unit
  const updateMealItemUnit = (itemId, newUnit) => {
    const item = mealItems.find(i => i.id === itemId);
    if (!item) return;

    // Find conversion factor for new unit
    const unitInfo = item.availableUnits?.find(u => u.unit_name === newUnit);
    const newConversionFactor = unitInfo?.conversion_factor || 1;

    setMealItems(mealItems.map(i =>
      i.id === itemId
        ? { ...i, unit: newUnit, conversionFactor: newConversionFactor }
        : i
    ));
  };

  // Add item to meal (simplified - no section selector since meals don't have sections)
  const addItemToMeal = async (item) => {
    if (searchType === "recipe") {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-macros/${item.recipe_id || item.id}`,
          { credentials: "include" }
        );
        if (response.ok) {
          const macroData = await response.json();
          const recipeId = item.recipe_id || item.id;
          const recipeServings = macroData.servings || 1;

          // Add the recipe
          setMealItems([...mealItems, {
            ...item,
            ...macroData,
            type: "recipe",
            recipe_id: recipeId,
            quantity: 1, // Default to 1 serving
            originalServings: recipeServings,
            id: `recipe-${recipeId}-${Date.now()}`,
            section: 'default'
          }]);

          // Fetch recipe ingredients
          let ingredients = recipeIngredients[recipeId];
          if (!ingredients) {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-ingredients/${recipeId}`,
              { credentials: "include" }
            );
            if (response.ok) {
              ingredients = await response.json();
              setRecipeIngredients(prev => ({ ...prev, [recipeId]: ingredients }));
            }
          }

          if (ingredients) {
            const perServingFactor = 1 / recipeServings;
            const newItems = ingredients.map(ing => ({
              ...ing,
              type: "ingredient",
              fromRecipe: recipeId,
              id: `recipe-ingredient-${recipeId}-${ing.ingredient_id}-${Date.now()}`,
              ingredient_name: ing.ingredient_name,
              ingredient_id: ing.ingredient_id,
              custom_ingredient: ing.custom_ingredient,
              quantity: (ing.quantity || 1) * perServingFactor,
              unit: ing.unit || ing.unit_name,
              availableUnits: ing.availableUnits || [],
              recipeServings: 1,
              originalQuantity: (ing.quantity || 1) * perServingFactor,
              section: 'default'
            }));
            setMealItems(prev => [...prev, ...newItems]);
          }

          // Auto-expand the recipe
          if (!mealExpandedRecipes[recipeId]) {
            setMealExpandedRecipes(prev => ({ ...prev, [recipeId]: true }));
          }
        }
      } catch (error) {
        console.error("Error fetching recipe macros:", error);
      }
    } else {
      // Add ingredient
      const defaultUnit = item.default_unit_name ||
        (item.availableUnits && item.availableUnits.length > 0
          ? item.availableUnits[0].unit_name
          : (isMetric ? "g" : "oz"));

      const selectedUnitData = item.availableUnits?.find(u => u.unit_name === defaultUnit);
      const conversionFactor = selectedUnitData?.conversion_factor;

      setMealItems([...mealItems, {
        ...item,
        type: "ingredient",
        custom_ingredient: item.type === 'custom' ? 1 : 0,
        quantity: 1,
        unit: defaultUnit,
        unit_id: selectedUnitData?.unit_id || item.default_unit_id,
        conversionFactor: conversionFactor,
        availableUnits: item.availableUnits || [],
        id: `ingredient-${item.ingredient_id}-${Date.now()}`,
        section: 'default'
      }]);
    }

    // Clear search
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  // Load available units for a meal item (for inline editing)
  const loadUnitsForMealItem = async (item) => {
    if (item.type !== 'ingredient') return;

    setLoadingMealUnitsFor({ ...loadingMealUnitsFor, [item.id]: true });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/ingredient-units/${item.ingredient_id}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const units = await response.json();
        setMealItems(mealItems.map(i =>
          i.id === item.id ? { ...i, availableUnits: units } : i
        ));
      }
    } catch (error) {
      console.error('Error loading units:', error);
    } finally {
      setLoadingMealUnitsFor({ ...loadingMealUnitsFor, [item.id]: false });
    }
  };

  const generateMealPlanText = () => {
    const lines = [];

    // Header
    lines.push('MEAL PLAN EXPORT');
    lines.push(`Generated: ${new Date().toLocaleString()}`);
    lines.push('');

    // Total Macros
    const totals = calculateTotalMacros();
    lines.push('='.repeat(80));
    lines.push('TOTAL MACROS (All Sections Combined)');
    lines.push('='.repeat(80));
    lines.push(`Calories: ${totals.calories.toFixed(0)} kcal`);
    lines.push(`Protein:  ${totals.protein.toFixed(1)}g`);
    lines.push(`Carbs:    ${totals.carbs.toFixed(1)}g`);
    lines.push(`Fat:      ${totals.fat.toFixed(1)}g`);
    lines.push(`Fiber:    ${totals.fiber.toFixed(1)}g`);
    lines.push(`Sugar:    ${totals.sugar.toFixed(1)}g`);
    lines.push('');

    // Each Section
    sections.forEach(section => {
      const sectionItems = selectedItems.filter(item =>
        !item.fromRecipe && !item.fromMeal && item.section === section.id
      );

      if (sectionItems.length === 0) return;

      const sectionMacros = calculateSectionMacros(section.id);

      lines.push('='.repeat(80));
      lines.push(`SECTION: ${section.name}`);
      lines.push('='.repeat(80));
      lines.push('');

      lines.push('MACROS:');
      lines.push(`  Calories: ${sectionMacros.calories.toFixed(0)} kcal  |  Protein: ${sectionMacros.protein.toFixed(1)}g  |  Carbs: ${sectionMacros.carbs.toFixed(1)}g`);
      lines.push(`  Fat: ${sectionMacros.fat.toFixed(1)}g  |  Fiber: ${sectionMacros.fiber.toFixed(1)}g  |  Sugar: ${sectionMacros.sugar.toFixed(1)}g`);
      lines.push('');

      // Separate recipes, meals, and standalone ingredients
      const recipes = sectionItems.filter(item => item.type === 'recipe');
      const meals = sectionItems.filter(item => item.type === 'meal');
      const standaloneIngredients = sectionItems.filter(item => item.type === 'ingredient');

      if (standaloneIngredients.length > 0) {
        lines.push('INGREDIENTS:');
        standaloneIngredients.forEach(item => {
          lines.push(`  ☐ ${item.quantity} ${item.unit} ${item.ingredient_name || item.name}`);
        });
        lines.push('');
      }

      if (recipes.length > 0) {
        lines.push('RECIPES:');
        recipes.forEach(recipe => {
          // Calculate recipe macros from its ingredients
          const recipeIngredients = selectedItems.filter(item =>
            item.fromRecipe === (recipe.recipe_id || recipe.id) && item.type === 'ingredient'
          );

          const recipeMacros = recipeIngredients.reduce((acc, ing) => {
            const qty = ing.quantity === '' ? 0 : ing.quantity;
            const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
              ? qty
              : qty * ing.conversionFactor;

            return {
              calories: acc.calories + (parseFloat(ing.calories) || 0) * multiplier,
              protein: acc.protein + (parseFloat(ing.protein) || 0) * multiplier,
              carbs: acc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
              fat: acc.fat + (parseFloat(ing.fat) || 0) * multiplier,
              fiber: acc.fiber + (parseFloat(ing.fiber) || 0) * multiplier,
              sugar: acc.sugar + (parseFloat(ing.sugar) || 0) * multiplier,
            };
          }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });

          lines.push(`  • ${recipe.name} (${recipe.quantity} servings)`);
          lines.push(`    Macros: ${recipeMacros.calories.toFixed(0)} cal | ${recipeMacros.protein.toFixed(1)}g protein | ${recipeMacros.carbs.toFixed(1)}g carbs | ${recipeMacros.fat.toFixed(1)}g fat`);

          // Only include ingredients if option is enabled
          if (includeRecipeIngredients) {
            lines.push('    Ingredients:');
            recipeIngredients.forEach(ing => {
              lines.push(`      - ${ing.quantity.toFixed(1)} ${ing.unit} ${ing.ingredient_name}`);
            });
          }
          lines.push('');
        });
      }

      if (meals.length > 0) {
        lines.push('MEALS:');
        meals.forEach(meal => {
          // Get all items in this meal (ingredients + recipes)
          const mealIngredients = selectedItems.filter(item =>
            item.fromMeal === meal.meal_id && item.type === 'ingredient'
          );
          const mealRecipes = selectedItems.filter(item =>
            item.fromMeal === meal.meal_id && item.type === 'recipe'
          );

          // Calculate meal macros from direct ingredients
          let mealMacros = mealIngredients.reduce((acc, ing) => {
            const qty = ing.quantity === '' ? 0 : ing.quantity;
            const servings = ing.mealServings || 1;
            const multiplier = (ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
              ? qty
              : qty * ing.conversionFactor) * servings;

            return {
              calories: acc.calories + (parseFloat(ing.calories) || 0) * multiplier,
              protein: acc.protein + (parseFloat(ing.protein) || 0) * multiplier,
              carbs: acc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
              fat: acc.fat + (parseFloat(ing.fat) || 0) * multiplier,
              fiber: acc.fiber + (parseFloat(ing.fiber) || 0) * multiplier,
              sugar: acc.sugar + (parseFloat(ing.sugar) || 0) * multiplier,
            };
          }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });

          // Add macros from recipes within meal
          mealRecipes.forEach(recipe => {
            const recipeIngredients = selectedItems.filter(item =>
              item.fromRecipe === (recipe.recipe_id || recipe.id) && item.type === 'ingredient'
            );

            recipeIngredients.forEach(ing => {
              const qty = ing.quantity === '' ? 0 : ing.quantity;
              const recipeServings = ing.recipeServings || 1;
              const multiplier = (ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
                ? qty
                : qty * ing.conversionFactor) * recipeServings;

              mealMacros.calories += (parseFloat(ing.calories) || 0) * multiplier;
              mealMacros.protein += (parseFloat(ing.protein) || 0) * multiplier;
              mealMacros.carbs += (parseFloat(ing.carbs) || 0) * multiplier;
              mealMacros.fat += (parseFloat(ing.fat) || 0) * multiplier;
              mealMacros.fiber += (parseFloat(ing.fiber) || 0) * multiplier;
              mealMacros.sugar += (parseFloat(ing.sugar) || 0) * multiplier;
            });
          });

          lines.push(`  • ${meal.name} (${meal.quantity}x servings)`);
          lines.push(`    Macros: ${mealMacros.calories.toFixed(0)} cal | ${mealMacros.protein.toFixed(1)}g protein | ${mealMacros.carbs.toFixed(1)}g carbs | ${mealMacros.fat.toFixed(1)}g fat`);

          // Only include items if option is enabled
          if (includeRecipeIngredients) {
            lines.push('    Items:');
            mealIngredients.forEach(ing => {
              lines.push(`      - ${ing.quantity.toFixed(1)} ${ing.unit} ${ing.ingredient_name}`);
            });
            mealRecipes.forEach(recipe => {
              lines.push(`      - ${recipe.name} (${recipe.quantity} servings)`);
            });
          }
          lines.push('');
        });
      }
    });

    lines.push('='.repeat(80));
    lines.push('END OF MEAL PLAN');
    lines.push('='.repeat(80));

    return lines.join('\n');
  };

  const exportToText = () => {
    if (selectedItems.length === 0) {
      alert("Please add some items before exporting");
      return;
    }

    const textContent = generateMealPlanText();
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `meal-plan-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderCombination = (combination, index, folderId) => {
    const macros = calculateCombinationMacros(combination);
    const truncatedDesc = combination.description
      ? (combination.description.length > 200
          ? combination.description.substring(0, 200) + '...'
          : combination.description)
      : '';

    const isDragging = draggedCombination?.combination.id === combination.id;
    const isDragOver = dragOverCombinationIndex === index;

    return (
      <div
        key={combination.id}
        className={`saved-combination ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
        draggable={true}
        onDragStart={(e) => handleCombinationDragStart(e, combination, index, folderId)}
        onDragOver={(e) => handleCombinationDragOver(e, index)}
        onDragEnd={handleCombinationDragEnd}
        onDrop={(e) => handleCombinationDrop(e, index, folderId)}
      >
        <span className="drag-handle">⋮⋮</span>
        <div className="combination-info">
          <h3>{combination.name}</h3>
          <div className="combination-macros">
            <span className="macro"><strong>{macros.calories.toFixed(0)}</strong> cal</span>
            <span className="macro"><strong>{macros.protein.toFixed(0)}</strong>p</span>
            <span className="macro"><strong>{macros.carbs.toFixed(0)}</strong>c</span>
            <span className="macro"><strong>{macros.fat.toFixed(0)}</strong>f</span>
          </div>
          {truncatedDesc && <p className="combination-description">{truncatedDesc}</p>}
        </div>
        <div className="combination-actions">
          <select
            value={combination.folder_id || ''}
            onChange={(e) => moveCombination(combination.id, e.target.value ? parseInt(e.target.value) : null)}
            className="move-select"
            title="Move to folder"
          >
            <option value="">Uncategorized</option>
            {folders.map(folder => (
              <option key={folder.id} value={folder.id}>{folder.name}</option>
            ))}
          </select>
          <button onClick={() => loadCombination(combination)} className="load-button">
            Load
          </button>
          <button onClick={() => deleteCombination(combination.id)} className="delete-button">
            ×
          </button>
        </div>
      </div>
    );
  };

  // Analytics Tab Component
  const AnalyticsTab = () => {
    const [analyticsData, setAnalyticsData] = React.useState(null);
    const [isLoadingAnalytics, setIsLoadingAnalytics] = React.useState(false);
    const [selectedPeriod, setSelectedPeriod] = React.useState('30');
    const [selectedMetric, setSelectedMetric] = React.useState('calories');

    React.useEffect(() => {
      fetchAnalyticsData();
    }, [selectedPeriod]);

    const fetchAnalyticsData = async () => {
      setIsLoadingAnalytics(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/analytics?period=${selectedPeriod}`,
          { credentials: 'include' }
        );
        const data = await response.json();

        // Add calculated calorie goals to each day's data
        if (data.dailyData) {
          data.dailyData = data.dailyData.map(day => {
            if (day.goals?.protein && day.goals?.carbs && day.goals?.fat) {
              const calorieGoal = (day.goals.protein * 4) + (day.goals.carbs * 4) + (day.goals.fat * 9);
              return {
                ...day,
                goals: {
                  ...day.goals,
                  calories: calorieGoal
                }
              };
            }
            return day;
          });
        }

        setAnalyticsData(data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setIsLoadingAnalytics(false);
      }
    };

    if (isLoadingAnalytics) {
      return <div className="analytics-loading">Loading analytics...</div>;
    }

    if (!analyticsData || analyticsData.totalLogs === 0) {
      return (
        <div className="analytics-empty-state">
          <h2>No Data Available</h2>
          <p>Start logging your daily intake to see analytics and trends!</p>
          <p>You need at least 2 logged days to see meaningful analytics.</p>
        </div>
      );
    }

    // Calculate average daily variance (difference between actual and goal per day)
    const calculateDailyVariance = () => {
      if (!analyticsData.dailyData || analyticsData.dailyData.length === 0) {
        return { calories: 0, protein: 0, carbs: 0, fat: 0 };
      }

      let totalCaloriesDiff = 0;
      let totalProteinDiff = 0;
      let totalCarbsDiff = 0;
      let totalFatDiff = 0;
      let daysWithGoals = 0;

      analyticsData.dailyData.forEach(day => {
        if (day.goals && day.goals.protein && day.goals.carbs && day.goals.fat) {
          // Actual values are at root level of day object
          const actualCalories = day.calories || 0;
          const actualProtein = day.protein || 0;
          const actualCarbs = day.carbs || 0;
          const actualFat = day.fat || 0;

          totalCaloriesDiff += actualCalories - (day.goals.calories || 0);
          totalProteinDiff += actualProtein - (day.goals.protein || 0);
          totalCarbsDiff += actualCarbs - (day.goals.carbs || 0);
          totalFatDiff += actualFat - (day.goals.fat || 0);
          daysWithGoals++;
        }
      });

      if (daysWithGoals === 0) {
        return { calories: 0, protein: 0, carbs: 0, fat: 0 };
      }

      return {
        calories: Math.round(totalCaloriesDiff / daysWithGoals),
        protein: Math.round(totalProteinDiff / daysWithGoals),
        carbs: Math.round(totalCarbsDiff / daysWithGoals),
        fat: Math.round(totalFatDiff / daysWithGoals)
      };
    };

    const dailyVariance = calculateDailyVariance();

    return (
      <div className="analytics-tab-content">
        <div className="analytics-header">
          <h2>Macro Analytics</h2>
          <div className="period-selector">
            <button
              className={selectedPeriod === '7' ? 'active' : ''}
              onClick={() => setSelectedPeriod('7')}
            >
              7 Days
            </button>
            <button
              className={selectedPeriod === '30' ? 'active' : ''}
              onClick={() => setSelectedPeriod('30')}
            >
              30 Days
            </button>
            <button
              className={selectedPeriod === '90' ? 'active' : ''}
              onClick={() => setSelectedPeriod('90')}
            >
              90 Days
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="analytics-summary">
          <div className="summary-card">
            <h3>Total Days Logged</h3>
            <div className="summary-value">{analyticsData.totalLogs}</div>
          </div>
          <div className="summary-card">
            <h3>Average Calories</h3>
            <div className="summary-value">{analyticsData.averages.calories}</div>
          </div>
          <div className="summary-card">
            <h3>Average Protein</h3>
            <div className="summary-value">{analyticsData.averages.protein}g</div>
          </div>
          <div className="summary-card">
            <h3>Average Carbs</h3>
            <div className="summary-value">{analyticsData.averages.carbs}g</div>
          </div>
          <div className="summary-card">
            <h3>Average Fat</h3>
            <div className="summary-value">{analyticsData.averages.fat}g</div>
          </div>
          <div className="summary-card">
            <h3>Daily Surplus/Deficit</h3>
            <div className="summary-value" style={{ color: dailyVariance.calories > 0 ? '#ff9800' : dailyVariance.calories < 0 ? '#2196f3' : '#666' }}>
              {dailyVariance.calories > 0 ? '+' : ''}{dailyVariance.calories} cal
            </div>
            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
              P: {dailyVariance.protein > 0 ? '+' : ''}{dailyVariance.protein}g |
              C: {dailyVariance.carbs > 0 ? '+' : ''}{dailyVariance.carbs}g |
              F: {dailyVariance.fat > 0 ? '+' : ''}{dailyVariance.fat}g
            </div>
          </div>
        </div>

        {/* Goal Adherence */}
        {analyticsData.goalAdherence && (
          <div className="goal-adherence-section">
            <h3>Goal Adherence</h3>
            <div className="adherence-grid">
              {['calories', 'protein', 'carbs', 'fat'].map(macro => {
                const data = analyticsData.goalAdherence[macro];
                if (!data) return null;

                return (
                  <div key={macro} className="adherence-card">
                    <h4>{macro.charAt(0).toUpperCase() + macro.slice(1)}</h4>
                    <div className="adherence-percentage">{data.percentage}%</div>
                    <div className="adherence-stats">
                      {data.met} out of {data.total} days
                    </div>
                    <div className="adherence-bar">
                      <div
                        className="adherence-fill"
                        style={{
                          width: `${data.percentage}%`,
                          backgroundColor: data.percentage >= 80 ? '#28a745' : data.percentage >= 60 ? '#ffc107' : '#dc3545'
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Metric Selector */}
        <div className="metric-selector">
          <label>View Trend For:</label>
          <select value={selectedMetric} onChange={(e) => setSelectedMetric(e.target.value)}>
            <option value="calories">Calories</option>
            <option value="protein">Protein</option>
            <option value="carbs">Carbs</option>
            <option value="fat">Fat</option>
            <option value="fiber">Fiber</option>
            <option value="sugar">Sugar</option>
          </select>
        </div>

        {/* Line Chart - Daily Trends */}
        <div className="chart-section">
          <h3>Daily {selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)} Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analyticsData.dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => {
                  const d = new Date(date);
                  return `${d.getMonth() + 1}/${d.getDate()}`;
                }}
              />
              <YAxis />
              <Tooltip
                labelFormatter={(date) => formatLocalDate(date)}
                formatter={(value) => [
                  `${Math.round(value)}${selectedMetric === 'calories' ? '' : 'g'}`,
                  selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)
                ]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey={selectedMetric}
                stroke="#8884d8"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                name={selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}
              />
              {analyticsData.dailyData[0]?.goals?.[selectedMetric] && (
                <Line
                  type="monotone"
                  dataKey={`goals.${selectedMetric}`}
                  stroke="#82ca9d"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  dot={false}
                  name="Goal"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Bar Chart - Comparison */}
        <div className="chart-section">
          <h3>Macro Breakdown Comparison</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={[
                {
                  name: 'Average',
                  Protein: analyticsData.averages.protein,
                  Carbs: analyticsData.averages.carbs,
                  Fat: analyticsData.averages.fat,
                  Fiber: analyticsData.averages.fiber
                }
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Protein" fill="#82ca9d" />
              <Bar dataKey="Carbs" fill="#ffc658" />
              <Bar dataKey="Fat" fill="#ff7c7c" />
              <Bar dataKey="Fiber" fill="#a28fd0" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Weekly Averages */}
        {analyticsData.weeklyAverages.length > 0 && (
          <div className="weekly-averages-section">
            <h3>Weekly Averages</h3>
            <div className="weekly-data">
              {analyticsData.weeklyAverages.map((week, idx) => (
                <div key={idx} className="week-card">
                  <div className="week-label">Week of {formatLocalDate(week.weekStart)}</div>
                  <div className="week-stats">
                    <span>{week.daysLogged} days logged</span>
                    <span>Avg: {week.averages[selectedMetric]}{selectedMetric === 'calories' ? '' : 'g'}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ==================== WEIGHT TRACKING HELPER FUNCTIONS ====================

  // Format time of day for display
  const formatTimeOfDay = React.useCallback((timeOfDay) => {
    const times = {
      morning: 'Morning',
      afternoon: 'Afternoon',
      evening: 'Evening',
      night: 'Night'
    };
    return times[timeOfDay] || timeOfDay;
  }, []);

  // Fetch weight logs and goals
  const fetchWeightData = React.useCallback(async () => {
    setIsLoadingWeight(true);
    try {
      const [logsRes, goalsRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/weight/logs?limit=50`,
          { credentials: 'include' }
        ),
        fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/weight/goals`,
          { credentials: 'include' }
        )
      ]);

      const logsData = await logsRes.json();
      const goalsData = await goalsRes.json();

      setWeightLogs(logsData.logs || []);
      setWeightGoals(goalsData.goals || []);
    } catch (error) {
      console.error('Error fetching weight data:', error);
      alert('Failed to load weight data');
    } finally {
      setIsLoadingWeight(false);
    }
  }, []);

  // Save weight log (create or update)
  const saveWeightLog = React.useCallback(async () => {
    if (!weightValue) {
      alert('Please enter a weight value');
      return;
    }

    try {
      const url = editingWeightLogId
        ? `${process.env.REACT_APP_API_URL}/api/macro-calculator/weight/logs/${editingWeightLogId}`
        : `${process.env.REACT_APP_API_URL}/api/macro-calculator/weight/logs`;

      const response = await fetch(url, {
        method: editingWeightLogId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          log_date: weightLogDate,
          weight: parseFloat(weightValue),
          unit: weightUnit,
          time_of_day: weightTimeOfDay,
          notes: weightNotes
        })
      });

      if (response.ok) {
        // Clear form
        setEditingWeightLogId(null);
        setWeightValue('');
        setWeightNotes('');
        setWeightLogDate(getTodayLocalDate());
        setWeightTimeOfDay('morning');
        setShowWeightLogModal(false);
        fetchWeightData();
        alert(editingWeightLogId ? 'Weight log updated successfully!' : 'Weight logged successfully!');
      } else {
        throw new Error('Failed to save weight log');
      }
    } catch (error) {
      console.error('Error saving weight log:', error);
      alert('Failed to save weight log. Please try again.');
    }
  }, [weightValue, editingWeightLogId, weightLogDate, weightUnit, weightTimeOfDay, weightNotes, fetchWeightData]);

  // Delete weight log
  const deleteWeightLog = React.useCallback(async (id, date) => {
    if (!window.confirm(`Delete weight log from ${formatLocalDate(date)}?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/weight/logs/${id}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        fetchWeightData();
        alert('Weight log deleted');
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting weight log:', error);
      alert('Failed to delete weight log');
    }
  }, [formatLocalDate, fetchWeightData]);

  // Populate inline form for editing weight log
  const editWeightLogInline = React.useCallback((log = null) => {
    if (log) {
      setEditingWeightLogId(log.id);
      setWeightLogDate(log.log_date);
      setWeightValue(log.weight.toString());
      setWeightUnit(log.unit);
      setWeightTimeOfDay(log.time_of_day || 'morning');
      setWeightNotes(log.notes || '');
      // Scroll to form
      setTimeout(() => {
        document.getElementById('weight-log-date-inline')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else {
      setEditingWeightLogId(null);
      setWeightLogDate(getTodayLocalDate());
      setWeightValue('');
      setWeightUnit('lbs');
      setWeightTimeOfDay('morning');
      setWeightNotes('');
    }
  }, []);

  // Keep old function for modal support (if needed elsewhere)
  const openWeightLogModal = (log = null) => {
    editWeightLogInline(log);
    setShowWeightLogModal(true);
  };

  // Save weight goal
  const saveWeightGoal = async () => {
    if (!goalWeight || !goalTargetDate) {
      alert('Please enter goal weight and target date');
      return;
    }

    try {
      const url = editingWeightGoalId
        ? `${process.env.REACT_APP_API_URL}/api/macro-calculator/weight/goals/${editingWeightGoalId}`
        : `${process.env.REACT_APP_API_URL}/api/macro-calculator/weight/goals`;

      const response = await fetch(url, {
        method: editingWeightGoalId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          goal_weight: parseFloat(goalWeight),
          unit: weightUnit,
          target_date: goalTargetDate,
          start_date: goalStartDate,
          start_weight: goalStartWeight ? parseFloat(goalStartWeight) : null,
          notes: goalNotes
        })
      });

      if (response.ok) {
        setShowWeightGoalModal(false);
        setGoalWeight('');
        setGoalTargetDate('');
        setGoalStartWeight('');
        setGoalNotes('');
        fetchWeightData();
        alert('Goal saved successfully!');
      } else {
        throw new Error('Failed to save goal');
      }
    } catch (error) {
      console.error('Error saving goal:', error);
      alert('Failed to save goal');
    }
  };

  // Open weight goal modal
  const openWeightGoalModal = React.useCallback((goal = null) => {
    if (goal) {
      setEditingWeightGoalId(goal.id);
      setGoalWeight(goal.goal_weight.toString());
      setWeightUnit(goal.unit);
      setGoalTargetDate(goal.target_date);
      setGoalStartDate(goal.start_date);
      setGoalStartWeight(goal.start_weight ? goal.start_weight.toString() : '');
      setGoalNotes(goal.notes || '');
    } else {
      setEditingWeightGoalId(null);
      setGoalWeight('');
      setGoalTargetDate('');
      setGoalStartDate(getTodayLocalDate());
      setGoalStartWeight('');
      setGoalNotes('');
    }
    setShowWeightGoalModal(true);
  }, []);

  // Toggle goal active status
  const toggleGoalActive = React.useCallback(async (goalId, isActive) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/weight/goals/${goalId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ is_active: isActive ? 1 : 0 })
        }
      );

      if (response.ok) {
        fetchWeightData();
      }
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  }, [fetchWeightData]);

  // Delete weight goal
  const deleteWeightGoal = React.useCallback(async (goalId) => {
    if (!window.confirm('Delete this weight goal?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/weight/goals/${goalId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        fetchWeightData();
        alert('Goal deleted');
      }
    } catch (error) {
      console.error('Error deleting goal:', error);
      alert('Failed to delete goal');
    }
  }, [fetchWeightData]);

  // Fetch weight analytics (memoized to prevent infinite loops)
  const fetchWeightAnalytics = useCallback(async () => {
    // Prevent concurrent fetches
    if (isFetchingWeightAnalytics.current) {
      console.log('Already fetching, skipping duplicate request');
      return;
    }

    isFetchingWeightAnalytics.current = true;
    setIsLoadingWeightAnalytics(true);

    try {
      // Build query params based on period type
      let queryParams = '';
      if (selectedWeightPeriod === 'custom') {
        if (!customWeightStartDate || !customWeightEndDate) {
          alert('Please select both start and end dates');
          setIsLoadingWeightAnalytics(false);
          isFetchingWeightAnalytics.current = false;
          return;
        }
        queryParams = `start_date=${customWeightStartDate}&end_date=${customWeightEndDate}`;
        console.log('Fetching weight analytics for custom range:', customWeightStartDate, 'to', customWeightEndDate);
      } else {
        queryParams = `period=${selectedWeightPeriod}`;
        console.log('Fetching weight analytics for period:', selectedWeightPeriod);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/weight/analytics?${queryParams}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Weight analytics data received:', data);
      setWeightAnalytics(data);
    } catch (error) {
      console.error('Error fetching weight analytics:', error);
      alert(`Failed to load analytics: ${error.message}`);
      // Set empty data so it shows the empty state instead of loading forever
      setWeightAnalytics({ weightData: [], macroAdherence: [], activeGoal: null, statistics: {} });
    } finally {
      setIsLoadingWeightAnalytics(false);
      isFetchingWeightAnalytics.current = false;
    }
  }, [selectedWeightPeriod, customWeightStartDate, customWeightEndDate]); // Only recreate when period or dates change

  // Fetch macro calorie data for maintenance adjustment calculation
  const fetchMacroCaloriesForWeight = useCallback(async () => {
    setIsLoadingMacroCalories(true);
    try {
      // Mirror the same query params as weight analytics
      let queryParams;
      if (selectedWeightPeriod === 'custom') {
        queryParams = `?start_date=${customWeightStartDate}&end_date=${customWeightEndDate}`;
      } else {
        queryParams = `?period=${selectedWeightPeriod}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/macro-calculator/analytics${queryParams}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch macro analytics');
      }

      const data = await response.json();

      // Extract daily calorie data from the response
      // Filter to only days with goals set (protein, carbs, fat exist)
      const dailyData = (data.dailyData || [])
        .filter(day => day.protein_goal && day.carbs_goal && day.fat_goal)
        .map(day => ({
          date: day.date,
          actual_calories: day.total_calories || 0,
          goal_calories: (day.protein_goal * 4) + (day.carbs_goal * 4) + (day.fat_goal * 9),
          goal_type: day.goal_type
        }));

      setMacroCalorieData(dailyData);
    } catch (error) {
      console.error('Error fetching macro calorie data:', error);
      setMacroCalorieData([]);
    } finally {
      setIsLoadingMacroCalories(false);
    }
  }, [selectedWeightPeriod, customWeightStartDate, customWeightEndDate]);

  // Merge weight and macro data by date
  const mergeWeightAndMacroData = React.useCallback((weightData, macroData) => {
    const dateMap = new Map();

    weightData.forEach(item => {
      dateMap.set(item.date, { date: item.date, weight: item.weight });
    });

    macroData.forEach(item => {
      const existing = dateMap.get(item.date) || { date: item.date };
      dateMap.set(item.date, {
        ...existing,
        adherence_percentage: item.adherence_percentage
      });
    });

    if (weightAnalytics?.activeGoal) {
      dateMap.forEach((value) => {
        value.goal_weight = weightAnalytics.activeGoal.goal_weight;
      });
    }

    return Array.from(dateMap.values()).sort((a, b) =>
      new Date(a.date) - new Date(b.date)
    );
  }, [weightAnalytics]);

  // Calculate goal progress
  const calculateGoalProgress = React.useCallback((goal) => {
    if (!goal.start_weight || weightLogs.length === 0) {
      return <div className="no-progress">No data yet</div>;
    }

    const currentWeight = weightLogs[weightLogs.length - 1].weight;
    const totalChange = goal.goal_weight - goal.start_weight;
    const currentChange = currentWeight - goal.start_weight;
    const progressPercentage = Math.min(100, Math.max(0, (currentChange / totalChange) * 100));

    return (
      <>
        <div className="progress-text">
          {currentWeight} {goal.unit} / {goal.goal_weight} {goal.unit}
          {' '}({progressPercentage.toFixed(1)}%)
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{
              width: `${progressPercentage}%`,
              backgroundColor: progressPercentage >= 100 ? '#28a745' : '#007bff'
            }}
          />
        </div>
      </>
    );
  }, [weightLogs]);

  // Calculate projected goal date based on current trend
  const calculateProjectedGoalDate = React.useCallback((analytics, periodLabel) => {
    // Need active goal, current weight, and trend data
    if (!analytics.activeGoal || !analytics.statistics.current_weight || !analytics.statistics.average_weekly_change) {
      return null;
    }

    const currentWeight = parseFloat(analytics.statistics.current_weight);
    const goalWeight = parseFloat(analytics.activeGoal.goal_weight);
    const weeklyChange = analytics.statistics.average_weekly_change;
    const daysLogged = analytics.statistics.days_logged;

    // Not enough data for reliable projection
    if (daysLogged < 7) {
      return {
        message: 'Need at least 7 days of data for projection',
        insufficient: true
      };
    }

    // Already at or past goal
    if ((weeklyChange < 0 && currentWeight <= goalWeight) ||
        (weeklyChange > 0 && currentWeight >= goalWeight)) {
      return {
        message: 'Goal weight achieved! 🎉',
        achieved: true
      };
    }

    // Calculate remaining weight to lose/gain
    const weightRemaining = goalWeight - currentWeight;

    // Check if trend is moving in the right direction
    const needToLose = weightRemaining < 0;
    const isTrendCorrect = needToLose ? weeklyChange < 0 : weeklyChange > 0;

    if (!isTrendCorrect || Math.abs(weeklyChange) < 0.01) {
      return {
        message: weeklyChange === 0 ? 'No weight change trend detected' : 'Current trend moving away from goal',
        wrongDirection: true
      };
    }

    // Calculate weeks until goal
    const weeksUntilGoal = Math.abs(weightRemaining / weeklyChange);
    const daysUntilGoal = Math.round(weeksUntilGoal * 7);

    // Calculate projected date
    const projectedDate = new Date();
    projectedDate.setDate(projectedDate.getDate() + daysUntilGoal);

    // Get target date for comparison
    const targetDate = new Date(analytics.activeGoal.target_date);
    const isAheadOfSchedule = projectedDate < targetDate;
    const isBehindSchedule = projectedDate > targetDate;

    return {
      projectedDate,
      daysUntilGoal,
      weeksUntilGoal: weeksUntilGoal.toFixed(1),
      isAheadOfSchedule,
      isBehindSchedule,
      targetDate,
      periodLabel,
      weeklyChange
    };
  }, []);

  // Generate weight insights
  const generateWeightInsights = React.useCallback((analytics) => {
    const insights = [];

    if (analytics.statistics.average_weekly_change) {
      const weeklyChange = analytics.statistics.average_weekly_change;
      insights.push(
        <div key="weight-trend" className="insight-card">
          <h4>Weight Trend</h4>
          <p>
            {weeklyChange < 0 ? "You're losing" : "You're gaining"} an average of{' '}
            <strong>{Math.abs(weeklyChange).toFixed(2)} {analytics.weightData[0]?.unit}/week</strong>.
          </p>
        </div>
      );
    }

    const hasWeightData = analytics.weightData.length > 7;
    const hasMacroData = analytics.macroAdherence.filter(m => m.adherence_percentage !== null).length > 7;

    if (hasWeightData && hasMacroData) {
      const avgAdherence = analytics.macroAdherence
        .filter(m => m.adherence_percentage !== null)
        .reduce((sum, m) => sum + m.adherence_percentage, 0) /
        analytics.macroAdherence.filter(m => m.adherence_percentage !== null).length;

      insights.push(
        <div key="adherence" className="insight-card">
          <h4>Macro Adherence</h4>
          <p>
            Your average macro adherence is <strong>{avgAdherence.toFixed(1)}%</strong>.
            {avgAdherence >= 80 && ' Great consistency! '}
            {avgAdherence < 60 && ' Consider focusing on hitting your targets more consistently.'}
          </p>
        </div>
      );
    }

    if (analytics.activeGoal && analytics.statistics.current_weight) {
      const remaining = analytics.activeGoal.goal_weight - analytics.statistics.current_weight;
      const daysUntilTarget = Math.ceil(
        (new Date(analytics.activeGoal.target_date) - new Date()) / (1000 * 60 * 60 * 24)
      );

      insights.push(
        <div key="goal-progress" className="insight-card">
          <h4>Goal Progress</h4>
          <p>
            {Math.abs(remaining).toFixed(1)} {analytics.weightData[0]?.unit} {remaining < 0 ? 'below' : 'to go'} until your goal.
            {daysUntilTarget > 0 && ` ${daysUntilTarget} days remaining.`}
          </p>
        </div>
      );
    }

    return insights;
  }, []);

  // Calculate maintenance adjustment based on weight trends and caloric intake
  const calculateMaintenanceAdjustment = useCallback((weightStats, macroData, weightDataArray) => {
    // Validation: check all parameters exist
    if (!weightStats || !macroData || !weightDataArray || macroData.length === 0 || weightDataArray.length === 0) {
      return {
        insufficient: true,
        message: 'No data available',
        daysAnalyzed: 0
      };
    }

    // Merge weight and macro data by date to find overlapping days
    const weightByDate = new Map();
    weightDataArray.forEach(w => {
      weightByDate.set(w.date, w);
    });

    // Filter to overlapping days with both weight AND macro goals
    const overlappingDays = macroData.filter(macro => weightByDate.has(macro.date));

    // Require >= 14 overlapping days
    if (overlappingDays.length < 14) {
      return {
        insufficient: true,
        message: `Need at least 14 days of overlapping weight and macro data. Currently have ${overlappingDays.length} days.`,
        daysAnalyzed: overlappingDays.length
      };
    }

    // Calculate average daily surplus from macro data
    const avgDailySurplus = overlappingDays.reduce((sum, day) => {
      return sum + (day.actual_calories - day.goal_calories);
    }, 0) / overlappingDays.length;

    // Get actual weight change per week from statistics
    const actualWeightChangePerWeek = weightStats.average_weekly_change || 0;

    // Detect weight unit from first weight entry
    const weightUnit = weightDataArray[0]?.unit || 'lbs';
    const calsPerUnit = weightUnit === 'kg' ? 7700 : 3500;

    // Calculate expected weight change per week based on caloric surplus
    // Formula: (avgDailySurplus × 7 days) / calories per unit
    const expectedWeightChangePerWeek = (avgDailySurplus * 7) / calsPerUnit;

    // Calculate maintenance adjustment
    // If expected > actual: eating more than thermodynamics predicts, so maintenance is HIGHER
    // Formula: (expectedChange - actualChange) × calsPerUnit / 7
    const maintenanceAdjustment = ((expectedWeightChangePerWeek - actualWeightChangePerWeek) * calsPerUnit) / 7;

    return {
      adjustment: Math.round(maintenanceAdjustment),
      avgDailySurplus: Math.round(avgDailySurplus),
      expectedWeightChange: expectedWeightChangePerWeek,
      actualWeightChange: actualWeightChangePerWeek,
      daysAnalyzed: overlappingDays.length,
      weightUnit: weightUnit,
      insufficient: false
    };
  }, []);

  // ==================== WEIGHT TAB COMPONENTS ====================

  // Weight Goals View Component
  const WeightGoalsView = React.memo(() => {
    const activeGoals = weightGoals.filter(g => g.is_active);
    const pastGoals = weightGoals.filter(g => !g.is_active);

    return (
      <div className="weight-goals-view">
        <div className="weight-goals-header">
          <h2>Weight Goals</h2>
          <button
            className="add-goal-button"
            onClick={() => openWeightGoalModal()}
          >
            ➕ New Goal
          </button>
        </div>

        {/* Active Goals */}
        <div className="active-goals-section">
          <h3>Active Goals</h3>
          {activeGoals.length === 0 ? (
            <div className="empty-state">
              <p>No active weight goals.</p>
              <p>Set a goal to track your progress!</p>
            </div>
          ) : (
            <div className="goals-grid">
              {activeGoals.map(goal => (
                <div key={goal.id} className="goal-card active-goal">
                  <div className="goal-header">
                    <h4>Target: {goal.goal_weight} {goal.unit}</h4>
                    <button
                      onClick={() => toggleGoalActive(goal.id, false)}
                      className="deactivate-button"
                      title="Mark as complete"
                    >
                      ✓
                    </button>
                  </div>

                  <div className="goal-details">
                    <div className="goal-detail-row">
                      <span className="label">Target Date:</span>
                      <span className="value">{formatLocalDate(goal.target_date)}</span>
                    </div>
                    <div className="goal-detail-row">
                      <span className="label">Started:</span>
                      <span className="value">
                        {formatLocalDate(goal.start_date)}
                        {goal.start_weight && ` at ${goal.start_weight} ${goal.unit}`}
                      </span>
                    </div>

                    {goal.start_weight && weightLogs.length > 0 && (
                      <div className="goal-progress">
                        <div className="progress-label">Progress</div>
                        <div className="progress-bar-container">
                          {calculateGoalProgress(goal)}
                        </div>
                      </div>
                    )}

                    {goal.notes && (
                      <div className="goal-notes">
                        <span className="notes-icon">📝</span>
                        {goal.notes}
                      </div>
                    )}
                  </div>

                  <div className="goal-actions">
                    <button
                      onClick={() => openWeightGoalModal(goal)}
                      className="edit-button"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteWeightGoal(goal.id)}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Past Goals */}
        {pastGoals.length > 0 && (
          <div className="past-goals-section">
            <h3>Completed/Past Goals</h3>
            <div className="goals-list-compact">
              {pastGoals.map(goal => (
                <div key={goal.id} className="goal-card-compact">
                  <span className="goal-summary">
                    {goal.goal_weight} {goal.unit} by {formatLocalDate(goal.target_date)}
                  </span>
                  <button
                    onClick={() => toggleGoalActive(goal.id, true)}
                    className="reactivate-button"
                    title="Reactivate"
                  >
                    ↻
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  });

  // Weight Analytics View Component
  const WeightAnalyticsView = React.memo(() => {
    // Note: fetchWeightAnalytics is called by WeightTab's useEffect
    // when weightViewMode changes to 'analytics' or selectedWeightPeriod changes

    if (isLoadingWeightAnalytics || isLoadingMacroCalories) {
      return <div className="analytics-loading">Loading analytics...</div>;
    }

    if (!weightAnalytics || (weightAnalytics.weightData.length === 0 && weightAnalytics.macroAdherence.length === 0)) {
      return (
        <div className="analytics-empty-state">
          <h2>No Data Available</h2>
          <p>Start logging your weight and tracking macros to see correlations!</p>
        </div>
      );
    }

    const mergedData = mergeWeightAndMacroData(
      weightAnalytics.weightData,
      weightAnalytics.macroAdherence
    );

    return (
      <div className="weight-analytics-view">
        <div className="analytics-header">
          <h2>Weight & Macro Adherence Analytics</h2>
          <div className="period-selector">
            <button
              className={selectedWeightPeriod === '7' ? 'active' : ''}
              onClick={() => setSelectedWeightPeriod('7')}
            >
              7 Days
            </button>
            <button
              className={selectedWeightPeriod === '30' ? 'active' : ''}
              onClick={() => setSelectedWeightPeriod('30')}
            >
              30 Days
            </button>
            <button
              className={selectedWeightPeriod === '90' ? 'active' : ''}
              onClick={() => setSelectedWeightPeriod('90')}
            >
              90 Days
            </button>
            <button
              className={selectedWeightPeriod === '365' ? 'active' : ''}
              onClick={() => setSelectedWeightPeriod('365')}
            >
              1 Year
            </button>
            <button
              className={selectedWeightPeriod === 'custom' ? 'active' : ''}
              onClick={() => {
                setSelectedWeightPeriod('custom');
                // Set default custom range to last 30 days if not set
                if (!customWeightStartDate) {
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                  setCustomWeightStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                }
              }}
            >
              Custom
            </button>
          </div>

          {/* Custom Date Range Inputs */}
          {selectedWeightPeriod === 'custom' && (
            <div className="custom-date-range" style={{ marginTop: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <label>
                From:
                <input
                  type="date"
                  value={customWeightStartDate}
                  onChange={(e) => setCustomWeightStartDate(e.target.value)}
                  max={customWeightEndDate}
                  style={{ marginLeft: '5px', padding: '5px' }}
                />
              </label>
              <label>
                To:
                <input
                  type="date"
                  value={customWeightEndDate}
                  onChange={(e) => setCustomWeightEndDate(e.target.value)}
                  min={customWeightStartDate}
                  max={getTodayLocalDate()}
                  style={{ marginLeft: '5px', padding: '5px' }}
                />
              </label>
              <button
                onClick={() => {
                  fetchWeightAnalytics();
                  fetchMacroCaloriesForWeight();
                }}
                style={{ padding: '5px 15px', cursor: 'pointer' }}
              >
                Apply
              </button>
            </div>
          )}
        </div>

        {/* Summary Statistics */}
        {weightAnalytics.statistics && (
          <div className="weight-stats-summary">
            <div className="stat-card">
              <h3>Current Weight</h3>
              <div className="stat-value">
                {weightAnalytics.statistics.current_weight} {weightAnalytics.weightData[0]?.unit || 'lbs'}
              </div>
            </div>
            <div className="stat-card">
              <h3>Total Change</h3>
              <div className={`stat-value ${weightAnalytics.statistics.total_change < 0 ? 'positive' : 'negative'}`}>
                {weightAnalytics.statistics.total_change > 0 ? '+' : ''}
                {weightAnalytics.statistics.total_change?.toFixed(1)} {weightAnalytics.weightData[0]?.unit || 'lbs'}
              </div>
            </div>
            <div className="stat-card">
              <h3>Avg Weekly Change</h3>
              <div className={`stat-value ${weightAnalytics.statistics.average_weekly_change < 0 ? 'positive' : 'negative'}`}>
                {weightAnalytics.statistics.average_weekly_change > 0 ? '+' : ''}
                {weightAnalytics.statistics.average_weekly_change?.toFixed(2)} {weightAnalytics.weightData[0]?.unit || 'lbs'}/week
              </div>
            </div>
            <div className="stat-card">
              <h3>Days Logged</h3>
              <div className="stat-value">{weightAnalytics.statistics.days_logged}</div>
            </div>

            {/* Maintenance Adjustment */}
            {(() => {
              const maintenanceResult = calculateMaintenanceAdjustment(
                weightAnalytics.statistics,
                macroCalorieData,
                weightAnalytics.weightData
              );

              if (maintenanceResult.insufficient) {
                return (
                  <div className="stat-card maintenance-adjustment" style={{ gridColumn: 'span 2' }}>
                    <h3>
                      Maintenance Adjustment
                      <span
                        className="info-tooltip"
                        title="Compares actual weight change to expected change based on calorie intake to estimate if your maintenance calories are higher or lower than your goals."
                      >
                        ℹ️
                      </span>
                    </h3>
                    <div className="stat-value" style={{ fontSize: '0.9em', color: '#ff9800' }}>
                      Insufficient Data
                    </div>
                    <div style={{ fontSize: '0.75em', marginTop: '5px', opacity: 0.8 }}>
                      {maintenanceResult.message}
                    </div>
                  </div>
                );
              }

              // Color coding based on magnitude
              let adjustmentColor = '#4CAF50'; // Green - well calibrated
              if (Math.abs(maintenanceResult.adjustment) >= 150) {
                adjustmentColor = '#f44336'; // Red - large difference
              } else if (Math.abs(maintenanceResult.adjustment) >= 50) {
                adjustmentColor = '#ff9800'; // Orange - moderate difference
              }

              // Interpretation text
              let interpretation = '';
              if (maintenanceResult.adjustment > 0) {
                interpretation = `Your actual maintenance is about ${Math.abs(maintenanceResult.adjustment)} cal/day HIGHER than your goals suggest.`;
              } else if (maintenanceResult.adjustment < 0) {
                interpretation = `Your actual maintenance is about ${Math.abs(maintenanceResult.adjustment)} cal/day LOWER than your goals suggest.`;
              } else {
                interpretation = 'Your goals are well-calibrated with your actual results!';
              }

              return (
                <div className="stat-card maintenance-adjustment" style={{ gridColumn: 'span 2' }}>
                  <h3>
                    Maintenance Adjustment
                    <span
                      className="info-tooltip"
                      title="Compares actual weight change to expected change based on calorie intake. Uses the 3500 cal/lb (7700 cal/kg) formula to estimate if your maintenance calories differ from your set goals."
                    >
                      ℹ️
                    </span>
                  </h3>
                  <div className="stat-value" style={{ color: adjustmentColor, fontSize: '1.5em' }}>
                    {maintenanceResult.adjustment > 0 ? '+' : ''}{maintenanceResult.adjustment} cal/day
                  </div>
                  <div style={{ fontSize: '0.8em', marginTop: '8px', opacity: 0.9 }}>
                    {interpretation}
                  </div>
                  <div style={{
                    fontSize: '0.75em',
                    marginTop: '10px',
                    paddingTop: '10px',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    display: 'grid',
                    gap: '5px'
                  }}>
                    <div>Avg daily surplus: {maintenanceResult.avgDailySurplus > 0 ? '+' : ''}{maintenanceResult.avgDailySurplus} cal</div>
                    <div>
                      Expected weight change: {maintenanceResult.expectedWeightChange > 0 ? '+' : ''}{maintenanceResult.expectedWeightChange.toFixed(2)} {maintenanceResult.weightUnit}/week
                    </div>
                    <div>
                      Actual weight change: {maintenanceResult.actualWeightChange > 0 ? '+' : ''}{maintenanceResult.actualWeightChange.toFixed(2)} {maintenanceResult.weightUnit}/week
                    </div>
                    <div style={{ opacity: 0.7 }}>Based on {maintenanceResult.daysAnalyzed} days of data</div>
                  </div>
                </div>
              );
            })()}

            {/* Projected Goal Date */}
            {(() => {
              const periodLabels = {
                '7': '7-day',
                '30': '30-day',
                '90': '90-day',
                '365': '1-year',
                'custom': 'custom'
              };
              const periodLabel = periodLabels[selectedWeightPeriod] || 'current';
              const projection = calculateProjectedGoalDate(weightAnalytics, periodLabel);

              if (!projection) return null;

              return (
                <div className="stat-card" style={{ gridColumn: 'span 2', position: 'relative' }}>
                  <h3>
                    Projected Goal Date
                    <span style={{ fontSize: '0.7em', fontWeight: 'normal', marginLeft: '5px', opacity: 0.7 }}>
                      (based on {periodLabel} trend)
                    </span>
                  </h3>
                  <div className="stat-value" style={{ fontSize: projection.insufficient || projection.wrongDirection ? '0.9em' : '1.5em' }}>
                    {projection.achieved ? (
                      <span style={{ color: '#4CAF50' }}>🎉 Goal Achieved!</span>
                    ) : projection.insufficient || projection.wrongDirection ? (
                      <span style={{ color: '#ff9800' }}>{projection.message}</span>
                    ) : (
                      <>
                        <div style={{ fontSize: '1.2em', marginBottom: '5px' }}>
                          {projection.projectedDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                        <div style={{ fontSize: '0.6em', opacity: 0.8 }}>
                          ({projection.weeksUntilGoal} weeks / {projection.daysUntilGoal} days)
                        </div>
                        {projection.isAheadOfSchedule && (
                          <div style={{ fontSize: '0.55em', color: '#4CAF50', marginTop: '5px' }}>
                            ✓ Ahead of target: {projection.targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                        {projection.isBehindSchedule && (
                          <div style={{ fontSize: '0.55em', color: '#ff9800', marginTop: '5px' }}>
                            ⚠ Behind target: {projection.targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {!projection.insufficient && !projection.wrongDirection && !projection.achieved && (
                    <div style={{
                      fontSize: '0.75em',
                      marginTop: '8px',
                      padding: '8px',
                      background: 'rgba(33, 150, 243, 0.1)',
                      borderRadius: '4px',
                      color: '#666'
                    }}>
                      💡 This projection assumes you maintain your current {periodLabel} average of {projection.weeklyChange > 0 ? '+' : ''}{projection.weeklyChange.toFixed(2)} {weightAnalytics.weightData[0]?.unit || 'lbs'}/week.
                      Try different time periods to see how recent vs. long-term trends compare!
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Dual-Axis Chart */}
        <div className="chart-section dual-axis-chart">
          <h3>Weight Trend & Macro Adherence Correlation</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={mergedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => {
                  const d = new Date(date);
                  return `${d.getMonth() + 1}/${d.getDate()}`;
                }}
              />

              <YAxis
                yAxisId="left"
                orientation="left"
                label={{ value: `Weight (${weightAnalytics.weightData[0]?.unit || 'lbs'})`, angle: -90, position: 'insideLeft' }}
                domain={['dataMin - 2', 'dataMax + 2']}
              />

              <YAxis
                yAxisId="right"
                orientation="right"
                label={{ value: 'Macro Adherence (%)', angle: 90, position: 'insideRight' }}
                domain={[0, 100]}
              />

              <Tooltip
                labelFormatter={(date) => formatLocalDate(date)}
                formatter={(value, name) => {
                  if (name === 'Weight') {
                    return [`${value} ${weightAnalytics.weightData[0]?.unit || 'lbs'}`, name];
                  } else if (name === 'Macro Adherence') {
                    return [`${value}%`, name];
                  } else if (name === 'Goal Weight') {
                    return [`${value} ${weightAnalytics.weightData[0]?.unit || 'lbs'}`, name];
                  }
                  return [value, name];
                }}
              />

              <Legend />

              <Line
                yAxisId="left"
                type="monotone"
                dataKey="weight"
                stroke="#8884d8"
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
                name="Weight"
                connectNulls
              />

              {weightAnalytics.activeGoal && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="goal_weight"
                  stroke="#82ca9d"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  dot={false}
                  name="Goal Weight"
                />
              )}

              <Line
                yAxisId="right"
                type="monotone"
                dataKey="adherence_percentage"
                stroke="#ff7c7c"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                name="Macro Adherence"
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>

          <div className="chart-legend-explanation">
            <p>
              <strong>Blue line (left axis):</strong> Your weight over time
              {weightAnalytics.activeGoal && <> | <strong>Green dashed line:</strong> Goal weight target</>}
            </p>
            <p>
              <strong>Red line (right axis):</strong> How well you're hitting your macro goals (100% = perfect adherence)
            </p>
          </div>
        </div>

        {/* Correlation Insights */}
        {weightAnalytics.weightData.length > 7 && weightAnalytics.macroAdherence.length > 7 && (
          <div className="correlation-insights">
            <h3>Insights</h3>
            <div className="insight-cards">
              {generateWeightInsights(weightAnalytics)}
            </div>
          </div>
        )}
      </div>
    );
  });

  // Fetch weight analytics when switching to analytics view or period changes
  useEffect(() => {
    if (activeTab === 'weight' && weightViewMode === 'analytics') {
      // Only auto-fetch for non-custom periods (custom requires Apply button)
      if (selectedWeightPeriod !== 'custom') {
        fetchWeightAnalytics();
        fetchMacroCaloriesForWeight();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, weightViewMode, selectedWeightPeriod]); // Fetch when mode or period changes

  const totals = calculateTotalMacros();

  return (
    <div className="macro-calculator-container">
      <h1>Macro Calculator</h1>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          📝 Daily Tracking
        </button>
        <button
          className={`tab-button ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          📂 Templates
        </button>
        <button
          className={`tab-button ${activeTab === 'meals' ? 'active' : ''}`}
          onClick={() => setActiveTab('meals')}
        >
          🍽️ Meals
        </button>
        <button
          className={`tab-button ${activeTab === 'macroLogs' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('macroLogs');
            if (macroLogsViewMode === 'history') {
              fetchHistoryLogs();
            }
          }}
        >
          📊 Macro Logs
        </button>
        <button
          className={`tab-button ${activeTab === 'weight' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('weight');
            fetchWeightData();
          }}
        >
          ⚖️ Weight
        </button>
      </div>

      {/* Sticky Macro Bar (Mobile) */}
      {activeTab === 'daily' && showStickyBar && (
        <div className="sticky-macro-bar">
          <div className="sticky-macro-item">
            <span className="sticky-label">Cal</span>
            <span className="sticky-value">{totals.calories.toFixed(0)}</span>
            {showGoalsView && macroGoals && (
              <span className="sticky-goal">/{calculateCaloriesFromMacros(macroGoals.protein, macroGoals.carbs, macroGoals.fat).toFixed(0)}</span>
            )}
          </div>
          <div className="sticky-macro-item">
            <span className="sticky-label">P</span>
            <span className="sticky-value">{totals.protein.toFixed(0)}</span>
            {showGoalsView && macroGoals && (
              <span className="sticky-goal">/{macroGoals.protein}</span>
            )}
          </div>
          <div className="sticky-macro-item">
            <span className="sticky-label">C</span>
            <span className="sticky-value">{totals.carbs.toFixed(0)}</span>
            {showGoalsView && macroGoals && (
              <span className="sticky-goal">/{macroGoals.carbs}</span>
            )}
          </div>
          <div className="sticky-macro-item">
            <span className="sticky-label">F</span>
            <span className="sticky-value">{totals.fat.toFixed(0)}</span>
            {showGoalsView && macroGoals && (
              <span className="sticky-goal">/{macroGoals.fat}</span>
            )}
          </div>
          <div className="sticky-macro-item">
            <span className="sticky-label">Fib</span>
            <span className="sticky-value">{totals.fiber.toFixed(0)}</span>
            {showGoalsView && macroGoals && (
              <span className="sticky-goal">/{macroGoals.fiber}</span>
            )}
          </div>
        </div>
      )}

      {/* Daily Tracking Tab Content */}
      {activeTab === 'daily' && (
        <>
      <div ref={macrosDisplayRef} className="macros-display-section full-width">
        <div className="section-header-with-draft">
          <h2>Total Macros</h2>
          {isDraftMode && (
            <div className="draft-status-badge">
              <span className="draft-indicator">📝 Draft Mode</span>
              {draftLastSaved && (
                <span className="last-saved-time" title={draftLastSaved.toLocaleString()}>
                  Saved {formatTimeAgo(draftLastSaved)}
                </span>
              )}
              {isSaving && <span className="saving-indicator">Saving...</span>}
              {saveError && <span className="save-error">{saveError}</span>}
            </div>
          )}
        </div>

        {/* Goal Controls */}
        <div className="macros-controls">
          {macroGoals && (
            <button
              className={`toggle-goals-view-btn ${showGoalsView ? 'active' : ''}`}
              onClick={() => setShowGoalsView(!showGoalsView)}
              title={showGoalsView ? 'Hide goals' : 'Show goals'}
            >
              {showGoalsView ? '📊 Hide Goals' : '🎯 Show Goals'}
            </button>
          )}
          <button
            className="set-goals-btn"
            onClick={openGoalsModal}
            title={macroGoals ? 'Edit your macro goals' : 'Set your macro goals'}
          >
            {macroGoals ? '⚙️ Edit Goals' : '🎯 Set Goals'}
          </button>
        </div>

        <div className="macros-grid">
          {/* CALORIES */}
          <div className={`macro-item calories ${showGoalsView && macroGoals ? getMacroStatus(totals.calories, calculateCaloriesFromMacros(macroGoals.protein, macroGoals.carbs, macroGoals.fat)) : ''}`}>
            <span className="macro-label">Calories</span>
            {showGoalsView && macroGoals ? (
              <div className="macro-with-goal">
                <span className="macro-value">{totals.calories.toFixed(0)}</span>
                <span className="macro-separator">/</span>
                <span className="macro-goal">{calculateCaloriesFromMacros(macroGoals.protein, macroGoals.carbs, macroGoals.fat).toFixed(0)}</span>
              </div>
            ) : (
              <span className="macro-value">{totals.calories.toFixed(0)}</span>
            )}
          </div>
          {/* PROTEIN */}
          <div className={`macro-item protein ${showGoalsView && macroGoals ? getMacroStatus(totals.protein, macroGoals.protein) : ''}`}>
            <span className="macro-label">Protein</span>
            {showGoalsView && macroGoals ? (
              <div className="macro-with-goal">
                <span className="macro-value">{totals.protein.toFixed(1)}g</span>
                <span className="macro-separator">/</span>
                <span className="macro-goal">{macroGoals.protein}g</span>
              </div>
            ) : (
              <span className="macro-value">{totals.protein.toFixed(1)}g</span>
            )}
          </div>
          {/* CARBS */}
          <div className={`macro-item carbs ${showGoalsView && macroGoals ? getMacroStatus(totals.carbs, macroGoals.carbs) : ''}`}>
            <span className="macro-label">Carbs</span>
            {showGoalsView && macroGoals ? (
              <div className="macro-with-goal">
                <span className="macro-value">{totals.carbs.toFixed(1)}g</span>
                <span className="macro-separator">/</span>
                <span className="macro-goal">{macroGoals.carbs}g</span>
              </div>
            ) : (
              <span className="macro-value">{totals.carbs.toFixed(1)}g</span>
            )}
          </div>
          {/* FAT */}
          <div className={`macro-item fat ${showGoalsView && macroGoals ? getMacroStatus(totals.fat, macroGoals.fat) : ''}`}>
            <span className="macro-label">Fat</span>
            {showGoalsView && macroGoals ? (
              <div className="macro-with-goal">
                <span className="macro-value">{totals.fat.toFixed(1)}g</span>
                <span className="macro-separator">/</span>
                <span className="macro-goal">{macroGoals.fat}g</span>
              </div>
            ) : (
              <span className="macro-value">{totals.fat.toFixed(1)}g</span>
            )}
          </div>
          {/* FIBER */}
          <div className={`macro-item fiber ${showGoalsView && macroGoals ? getMacroStatus(totals.fiber, macroGoals.fiber) : ''}`}>
            <span className="macro-label">Fiber</span>
            {showGoalsView && macroGoals ? (
              <div className="macro-with-goal">
                <span className="macro-value">{totals.fiber.toFixed(1)}g</span>
                <span className="macro-separator">/</span>
                <span className="macro-goal">{macroGoals.fiber}g</span>
              </div>
            ) : (
              <span className="macro-value">{totals.fiber.toFixed(1)}g</span>
            )}
          </div>
          <div className="macro-item sugar">
            <span className="macro-label">Sugar</span>
            <span className="macro-value">{totals.sugar.toFixed(1)}g</span>
          </div>
        </div>

        {selectedItems.length > 0 && (
          <div className="save-combination-section">
            <div className="save-form-row">
              <div className="save-form-left">
                <input
                  type="text"
                  placeholder="Name your combination..."
                  value={combinationName}
                  onChange={(e) => setCombinationName(e.target.value)}
                  className="combination-name-input"
                />
                <div className="form-group-inline">
                  <select
                    value={selectedFolder || ''}
                    onChange={(e) => setSelectedFolder(e.target.value ? parseInt(e.target.value) : null)}
                    className="folder-select-compact"
                  >
                    <option value="">📂 Uncategorized</option>
                    {folders.map(folder => (
                      <option key={folder.id} value={folder.id}>📁 {folder.name}</option>
                    ))}
                  </select>
                  <textarea
                    placeholder="Notes (optional)..."
                    value={combinationDescription}
                    onChange={(e) => setCombinationDescription(e.target.value)}
                    className="combination-notes-input"
                    rows="1"
                  />
                </div>
              </div>

              <div className="save-form-right">
                <div className="action-buttons">
                  {loadedCombinationId && (
                    <button
                      onClick={() => {
                        setSelectedItems([]);
                        setCombinationName('');
                        setCombinationDescription('');
                        setSelectedFolder(null);
                        setLoadedCombinationId(null);
                        setSections([
                          { id: 'default', name: 'General', isDefault: true },
                          { id: 'breakfast1', name: 'Breakfast 1', isDefault: false },
                          { id: 'breakfast2', name: 'Breakfast 2', isDefault: false },
                          { id: 'snack1', name: 'Snack 1', isDefault: false },
                          { id: 'lunch', name: 'Lunch', isDefault: false },
                          { id: 'snack2', name: 'Snack 2', isDefault: false },
                          { id: 'dinner1', name: 'Dinner 1', isDefault: false },
                          { id: 'dinner2', name: 'Dinner 2', isDefault: false },
                          { id: 'dessert', name: 'Dessert', isDefault: false },
                          { id: 'snack3', name: 'Snack 3', isDefault: false }
                        ]);
                        setCurrentSection('default');
                      }}
                      className="new-button-compact"
                      title="Start a new combination"
                    >
                      🆕 New
                    </button>
                  )}
                  <button onClick={() => saveCombination(false)} className="save-button-compact">
                    {loadedCombinationId ? '💾 Update Template' : '💾 Save as Template'}
                  </button>
                  <button
                    onClick={async () => {
                      setSaveModalDate(getLocalDateString());
                      await fetchMostRecentGoalType();
                      setShowSaveDayModal(true);
                    }}
                    className="save-button-compact"
                    disabled={selectedItems.length === 0}
                    title="Save today's food log with notes"
                  >
                    💾 Save Day
                  </button>
                  <button onClick={clearDraft} className="clear-draft-button" title="Clear daily tracking">
                    🗑️ Clear Draft
                  </button>
                  <button onClick={() => setShowTemplateModal(true)} className="load-template-button" title="Load template into today">
                    📂 Load from Template
                  </button>
                  <button onClick={exportToText} className="export-button-compact" title="Export to text file">
                    📄 Export
                  </button>
                </div>
                <label className="export-option-compact">
                  <input
                    type="checkbox"
                    checked={includeRecipeIngredients}
                    onChange={(e) => setIncludeRecipeIngredients(e.target.checked)}
                  />
                  <span>Include recipe ingredients in export</span>
                </label>
              </div>
            </div>

            {showSaveOptions && (
              <div className="save-options-modal">
                <div className="save-options-content">
                  <h3>A combination with this name already exists</h3>
                  <p>Would you like to overwrite it or save as a new combination?</p>
                  <div className="save-options-buttons">
                    <button onClick={() => saveCombination(true)} className="overwrite-button">
                      Overwrite Existing
                    </button>
                    <button onClick={saveAsNew} className="save-new-button">
                      Save as New
                    </button>
                    <button onClick={() => setShowSaveOptions(false)} className="cancel-button">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="add-items-row">
        <div className="search-section">
          <h2>Add Items</h2>
          
          <div className="search-type-toggle">
            <button
              className={searchType === "ingredient" ? "active" : ""}
              onClick={() => {
                setSearchType("ingredient");
                setPendingItem(null);
                setPendingMeal(null);
                setPendingQuickMacro(null);
              }}
            >
              Ingredients
            </button>
            <button
              className={searchType === "recipe" ? "active" : ""}
              onClick={() => {
                setSearchType("recipe");
                setPendingItem(null);
                setPendingMeal(null);
                setPendingQuickMacro(null);
              }}
            >
              Recipes
            </button>
            <button
              className={searchType === "meal" ? "active" : ""}
              onClick={() => {
                setSearchType("meal");
                setPendingItem(null);
                setPendingMeal(null);
                setPendingQuickMacro(null);
              }}
            >
              Meals
            </button>
            <button
              className={searchType === "quick_macro" ? "active" : ""}
              onClick={() => {
                setSearchType("quick_macro");
                setPendingItem(null);
                setPendingMeal(null);
                // Show the quick macro form immediately
                setPendingQuickMacro({ timestamp: Date.now() });
              }}
            >
              ⚡ Quick Macros
            </button>
          </div>

          {searchType !== "quick_macro" && (
          <div className="search-container">
            <div className="search-bar-wrapper">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder={`Search ${searchType}s...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery && setShowDropdown(true)}
                  onBlur={(e) => {
                    // Delay hiding dropdown to allow click on results
                    if (!e.relatedTarget?.closest('.search-results')) {
                      setTimeout(() => setShowDropdown(false), 200);
                    }
                  }}
                />
                {isSearching && <span className="loading-spinner">⟳</span>}
              </div>

              {showDropdown && searchResults.length > 0 && (
                <div className="search-results">
                {searchResults.map((result) => {
                  // Calculate macros for meals (if it's a meal search)
                  let mealMacros = null;
                  if (searchType === "meal" && result.items) {
                    mealMacros = result.items.reduce((acc, item) => {
                      if (item.type === 'ingredient') {
                        const qty = item.quantity || 0;
                        const factor = item.conversionFactor || 1;
                        const multiplier = qty * factor;
                        return {
                          calories: acc.calories + ((item.calories || 0) * multiplier),
                          protein: acc.protein + ((item.protein || 0) * multiplier),
                          carbs: acc.carbs + ((item.carbs || 0) * multiplier),
                          fat: acc.fat + ((item.fat || 0) * multiplier)
                        };
                      }
                      return acc;
                    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
                  }

                  return (
                    <div
                      key={result.ingredient_id || result.recipe_id || result.id}
                      className="search-result-item"
                      onClick={() => selectItem(result)}
                      tabIndex="0"
                    >
                      <div className="result-info">
                        <span className="result-name">{result.ingredient_name || result.name}</span>
                        {searchType === "ingredient" && (
                          <span className="result-macros">
                            <strong>{result.calories?.toFixed(0) || 0}</strong> cal •
                            <strong>{result.protein || 0}</strong>g protein •
                            <strong>{result.carbs || 0}</strong>g carbs •
                            <strong>{result.fat || 0}</strong>g fat
                          </span>
                        )}
                        {searchType === "recipe" && (
                          <span className="result-macros">
                            <strong>{result.calories?.toFixed(0) || 0}</strong> cal •
                            <strong>{result.protein?.toFixed(1) || 0}</strong>g protein •
                            <strong>{result.carbs?.toFixed(1) || 0}</strong>g carbs •
                            <strong>{result.fat?.toFixed(1) || 0}</strong>g fat
                            {result.servings && <span className="per-serving"> (per serving)</span>}
                          </span>
                        )}
                        {searchType === "meal" && mealMacros && (
                          <span className="result-macros">
                            <strong>{mealMacros.calories?.toFixed(0) || 0}</strong> cal •
                            <strong>{mealMacros.protein?.toFixed(1) || 0}</strong>g protein •
                            <strong>{mealMacros.carbs?.toFixed(1) || 0}</strong>g carbs •
                            <strong>{mealMacros.fat?.toFixed(1) || 0}</strong>g fat
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                </div>
              )}

              {showDropdown && searchQuery && searchResults.length === 0 && !isSearching && (
                <div className="search-results">
                  <div className="no-results">No {searchType}s found</div>
                </div>
              )}
            </div>

            {/* Weekly Plan Day Selector - only show for recipe search */}
            {searchType === "recipe" && (
              <div className="weekly-plan-selector">
                <label htmlFor="weekly-plan-day">Or select from Weekly Plan:</label>
                <Select
                  inputId="weekly-plan-day"
                  options={[
                    { value: "monday", label: "Monday" },
                    { value: "tuesday", label: "Tuesday" },
                    { value: "wednesday", label: "Wednesday" },
                    { value: "thursday", label: "Thursday" },
                    { value: "friday", label: "Friday" },
                    { value: "saturday", label: "Saturday" },
                    { value: "sunday", label: "Sunday" },
                    { value: "none", label: "None (Unscheduled)" }
                  ]}
                  value={weeklyPlanDay ? { value: weeklyPlanDay, label: weeklyPlanDay.charAt(0).toUpperCase() + weeklyPlanDay.slice(1) } : null}
                  onChange={(option) => setWeeklyPlanDay(option ? option.value : '')}
                  isClearable
                  placeholder="-- Select Day --"
                />

                {/* Display recipes for selected day */}
                {weeklyPlanDay && weeklyPlanRecipes.length > 0 && (
                  <div className="weekly-plan-recipes">
                    <div className="recipe-list">
                      <p className="recipes-count">{weeklyPlanRecipes.length} recipe(s) on {weeklyPlanDay}</p>
                      {weeklyPlanRecipes.map((wpRecipe) => (
                        <div
                          key={wpRecipe.id}
                          className="weekly-plan-recipe-item"
                          onClick={() => selectWeeklyPlanRecipe(wpRecipe)}
                        >
                          <div className="recipe-info">
                            <span className="recipe-name">{wpRecipe.name}</span>
                            <span className="recipe-meta">
                              {wpRecipe.meal} • {wpRecipe.meal_qty || 1}x servings
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {weeklyPlanDay && weeklyPlanRecipes.length === 0 && !isLoadingWeeklyPlan && (
                  <p className="no-recipes">No recipes planned for {weeklyPlanDay}</p>
                )}
              </div>
            )}
          </div>
          )}

          {/* Pending Item Form */}
          {pendingItem && (
            <div className="pending-item-form">
              <h3>Add: {pendingItem.ingredient_name || pendingItem.name}</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    value={pendingQuantity}
                    onChange={(e) => setPendingQuantity(e.target.value)}
                    min="0"
                    step="0.1"
                    className="quantity-input"
                  />
                </div>

                {searchType === "ingredient" && pendingItem.availableUnits && pendingItem.availableUnits.length > 0 && (
                  <div className="form-group">
                    <label>Unit</label>
                    <select
                      value={pendingUnit}
                      onChange={(e) => setPendingUnit(e.target.value)}
                      className="unit-select"
                    >
                      {pendingItem.availableUnits.map(unit => (
                        <option key={unit.unit_id} value={unit.unit_name}>
                          {unit.unit_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {searchType === "recipe" && (
                  <div className="form-group">
                    <label>Unit</label>
                    <input type="text" value="servings" disabled className="unit-input-disabled" />
                  </div>
                )}

                <div className="form-group">
                  <label>Section</label>
                  <select
                    value={pendingSection}
                    onChange={(e) => setPendingSection(e.target.value)}
                    className="section-select"
                  >
                    {sections.map(section => (
                      <option key={section.id} value={section.id}>
                        {section.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button onClick={addPendingItem} className="add-item-btn">
                  Add to Tracker
                </button>
                <button onClick={() => setPendingItem(null)} className="cancel-add-btn">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Pending Meal Form (for adding meals to daily tracking) */}
          {pendingMeal && (
            <div className="pending-item-form">
              <h3>Add: {pendingMeal.name}</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    value={pendingMealQuantity}
                    onChange={(e) => setPendingMealQuantity(e.target.value)}
                    min="0.1"
                    step="0.1"
                    className="quantity-input"
                  />
                </div>

                <div className="form-group">
                  <label>Section</label>
                  <select
                    value={pendingMealSection}
                    onChange={(e) => setPendingMealSection(e.target.value)}
                    className="section-select"
                  >
                    {sections.map(section => (
                      <option key={section.id} value={section.id}>
                        {section.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button onClick={confirmPendingMeal} className="add-item-btn">
                  Add to Tracker
                </button>
                <button onClick={() => setPendingMeal(null)} className="cancel-add-btn">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Quick Macro Form */}
          {pendingQuickMacro && (
            <div className="pending-item-form quick-macro-form">
              <h3>⚡ Quick Add Macros</h3>
              <p className="form-hint">Enter custom macro values (grams per serving)</p>

              {/* Name field */}
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={quickMacroName}
                    onChange={(e) => setQuickMacroName(e.target.value)}
                    placeholder="e.g., Pizza from Dominos"
                    className="text-input"
                    autoFocus
                  />
                </div>
              </div>

              {/* Quantity, Unit, Section */}
              <div className="form-row">
                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    value={quickMacroQuantity}
                    onChange={(e) => setQuickMacroQuantity(e.target.value)}
                    min="0.1"
                    step="0.1"
                    className="quantity-input"
                  />
                </div>
                <div className="form-group">
                  <label>Unit *</label>
                  <input
                    type="text"
                    value={quickMacroUnit}
                    onChange={(e) => setQuickMacroUnit(e.target.value)}
                    placeholder="e.g., slice, piece"
                    className="text-input"
                  />
                </div>
                <div className="form-group">
                  <label>Section</label>
                  <select
                    value={quickMacroSection}
                    onChange={(e) => setQuickMacroSection(e.target.value)}
                    className="section-select"
                  >
                    {sections.map(section => (
                      <option key={section.id} value={section.id}>
                        {section.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Macro inputs grid */}
              <div className="macro-inputs-grid">
                <div className="form-group">
                  <label>Protein (g) *</label>
                  <input
                    type="number"
                    value={quickMacroProtein}
                    onChange={(e) => setQuickMacroProtein(e.target.value)}
                    min="0"
                    step="0.1"
                    placeholder="0"
                    className="macro-input"
                  />
                </div>
                <div className="form-group">
                  <label>Carbs (g) *</label>
                  <input
                    type="number"
                    value={quickMacroCarbs}
                    onChange={(e) => setQuickMacroCarbs(e.target.value)}
                    min="0"
                    step="0.1"
                    placeholder="0"
                    className="macro-input"
                  />
                </div>
                <div className="form-group">
                  <label>Fat (g) *</label>
                  <input
                    type="number"
                    value={quickMacroFat}
                    onChange={(e) => setQuickMacroFat(e.target.value)}
                    min="0"
                    step="0.1"
                    placeholder="0"
                    className="macro-input"
                  />
                </div>
                <div className="form-group">
                  <label>Fiber (g)</label>
                  <input
                    type="number"
                    value={quickMacroFiber}
                    onChange={(e) => setQuickMacroFiber(e.target.value)}
                    min="0"
                    step="0.1"
                    placeholder="0"
                    className="macro-input"
                  />
                </div>
                <div className="form-group">
                  <label>Sugar (g)</label>
                  <input
                    type="number"
                    value={quickMacroSugar}
                    onChange={(e) => setQuickMacroSugar(e.target.value)}
                    min="0"
                    step="0.1"
                    placeholder="0"
                    className="macro-input"
                  />
                </div>
              </div>

              {/* Calculated calories display */}
              <div className="calculated-calories">
                <strong>Calculated Calories:</strong>{' '}
                {((parseFloat(quickMacroProtein) || 0) * 4 +
                  (parseFloat(quickMacroCarbs) || 0) * 4 +
                  (parseFloat(quickMacroFat) || 0) * 9).toFixed(0)}{' '}
                per {quickMacroUnit || 'serving'}
              </div>

              {/* Action buttons */}
              <div className="form-actions">
                <button
                  onClick={addQuickMacro}
                  className="add-item-btn"
                  disabled={!quickMacroName || (!quickMacroProtein && !quickMacroCarbs && !quickMacroFat)}
                >
                  Add to Tracker
                </button>
                <button onClick={cancelQuickMacro} className="cancel-add-btn">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Section Management */}
          <div className="section-management-inline">
            <h3>Manage Sections</h3>

            {!showSectionInput ? (
              <button
                className="add-section-btn"
                onClick={() => setShowSectionInput(true)}
                title="Add new section"
              >
                + Add Section
              </button>
            ) : (
              <div className="new-section-input">
                <input
                  type="text"
                  placeholder="Section name..."
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addSection()}
                  autoFocus
                />
                <button onClick={addSection} className="confirm-btn">✓</button>
                <button onClick={() => {
                  setShowSectionInput(false);
                  setNewSectionName('');
                }} className="cancel-btn">✗</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="selected-items-section full-width">
          <div className="selected-items-header">
            <h2>Selected Items</h2>
            <div className="section-controls-buttons">
              <button
                className="toggle-detail-btn"
                onClick={() => setShowDetailedMacros(!showDetailedMacros)}
                title={showDetailedMacros ? "Hide macro details" : "Show macro details"}
              >
                {showDetailedMacros ? '📊 Hide Macros' : '📊 Show Macros'}
              </button>
              <button
                className="collapse-all-btn"
                onClick={() => {
                  const allCollapsed = sections.every(s => collapsedSections[s.id]);
                  const newState = {};
                  sections.forEach(s => {
                    newState[s.id] = !allCollapsed;
                  });
                  setCollapsedSections(newState);
                }}
                title={sections.every(s => collapsedSections[s.id]) ? "Expand all sections" : "Collapse all sections"}
              >
                {sections.every(s => collapsedSections[s.id]) ? '⬇️ Expand All' : '⬆️ Collapse All'}
              </button>
            </div>
          </div>

          {selectedItems.length === 0 ? (
            <p className="empty-message">No items added yet</p>
          ) : (
            <div className="selected-items-list">
              {sections.map((section, index) => {
                const sectionItems = selectedItems.filter(item => !item.fromRecipe && !item.fromMeal && item.section === section.id);
                if (sectionItems.length === 0) return null;
                
                const sectionMacros = calculateSectionMacros(section.id);
                const isCollapsed = collapsedSections[section.id];
                const isDragging = draggedSection?.section.id === section.id;
                const isDragOver = dragOverIndex === index;
                
                return (
                  <div
                    key={section.id}
                    className={`section-group ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, section, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <div className="section-header-wrapper">
                      <h3 className="section-header">
                        <span className="drag-handle">⋮⋮</span>
                        <button
                          className="collapse-toggle"
                          onClick={() => toggleSectionCollapse(section.id)}
                        >
                          {isCollapsed ? '▶' : '▼'}
                        </button>
                        {section.name}
                        <button
                          className="reorder-button reorder-up"
                          onClick={() => moveSectionUp(index)}
                          disabled={index === 0}
                          title="Move section up"
                        >
                          ▲
                        </button>
                      </h3>
                      <div className="section-controls-right">
                        <div className="section-macros">
                          <span className="section-macro"><strong>{sectionMacros.calories.toFixed(0)}</strong> cal</span>
                          <span className="section-macro"><strong>{sectionMacros.protein.toFixed(1)}</strong>g protein</span>
                          <span className="section-macro"><strong>{sectionMacros.carbs.toFixed(1)}</strong>g carbs</span>
                          <span className="section-macro"><strong>{sectionMacros.fat.toFixed(1)}</strong>g fat</span>
                          <button
                            className="reorder-button reorder-down"
                            onClick={() => moveSectionDown(index)}
                            disabled={index === sections.filter((s, i) => {
                              const items = selectedItems.filter(item => !item.fromRecipe && item.section === s.id);
                              return items.length > 0;
                            }).length - 1}
                            title="Move section down"
                          >
                            ▼
                          </button>
                        </div>
                      </div>
                    </div>
                    {!isCollapsed && (
                      <div className="section-items">
                        {sectionItems.map((item) => (
                <React.Fragment key={item.id}>
                  {!editingItems[item.id] ? (
                    // VIEW MODE
                    <div className={`selected-item-compact ${item.type === 'recipe' ? 'recipe-type' : item.type === 'meal' ? 'meal-type' : 'ingredient-type'}`}>
                      <div className="item-header-line">
                        <div className="item-name-with-quantity">
                          {(item.type === "recipe" || item.type === "meal") && (
                            <button
                              className="expand-toggle"
                              onClick={() => {
                                if (item.type === "recipe") {
                                  toggleRecipeExpanded(item.recipe_id || item.id);
                                } else if (item.type === "meal") {
                                  toggleMealExpanded(item.meal_id);
                                }
                              }}
                              title={
                                item.type === "recipe"
                                  ? (expandedRecipes[item.recipe_id || item.id] ? "Collapse" : "Expand")
                                  : (expandedMeals[item.meal_id] ? "Collapse" : "Expand")
                              }
                            >
                              {item.type === "recipe"
                                ? (expandedRecipes[item.recipe_id || item.id] ? '▼' : '▶')
                                : (expandedMeals[item.meal_id] ? '▼' : '▶')
                              }
                            </button>
                          )}
                          <span className="item-display-text">
                            {item.type === "recipe"
                              ? `${item.quantity} serving${item.quantity !== 1 ? 's' : ''} - ${item.name || item.ingredient_name}`
                              : item.type === "meal"
                              ? `${item.quantity}x ${item.name}`
                              : `${item.quantity} ${item.unit} ${item.ingredient_name || item.name}`
                            }
                          </span>
                        </div>
                        <button
                          className="edit-item-btn"
                          onClick={() => startEditingItem(item.id)}
                          title="Edit quantity and unit"
                          disabled={loadingUnitsFor[item.id]}
                        >
                          {loadingUnitsFor[item.id] ? '⏳' : '✏️'}
                        </button>
                      </div>
                      <div className="item-macros-inline">
                        <span className="macros-text">{renderInlineMacros(item)}</span>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="remove-button-compact"
                          title="Remove item"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ) : (
                    // EDIT MODE
                    <div className={`selected-item-compact editing ${item.type === 'recipe' ? 'recipe-type' : item.type === 'meal' ? 'meal-type' : 'ingredient-type'}`}>
                      <div className="item-header-line">
                        <div className="item-edit-controls">
                          {(item.type === "recipe" || item.type === "meal") && (
                            <button
                              className="expand-toggle"
                              onClick={() => {
                                if (item.type === "recipe") {
                                  toggleRecipeExpanded(item.recipe_id || item.id);
                                } else if (item.type === "meal") {
                                  toggleMealExpanded(item.meal_id);
                                }
                              }}
                              title={
                                item.type === "recipe"
                                  ? (expandedRecipes[item.recipe_id || item.id] ? "Collapse" : "Expand")
                                  : (expandedMeals[item.meal_id] ? "Collapse" : "Expand")
                              }
                            >
                              {item.type === "recipe"
                                ? (expandedRecipes[item.recipe_id || item.id] ? '▼' : '▶')
                                : (expandedMeals[item.meal_id] ? '▼' : '▶')
                              }
                            </button>
                          )}
                          <input
                            type="number"
                            className="quantity-input-inline"
                            value={item.quantity === '' ? '' : item.quantity}
                            onChange={(e) => updateItemQuantity(item.id, e.target.value)}
                            min="0"
                            step="0.1"
                            autoFocus
                          />
                          {item.availableUnits && item.availableUnits.length > 0 ? (
                            <select
                              value={item.unit}
                              onChange={(e) => updateItemUnit(item.id, e.target.value)}
                              className="unit-select-inline"
                            >
                              {item.availableUnits.map(unit => (
                                <option key={unit.unit_id} value={unit.unit_name}>
                                  {unit.unit_name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="unit-text-inline">{item.unit}</span>
                          )}
                          {/* Section selector */}
                          <select
                            value={item.section}
                            onChange={(e) => updateItemSection(item.id, e.target.value)}
                            className="section-select-inline"
                            title={
                              item.type === 'recipe'
                                ? "Change section (will move all ingredients)"
                                : item.type === 'meal'
                                ? "Change section (will move all meal items)"
                                : "Change section"
                            }
                          >
                            {sections.map(section => (
                              <option key={section.id} value={section.id}>
                                {section.name}
                              </option>
                            ))}
                          </select>
                          <span className="item-name-editing">
                            {item.type === "recipe"
                              ? `${item.name || item.ingredient_name}`
                              : item.type === "meal"
                              ? `${item.name}`
                              : `${item.ingredient_name || item.name}`
                            }
                          </span>
                        </div>
                        <button
                          className="save-item-btn"
                          onClick={() => saveItemEdit(item.id)}
                          title="Save changes"
                        >
                          ✓
                        </button>
                      </div>
                      <div className="item-macros-inline">
                        <span className="macros-text">{renderInlineMacros(item)}</span>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="remove-button-compact"
                          title="Remove item"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Recipe ingredients dropdown */}
                  {item.type === "recipe" && expandedRecipes[item.recipe_id || item.id] && (
                    <div className="recipe-ingredients-dropdown">
                      {loadingRecipeIngredients[item.recipe_id || item.id] ? (
                        <div className="loading-ingredients">Loading ingredients...</div>
                      ) : (
                        <>
                          <div className="recipe-ingredients-header">
                            <span>Ingredients:</span>
                          </div>
                          <div className="recipe-ingredients-list">
                            {selectedItems
                              .filter(si => si.fromRecipe === (item.recipe_id || item.id) && si.type === "ingredient")
                              .map(ingredient => (
                                <React.Fragment key={ingredient.id}>
                                  {!editingItems[ingredient.id] ? (
                                    // VIEW MODE for ingredient
                                    <div className="recipe-ingredient-compact">
                                      <div className="ingredient-header-line">
                                        <span className="ingredient-display-text">
                                          {ingredient.quantity} {ingredient.unit} {ingredient.ingredient_name}
                                        </span>
                                        <button
                                          className="edit-item-btn"
                                          onClick={() => startEditingItem(ingredient.id)}
                                          title="Edit ingredient"
                                          disabled={loadingUnitsFor[ingredient.id]}
                                        >
                                          {loadingUnitsFor[ingredient.id] ? '⏳' : '✏️'}
                                        </button>
                                      </div>
                                      <div className="ingredient-macros-inline">
                                        <span className="macros-text">{renderInlineMacros(ingredient)}</span>
                                        <button
                                          onClick={() => removeItem(ingredient.id)}
                                          className="remove-button-compact"
                                          title="Remove ingredient"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    // EDIT MODE for ingredient
                                    <div className="recipe-ingredient-compact editing">
                                      <div className="ingredient-header-line">
                                        <div className="item-edit-controls">
                                          <input
                                            type="number"
                                            className="quantity-input-inline"
                                            value={ingredient.quantity === '' ? '' : ingredient.quantity}
                                            onChange={(e) => updateRecipeIngredientQuantity(ingredient.id, e.target.value)}
                                            min="0"
                                            step="0.1"
                                            autoFocus
                                          />
                                          {ingredient.availableUnits && ingredient.availableUnits.length > 0 ? (
                                            <select
                                              value={ingredient.unit}
                                              onChange={(e) => updateRecipeIngredientUnit(ingredient.id, e.target.value)}
                                              className="unit-select-inline"
                                            >
                                              {ingredient.availableUnits.map(unit => (
                                                <option key={unit.unit_id} value={unit.unit_name}>
                                                  {unit.unit_name}
                                                </option>
                                              ))}
                                            </select>
                                          ) : (
                                            <span className="unit-text-inline">{ingredient.unit}</span>
                                          )}
                                          <span className="item-name-editing">
                                            {ingredient.ingredient_name}
                                          </span>
                                        </div>
                                        <button
                                          className="save-item-btn"
                                          onClick={() => saveItemEdit(ingredient.id)}
                                          title="Save changes"
                                        >
                                          ✓
                                        </button>
                                      </div>
                                      <div className="ingredient-macros-inline">
                                        <span className="macros-text">{renderInlineMacros(ingredient)}</span>
                                        <button
                                          onClick={() => removeItem(ingredient.id)}
                                          className="remove-button-compact"
                                          title="Remove ingredient"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </React.Fragment>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Meal items dropdown */}
                  {item.type === "meal" && expandedMeals[item.meal_id] && (
                    <div className="meal-items-dropdown">
                      {loadingMealItems[item.meal_id] ? (
                        <div className="loading-items">Loading meal items...</div>
                      ) : (
                        <>
                          <div className="meal-items-header">
                            <span>Meal Items:</span>
                          </div>
                          <div className="meal-items-list">
                            {selectedItems
                              .filter(si => si.fromMeal === item.meal_id)
                              .map(mealItem => (
                                <React.Fragment key={mealItem.id}>
                                  {mealItem.type === 'recipe' ? (
                                    // Render recipe within meal
                                    <div className="meal-recipe-item">
                                      {!editingItems[mealItem.id] ? (
                                        // VIEW MODE for recipe
                                        <>
                                          <div className="recipe-header-line">
                                            <button
                                              className="expand-toggle"
                                              onClick={() => toggleRecipeExpanded(mealItem.recipe_id || mealItem.id)}
                                              title={expandedRecipes[mealItem.recipe_id || mealItem.id] ? "Collapse" : "Expand"}
                                            >
                                              {expandedRecipes[mealItem.recipe_id || mealItem.id] ? '▼' : '▶'}
                                            </button>
                                            <span className="recipe-display-text">
                                              {mealItem.quantity}x {mealItem.name}
                                            </span>
                                            <button
                                              className="edit-item-btn"
                                              onClick={() => setEditingItems(prev => ({ ...prev, [mealItem.id]: true }))}
                                              title="Edit recipe quantity"
                                            >
                                              ✏️
                                            </button>
                                          </div>
                                          <div className="recipe-macros-inline">
                                            <span className="macros-text">{renderInlineMacros(mealItem)}</span>
                                            <button
                                              onClick={() => removeItem(mealItem.id)}
                                              className="remove-button-compact"
                                              title="Remove recipe from meal"
                                            >
                                              ×
                                            </button>
                                          </div>
                                        </>
                                      ) : (
                                        // EDIT MODE for recipe
                                        <>
                                          <div className="recipe-header-line">
                                            <button
                                              className="expand-toggle"
                                              onClick={() => toggleRecipeExpanded(mealItem.recipe_id || mealItem.id)}
                                              title={expandedRecipes[mealItem.recipe_id || mealItem.id] ? "Collapse" : "Expand"}
                                            >
                                              {expandedRecipes[mealItem.recipe_id || mealItem.id] ? '▼' : '▶'}
                                            </button>
                                            <div className="item-edit-controls">
                                              <input
                                                type="number"
                                                className="quantity-input-inline"
                                                value={mealItem.quantity === '' ? '' : mealItem.quantity}
                                                onChange={(e) => updateItemQuantity(mealItem.id, e.target.value)}
                                                step="0.1"
                                                min="0"
                                              />
                                              <span className="recipe-name-text">{mealItem.name}</span>
                                            </div>
                                            <button
                                              className="save-item-btn"
                                              onClick={() => setEditingItems(prev => ({ ...prev, [mealItem.id]: false }))}
                                              title="Done editing"
                                            >
                                              ✓
                                            </button>
                                          </div>
                                          <div className="recipe-macros-inline">
                                            <span className="macros-text">{renderInlineMacros(mealItem)}</span>
                                          </div>
                                        </>
                                      )}

                                      {/* Recipe ingredients within meal */}
                                      {expandedRecipes[mealItem.recipe_id || mealItem.id] && (
                                        <div className="recipe-ingredients-dropdown nested">
                                          <div className="recipe-ingredients-list">
                                            {selectedItems
                                              .filter(si => si.fromRecipe === (mealItem.recipe_id || mealItem.id))
                                              .map(ingredient => (
                                                <React.Fragment key={ingredient.id}>
                                                  {!editingItems[ingredient.id] ? (
                                                    // VIEW MODE
                                                    <div className="recipe-ingredient-compact">
                                                      <div className="ingredient-header-line">
                                                        <span className="ingredient-display-text">
                                                          {ingredient.quantity} {ingredient.unit} {ingredient.ingredient_name}
                                                        </span>
                                                        <button
                                                          className="edit-item-btn"
                                                          onClick={() => startEditingItem(ingredient.id)}
                                                          title="Edit ingredient"
                                                          disabled={loadingUnitsFor[ingredient.id]}
                                                        >
                                                          {loadingUnitsFor[ingredient.id] ? '⏳' : '✏️'}
                                                        </button>
                                                      </div>
                                                      <div className="ingredient-macros-inline">
                                                        <span className="macros-text">{renderInlineMacros(ingredient)}</span>
                                                        <button
                                                          onClick={() => removeItem(ingredient.id)}
                                                          className="remove-button-compact"
                                                          title="Remove ingredient"
                                                        >
                                                          ×
                                                        </button>
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    // EDIT MODE
                                                    <div className="recipe-ingredient-compact editing">
                                                      <div className="ingredient-header-line">
                                                        <div className="item-edit-controls">
                                                          <input
                                                            type="number"
                                                            className="quantity-input-inline"
                                                            value={ingredient.quantity === '' ? '' : ingredient.quantity}
                                                            onChange={(e) => updateRecipeIngredientQuantity(ingredient.id, e.target.value)}
                                                            step="0.1"
                                                            min="0"
                                                          />
                                                          <select
                                                            className="unit-select-inline"
                                                            value={ingredient.unit}
                                                            onChange={(e) => updateRecipeIngredientUnit(ingredient.id, e.target.value)}
                                                          >
                                                            {(ingredient.availableUnits || []).map(unitOption => (
                                                              <option key={unitOption.unit_name} value={unitOption.unit_name}>
                                                                {unitOption.unit_name}
                                                              </option>
                                                            ))}
                                                          </select>
                                                          <span className="ingredient-name-text">{ingredient.ingredient_name}</span>
                                                        </div>
                                                        <button
                                                          className="save-item-btn"
                                                          onClick={() => setEditingItems(prev => ({ ...prev, [ingredient.id]: false }))}
                                                          title="Done editing"
                                                        >
                                                          ✓
                                                        </button>
                                                      </div>
                                                      <div className="ingredient-macros-inline">
                                                        <span className="macros-text">{renderInlineMacros(ingredient)}</span>
                                                      </div>
                                                    </div>
                                                  )}
                                                </React.Fragment>
                                              ))
                                            }
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    // Render ingredient within meal
                                    <div className="meal-ingredient-item">
                                      {!editingItems[mealItem.id] ? (
                                        // VIEW MODE
                                        <>
                                          <div className="ingredient-header-line">
                                            <span className="ingredient-display-text">
                                              {mealItem.quantity} {mealItem.unit} {mealItem.ingredient_name}
                                            </span>
                                            <button
                                              className="edit-item-btn"
                                              onClick={() => startEditingItem(mealItem.id)}
                                              title="Edit ingredient"
                                              disabled={loadingUnitsFor[mealItem.id]}
                                            >
                                              {loadingUnitsFor[mealItem.id] ? '⏳' : '✏️'}
                                            </button>
                                          </div>
                                          <div className="ingredient-macros-inline">
                                            <span className="macros-text">{renderInlineMacros(mealItem)}</span>
                                            <button
                                              onClick={() => removeItem(mealItem.id)}
                                              className="remove-button-compact"
                                              title="Remove ingredient"
                                            >
                                              ×
                                            </button>
                                          </div>
                                        </>
                                      ) : (
                                        // EDIT MODE
                                        <>
                                          <div className="ingredient-header-line">
                                            <div className="item-edit-controls">
                                              <input
                                                type="number"
                                                className="quantity-input-inline"
                                                value={mealItem.quantity === '' ? '' : mealItem.quantity}
                                                onChange={(e) => updateRecipeIngredientQuantity(mealItem.id, e.target.value)}
                                                step="0.1"
                                                min="0"
                                              />
                                              <select
                                                className="unit-select-inline"
                                                value={mealItem.unit}
                                                onChange={(e) => updateRecipeIngredientUnit(mealItem.id, e.target.value)}
                                              >
                                                {(mealItem.availableUnits || []).map(unitOption => (
                                                  <option key={unitOption.unit_name} value={unitOption.unit_name}>
                                                    {unitOption.unit_name}
                                                  </option>
                                                ))}
                                              </select>
                                              <span className="ingredient-name-text">{mealItem.ingredient_name}</span>
                                            </div>
                                            <button
                                              className="save-item-btn"
                                              onClick={() => setEditingItems(prev => ({ ...prev, [mealItem.id]: false }))}
                                              title="Done editing"
                                            >
                                              ✓
                                            </button>
                                          </div>
                                          <div className="ingredient-macros-inline">
                                            <span className="macros-text">{renderInlineMacros(mealItem)}</span>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </React.Fragment>
                              ))
                            }
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {/* Template Loading Modal for Daily Tab */}
      {showTemplateModal && (
        <div className="template-modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="template-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="template-modal-header">
              <h2>Load Template into Today</h2>
              <button onClick={() => setShowTemplateModal(false)} className="modal-close-button">×</button>
            </div>
            <div className="template-modal-body">
              {savedCombinations.length === 0 ? (
                <p className="empty-message">No templates saved yet. Save your daily tracking as a template first!</p>
              ) : (
                <>
                  {/* Uncategorized templates */}
                  {savedCombinations.filter(c => !c.folder_id).length > 0 && (
                    <div className="template-modal-section">
                      <h4>Uncategorized</h4>
                      {savedCombinations
                        .filter(c => !c.folder_id)
                        .map((combination) => (
                          <div key={combination.id} className="template-modal-item">
                            <div className="template-modal-item-info">
                              <h5>{combination.name}</h5>
                              <div className="template-modal-macros">
                                <span><strong>{calculateCombinationMacros(combination).calories.toFixed(0)}</strong> cal</span>
                                <span><strong>{calculateCombinationMacros(combination).protein.toFixed(0)}</strong>p</span>
                                <span><strong>{calculateCombinationMacros(combination).carbs.toFixed(0)}</strong>c</span>
                                <span><strong>{calculateCombinationMacros(combination).fat.toFixed(0)}</strong>f</span>
                              </div>
                              {combination.description && (
                                <p className="template-modal-description">{combination.description}</p>
                              )}
                            </div>
                            <button
                              onClick={() => addTemplateToDaily(combination)}
                              className="add-to-day-button"
                            >
                              Add to Today
                            </button>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Folders with templates */}
                  {folders.map((folder) => {
                    const folderCombinations = savedCombinations.filter(c => c.folder_id === folder.id);
                    if (folderCombinations.length === 0) return null;

                    return (
                      <div key={folder.id} className="template-modal-section">
                        <h4>📁 {folder.name}</h4>
                        {folderCombinations.map((combination) => (
                          <div key={combination.id} className="template-modal-item">
                            <div className="template-modal-item-info">
                              <h5>{combination.name}</h5>
                              <div className="template-modal-macros">
                                <span><strong>{calculateCombinationMacros(combination).calories.toFixed(0)}</strong> cal</span>
                                <span><strong>{calculateCombinationMacros(combination).protein.toFixed(0)}</strong>p</span>
                                <span><strong>{calculateCombinationMacros(combination).carbs.toFixed(0)}</strong>c</span>
                                <span><strong>{calculateCombinationMacros(combination).fat.toFixed(0)}</strong>f</span>
                              </div>
                              {combination.description && (
                                <p className="template-modal-description">{combination.description}</p>
                              )}
                            </div>
                            <button
                              onClick={() => addTemplateToDaily(combination)}
                              className="add-to-day-button"
                            >
                              Add to Today
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Templates Tab Content */}
      {activeTab === 'templates' && (
        <div className="templates-tab-content">
          <div className="templates-header">
            <h2>My Templates</h2>
            <button
              className="create-template-btn"
              onClick={() => {
                // Start creating a new template
                setIsCreatingTemplate(true);
                setEditingTemplateId(null);
                setTemplateName('');
                setTemplateDescription('');
                setTemplateFolder(null);
                setTemplateItems([]);
                setTemplateSections([
                  { id: 'default', name: 'General', isDefault: true },
                  { id: 'breakfast1', name: 'Breakfast 1', isDefault: false },
                  { id: 'breakfast2', name: 'Breakfast 2', isDefault: false },
                  { id: 'snack1', name: 'Snack 1', isDefault: false },
                  { id: 'lunch', name: 'Lunch', isDefault: false },
                  { id: 'snack2', name: 'Snack 2', isDefault: false },
                  { id: 'dinner1', name: 'Dinner 1', isDefault: false },
                  { id: 'dinner2', name: 'Dinner 2', isDefault: false },
                  { id: 'dessert', name: 'Dessert', isDefault: false },
                  { id: 'snack3', name: 'Snack 3', isDefault: false }
                ]);
                setTemplateExpandedRecipes({});
                setTemplateCollapsedSections({});
              }}
            >
              + Create New Template
            </button>
          </div>

          {/* Template Browser (when not editing) */}
          {!editingTemplateId && !isCreatingTemplate && (
            <div className="template-browser">
              {savedCombinations.length === 0 ? (
                <p className="empty-message">No templates yet. Create your first template above!</p>
              ) : (
                <>
                  {/* Uncategorized templates */}
                  {savedCombinations.filter(c => !c.folder_id).length > 0 && (
                    <div className="folder-group">
                      <div className="folder-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            className="collapse-toggle"
                            onClick={() => toggleFolderCollapse('uncategorized')}
                          >
                            {collapsedFolders['uncategorized'] ? '▶' : '▼'}
                          </button>
                          <h4 className="folder-name">Uncategorized</h4>
                        </div>
                      </div>
                      {!collapsedFolders['uncategorized'] && (
                        <div className="combinations-in-folder">
                          {savedCombinations
                            .filter(c => !c.folder_id)
                            .map((combination, index) => {
                              const isDragging = draggedCombination?.combination.id === combination.id;
                              const isDragOver = dragOverCombinationIndex === index && draggedCombination?.folderId === null;

                              return (
                                <div
                                  key={combination.id}
                                  className={`saved-combination ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                                  draggable={true}
                                  onDragStart={(e) => handleCombinationDragStart(e, combination, index, null)}
                                  onDragOver={(e) => handleCombinationDragOver(e, index)}
                                  onDragEnd={handleCombinationDragEnd}
                                  onDrop={(e) => handleCombinationDrop(e, index, null)}
                                >
                                  <span className="drag-handle">⋮⋮</span>
                                  <div className="combination-info">
                                    <h3>{combination.name}</h3>
                                    <div className="combination-macros">
                                      <span className="macro"><strong>{calculateCombinationMacros(combination).calories.toFixed(0)}</strong> cal</span>
                                      <span className="macro"><strong>{calculateCombinationMacros(combination).protein.toFixed(0)}</strong>p</span>
                                      <span className="macro"><strong>{calculateCombinationMacros(combination).carbs.toFixed(0)}</strong>c</span>
                                      <span className="macro"><strong>{calculateCombinationMacros(combination).fat.toFixed(0)}</strong>f</span>
                                    </div>
                                    {combination.description && <p className="combination-description">{combination.description}</p>}
                                  </div>
                                  <div className="combination-actions">
                                    <button onClick={() => loadTemplateForEditing(combination)} className="edit-button">
                                      Edit
                                    </button>
                                    <button onClick={() => deleteCombination(combination.id)} className="delete-button">
                                      ×
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Folders with templates */}
                  {folders.map((folder) => {
                    const folderCombinations = savedCombinations.filter(c => c.folder_id === folder.id);
                    if (folderCombinations.length === 0) return null;

                    return (
                      <div key={folder.id} className="folder-group">
                        <div className="folder-header">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                              className="collapse-toggle"
                              onClick={() => toggleFolderCollapse(folder.id)}
                            >
                              {collapsedFolders[folder.id] ? '▶' : '▼'}
                            </button>
                            <h4 className="folder-name">📁 {folder.name}</h4>
                          </div>
                        </div>
                        {!collapsedFolders[folder.id] && (
                          <div className="combinations-in-folder">
                            {folderCombinations.map((combination, combIndex) => {
                              const isDragging = draggedCombination?.combination.id === combination.id;
                              const isDragOver = dragOverCombinationIndex === combIndex && draggedCombination?.folderId === folder.id;

                              return (
                                <div
                                  key={combination.id}
                                  className={`saved-combination ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                                  draggable={true}
                                  onDragStart={(e) => handleCombinationDragStart(e, combination, combIndex, folder.id)}
                                  onDragOver={(e) => handleCombinationDragOver(e, combIndex)}
                                  onDragEnd={handleCombinationDragEnd}
                                  onDrop={(e) => handleCombinationDrop(e, combIndex, folder.id)}
                                >
                                  <span className="drag-handle">⋮⋮</span>
                                  <div className="combination-info">
                                    <h3>{combination.name}</h3>
                                    <div className="combination-macros">
                                      <span className="macro"><strong>{calculateCombinationMacros(combination).calories.toFixed(0)}</strong> cal</span>
                                      <span className="macro"><strong>{calculateCombinationMacros(combination).protein.toFixed(0)}</strong>p</span>
                                      <span className="macro"><strong>{calculateCombinationMacros(combination).carbs.toFixed(0)}</strong>c</span>
                                      <span className="macro"><strong>{calculateCombinationMacros(combination).fat.toFixed(0)}</strong>f</span>
                                    </div>
                                    {combination.description && <p className="combination-description">{combination.description}</p>}
                                  </div>
                                  <div className="combination-actions">
                                    <button onClick={() => loadTemplateForEditing(combination)} className="edit-button">
                                      Edit
                                    </button>
                                    <button onClick={() => deleteCombination(combination.id)} className="delete-button">
                                      ×
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* Template Editor (when editing or creating) */}
          {(editingTemplateId || isCreatingTemplate) && (
            <div className="template-editor">
              <div className="template-form">
                <input
                  type="text"
                  placeholder="Template name..."
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="template-name-input"
                />
                <textarea
                  placeholder="Description (optional)..."
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  className="template-description-input"
                  rows="2"
                />
                <select
                  value={templateFolder || ''}
                  onChange={(e) => setTemplateFolder(e.target.value ? parseInt(e.target.value) : null)}
                  className="template-folder-select"
                >
                  <option value="">📂 Uncategorized</option>
                  {folders.map(folder => (
                    <option key={folder.id} value={folder.id}>📁 {folder.name}</option>
                  ))}
                </select>
                <div className="template-actions">
                  <button onClick={() => saveCombination()} className="save-template-btn">
                    {editingTemplateId ? '💾 Update Template' : '💾 Save Template'}
                  </button>
                  {editingTemplateId && (
                    <button
                      onClick={() => {
                        // Save as new template by clearing the editing ID
                        const currentEditId = editingTemplateId;
                        setEditingTemplateId(null);
                        saveCombination();
                        // Don't restore the edit ID after saving
                      }}
                      className="save-as-new-btn"
                      title="Save as a new template instead of updating the current one"
                    >
                      💾 Save As New
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (templateItems.length > 0 && !window.confirm('Discard unsaved changes?')) return;
                      setIsCreatingTemplate(false);
                      setEditingTemplateId(null);
                      setTemplateName('');
                      setTemplateDescription('');
                      setTemplateFolder(null);
                      setTemplateItems([]);
                      setTemplateSections([
                        { id: 'default', name: 'General', isDefault: true },
                        { id: 'breakfast1', name: 'Breakfast 1', isDefault: false },
                        { id: 'breakfast2', name: 'Breakfast 2', isDefault: false },
                        { id: 'snack1', name: 'Snack 1', isDefault: false },
                        { id: 'lunch', name: 'Lunch', isDefault: false },
                        { id: 'snack2', name: 'Snack 2', isDefault: false },
                        { id: 'dinner1', name: 'Dinner 1', isDefault: false },
                        { id: 'dinner2', name: 'Dinner 2', isDefault: false },
                        { id: 'dessert', name: 'Dessert', isDefault: false },
                        { id: 'snack3', name: 'Snack 3', isDefault: false }
                      ]);
                      setTemplateExpandedRecipes({});
                      setTemplateCollapsedSections({});
                    }}
                    className="cancel-template-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Template Total Macros */}
              {(templateItems.length > 0 || editingTemplateId) && (
                <div className="template-macros-display">
                  <h3>Template Macros</h3>
                  <div className="macros-grid">
                    {(() => {
                      const totals = calculateTemplateTotalMacros();
                      return (
                        <>
                          <div className="macro-item calories">
                            <span className="macro-label">Calories</span>
                            <span className="macro-value">{totals.calories.toFixed(0)}</span>
                          </div>
                          <div className="macro-item protein">
                            <span className="macro-label">Protein</span>
                            <span className="macro-value">{totals.protein.toFixed(1)}g</span>
                          </div>
                          <div className="macro-item carbs">
                            <span className="macro-label">Carbs</span>
                            <span className="macro-value">{totals.carbs.toFixed(1)}g</span>
                          </div>
                          <div className="macro-item fat">
                            <span className="macro-label">Fat</span>
                            <span className="macro-value">{totals.fat.toFixed(1)}g</span>
                          </div>
                          <div className="macro-item fiber">
                            <span className="macro-label">Fiber</span>
                            <span className="macro-value">{totals.fiber.toFixed(1)}g</span>
                          </div>
                          <div className="macro-item sugar">
                            <span className="macro-label">Sugar</span>
                            <span className="macro-value">{totals.sugar.toFixed(1)}g</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Search and Add Items */}
              <div className="template-search-section">
                <h3>Add Items</h3>

                <div className="search-type-toggle">
                  <button
                    className={searchType === "ingredient" ? "active" : ""}
                    onClick={() => {
                      setSearchType("ingredient");
                      setPendingItem(null);
                      setPendingMeal(null);
                    }}
                  >
                    Ingredients
                  </button>
                  <button
                    className={searchType === "recipe" ? "active" : ""}
                    onClick={() => {
                      setSearchType("recipe");
                      setPendingItem(null);
                      setPendingMeal(null);
                    }}
                  >
                    Recipes
                  </button>
                </div>

                <div className="search-container">
                  <div className="search-bar">
                    <input
                      type="text"
                      placeholder={`Search ${searchType}s...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => searchQuery && setShowDropdown(true)}
                      onBlur={(e) => {
                        if (!e.relatedTarget?.closest('.search-results')) {
                          setTimeout(() => setShowDropdown(false), 200);
                        }
                      }}
                    />
                    {isSearching && <span className="loading-spinner">⟳</span>}
                  </div>

                  {showDropdown && searchResults.length > 0 && (
                    <div className="search-results">
                      {searchResults.map((result) => (
                        <div
                          key={result.ingredient_id || result.recipe_id || result.id}
                          className="search-result-item"
                          onClick={() => addItemToTemplate(result)}
                          tabIndex="0"
                        >
                          <div className="result-info">
                            <span className="result-name">{result.ingredient_name || result.name}</span>
                            {searchType === "ingredient" && (
                              <span className="result-macros">
                                <strong>{result.calories?.toFixed(0) || 0}</strong> cal •
                                <strong>{result.protein || 0}</strong>g protein •
                                <strong>{result.carbs || 0}</strong>g carbs •
                                <strong>{result.fat || 0}</strong>g fat
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showDropdown && searchQuery && searchResults.length === 0 && !isSearching && (
                    <div className="search-results">
                      <div className="no-results">No {searchType}s found</div>
                    </div>
                  )}
                </div>

                {/* Section Management */}
                <div className="section-management-inline">
                  <h4>Manage Sections</h4>
                  {!showSectionInput ? (
                    <button
                      className="add-section-btn"
                      onClick={() => setShowSectionInput(true)}
                      title="Add new section"
                    >
                      + Add Section
                    </button>
                  ) : (
                    <div className="new-section-input">
                      <input
                        type="text"
                        placeholder="Section name..."
                        value={newSectionName}
                        onChange={(e) => setNewSectionName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addTemplateSection()}
                        autoFocus
                      />
                      <button onClick={addTemplateSection} className="confirm-btn">✓</button>
                      <button onClick={() => {
                        setShowSectionInput(false);
                        setNewSectionName('');
                      }} className="cancel-btn">✗</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Items */}
              {templateItems.length > 0 && (
                <div className="template-selected-items">
                  <div className="selected-items-header">
                    <h3>Template Items</h3>
                    <div className="section-controls-buttons">
                      <button
                        className="toggle-detail-btn"
                        onClick={() => setShowTemplateDetailedMacros(!showTemplateDetailedMacros)}
                        title={showTemplateDetailedMacros ? "Hide macro details" : "Show macro details"}
                      >
                        {showTemplateDetailedMacros ? '📊 Hide Macros' : '📊 Show Macros'}
                      </button>
                      <button
                        className="collapse-all-btn"
                        onClick={() => {
                          const allCollapsed = templateSections.every(s => templateCollapsedSections[s.id]);
                          const newState = {};
                          templateSections.forEach(s => {
                            newState[s.id] = !allCollapsed;
                          });
                          setTemplateCollapsedSections(newState);
                        }}
                        title={templateSections.every(s => templateCollapsedSections[s.id]) ? "Expand all sections" : "Collapse all sections"}
                      >
                        {templateSections.every(s => templateCollapsedSections[s.id]) ? '⬇️ Expand All' : '⬆️ Collapse All'}
                      </button>
                    </div>
                  </div>
                  {templateSections.map((section) => {
                    const sectionItems = templateItems.filter(item => !item.fromRecipe && item.section === section.id);
                    if (sectionItems.length === 0) return null;

                    const sectionMacros = calculateTemplateSectionMacros(section.id);
                    const isCollapsed = templateCollapsedSections[section.id];

                    return (
                      <div key={section.id} className="section-group">
                        <div className="section-header-wrapper">
                          <h4 className="section-header">
                            <button
                              className="collapse-toggle"
                              onClick={() => toggleTemplateSectionCollapse(section.id)}
                            >
                              {isCollapsed ? '▶' : '▼'}
                            </button>
                            {section.name}
                          </h4>
                          <div className="section-controls-right">
                            <div className="section-macros">
                              <span className="section-macro"><strong>{sectionMacros.calories.toFixed(0)}</strong> cal</span>
                              <span className="section-macro"><strong>{sectionMacros.protein.toFixed(1)}</strong>g protein</span>
                              <span className="section-macro"><strong>{sectionMacros.carbs.toFixed(1)}</strong>g carbs</span>
                              <span className="section-macro"><strong>{sectionMacros.fat.toFixed(1)}</strong>g fat</span>
                            </div>
                          </div>
                        </div>
                        {!isCollapsed && (
                          <div className="section-items">
                            {sectionItems.map((item) => (
                              <React.Fragment key={item.id}>
                                {!editingTemplateItems[item.id] ? (
                                  // VIEW MODE
                                  <div className={`selected-item-compact ${item.type === 'recipe' ? 'recipe-type' : 'ingredient-type'}`}>
                                    <div className="item-header-line">
                                      <div className="item-name-with-quantity">
                                        {item.type === "recipe" && (
                                          <button
                                            className="expand-toggle"
                                            onClick={() => toggleTemplateRecipeExpanded(item.recipe_id || item.id)}
                                            title={templateExpandedRecipes[item.recipe_id || item.id] ? "Collapse" : "Expand"}
                                          >
                                            {templateExpandedRecipes[item.recipe_id || item.id] ? '▼' : '▶'}
                                          </button>
                                        )}
                                        <span className="item-display-text">
                                          {item.type === "recipe"
                                            ? `${item.quantity} serving${item.quantity !== 1 ? 's' : ''} - ${item.name || item.ingredient_name}`
                                            : `${item.quantity} ${item.unit} ${item.ingredient_name || item.name}`
                                          }
                                        </span>
                                      </div>
                                      <button
                                        className="edit-item-btn"
                                        onClick={() => startEditingTemplateItem(item.id)}
                                        title="Edit quantity and unit"
                                        disabled={loadingTemplateUnitsFor[item.id]}
                                      >
                                        {loadingTemplateUnitsFor[item.id] ? '⏳' : '✏️'}
                                      </button>
                                    </div>
                                    <div className="item-macros-inline">
                                      <span className="macros-text">{renderInlineMacros(item, templateItems)}</span>
                                      <button
                                        onClick={() => removeTemplateItem(item.id)}
                                        className="remove-button-compact"
                                        title="Remove item"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  // EDIT MODE
                                  <div className={`selected-item-compact editing ${item.type === 'recipe' ? 'recipe-type' : 'ingredient-type'}`}>
                                    <div className="item-header-line">
                                      <div className="item-edit-controls">
                                        {item.type === "recipe" && (
                                          <button
                                            className="expand-toggle"
                                            onClick={() => toggleTemplateRecipeExpanded(item.recipe_id || item.id)}
                                            title={templateExpandedRecipes[item.recipe_id || item.id] ? "Collapse" : "Expand"}
                                          >
                                            {templateExpandedRecipes[item.recipe_id || item.id] ? '▼' : '▶'}
                                          </button>
                                        )}
                                        <input
                                          type="number"
                                          className="quantity-input-inline"
                                          value={item.quantity === '' ? '' : item.quantity}
                                          onChange={(e) => updateTemplateItemQuantity(item.id, e.target.value)}
                                          min="0"
                                          step="0.1"
                                          autoFocus
                                        />
                                        {item.availableUnits && item.availableUnits.length > 0 ? (
                                          <select
                                            value={item.unit}
                                            onChange={(e) => updateTemplateItemUnit(item.id, e.target.value)}
                                            className="unit-select-inline"
                                          >
                                            {item.availableUnits.map(unit => (
                                              <option key={unit.unit_id} value={unit.unit_name}>
                                                {unit.unit_name}
                                              </option>
                                            ))}
                                          </select>
                                        ) : (
                                          <span className="unit-text-inline">{item.unit}</span>
                                        )}
                                        {/* Section selector */}
                                        <select
                                          value={item.section}
                                          onChange={(e) => updateTemplateItemSection(item.id, e.target.value)}
                                          className="section-select-inline"
                                          title={item.type === 'recipe'
                                            ? "Change section (will move all ingredients)"
                                            : "Change section"}
                                        >
                                          {templateSections.map(section => (
                                            <option key={section.id} value={section.id}>
                                              {section.name}
                                            </option>
                                          ))}
                                        </select>
                                        <span className="item-name-editing">
                                          {item.type === "recipe"
                                            ? `${item.name || item.ingredient_name}`
                                            : `${item.ingredient_name || item.name}`
                                          }
                                        </span>
                                      </div>
                                      <button
                                        className="save-item-btn"
                                        onClick={() => saveTemplateItemEdit(item.id)}
                                        title="Save changes"
                                      >
                                        ✓
                                      </button>
                                    </div>
                                    <div className="item-macros-inline">
                                      <span className="macros-text">{renderInlineMacros(item, templateItems)}</span>
                                      <button
                                        onClick={() => removeTemplateItem(item.id)}
                                        className="remove-button-compact"
                                        title="Remove item"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Recipe ingredients dropdown */}
                                {item.type === "recipe" && templateExpandedRecipes[item.recipe_id || item.id] && (
                                  <div className="recipe-ingredients-dropdown">
                                    {loadingRecipeIngredients[item.recipe_id || item.id] ? (
                                      <div className="loading-ingredients">Loading ingredients...</div>
                                    ) : (
                                      <>
                                        <div className="recipe-ingredients-header">
                                          <span>Ingredients:</span>
                                        </div>
                                        <div className="recipe-ingredients-list">
                                          {templateItems
                                            .filter(si => si.fromRecipe === (item.recipe_id || item.id) && si.type === "ingredient")
                                            .map(ingredient => (
                                              <React.Fragment key={ingredient.id}>
                                                {!editingTemplateItems[ingredient.id] ? (
                                                  // VIEW MODE for ingredient
                                                  <div className="recipe-ingredient-compact">
                                                    <div className="ingredient-header-line">
                                                      <span className="ingredient-display-text">
                                                        {ingredient.quantity} {ingredient.unit} {ingredient.ingredient_name}
                                                      </span>
                                                      <button
                                                        className="edit-item-btn"
                                                        onClick={() => startEditingTemplateItem(ingredient.id)}
                                                        title="Edit ingredient"
                                                        disabled={loadingTemplateUnitsFor[ingredient.id]}
                                                      >
                                                        {loadingTemplateUnitsFor[ingredient.id] ? '⏳' : '✏️'}
                                                      </button>
                                                    </div>
                                                    <div className="ingredient-macros-inline">
                                                      <span className="macros-text">{renderInlineMacros(ingredient, templateItems)}</span>
                                                      <button
                                                        onClick={() => removeTemplateItem(ingredient.id)}
                                                        className="remove-button-compact"
                                                        title="Remove ingredient"
                                                      >
                                                        ×
                                                      </button>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  // EDIT MODE for ingredient
                                                  <div className="recipe-ingredient-compact editing">
                                                    <div className="ingredient-header-line">
                                                      <div className="item-edit-controls">
                                                        <input
                                                          type="number"
                                                          className="quantity-input-inline"
                                                          value={ingredient.quantity === '' ? '' : ingredient.quantity}
                                                          onChange={(e) => updateTemplateItemQuantity(ingredient.id, e.target.value)}
                                                          min="0"
                                                          step="0.1"
                                                          autoFocus
                                                        />
                                                        {ingredient.availableUnits && ingredient.availableUnits.length > 0 ? (
                                                          <select
                                                            value={ingredient.unit}
                                                            onChange={(e) => updateTemplateItemUnit(ingredient.id, e.target.value)}
                                                            className="unit-select-inline"
                                                          >
                                                            {ingredient.availableUnits.map(unit => (
                                                              <option key={unit.unit_id} value={unit.unit_name}>
                                                                {unit.unit_name}
                                                              </option>
                                                            ))}
                                                          </select>
                                                        ) : (
                                                          <span className="unit-text-inline">{ingredient.unit}</span>
                                                        )}
                                                        <span className="item-name-editing">
                                                          {ingredient.ingredient_name}
                                                        </span>
                                                      </div>
                                                      <button
                                                        className="save-item-btn"
                                                        onClick={() => saveTemplateItemEdit(ingredient.id)}
                                                        title="Save changes"
                                                      >
                                                        ✓
                                                      </button>
                                                    </div>
                                                    <div className="ingredient-macros-inline">
                                                      <span className="macros-text">{renderInlineMacros(ingredient, templateItems)}</span>
                                                      <button
                                                        onClick={() => removeTemplateItem(ingredient.id)}
                                                        className="remove-button-compact"
                                                        title="Remove ingredient"
                                                      >
                                                        ×
                                                      </button>
                                                    </div>
                                                  </div>
                                                )}
                                              </React.Fragment>
                                            ))}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===================================== */}
      {/* MEALS TAB CONTENT */}
      {/* ===================================== */}
      {activeTab === 'meals' && (
        <div className="meals-tab-content">
          <div className="meals-header">
            <h2>My Meals</h2>
            <button
              className="create-meal-btn"
              onClick={() => {
                setIsCreatingMeal(true);
                setEditingMealId(null);
                setMealName('');
                setMealDescription('');
                setMealItems([]);
                setMealExpandedRecipes({});
              }}
            >
              + Create New Meal
            </button>
          </div>

          {/* Meal Browser (when not editing) */}
          {!editingMealId && !isCreatingMeal && (
            <div className="meal-browser">
              {savedMeals.length === 0 ? (
                <p className="empty-message">No meals yet. Create your first meal above!</p>
              ) : (
                <div className="meals-list">
                  {savedMeals.map((meal) => {
                    const mealMacros = calculateMealMacros(meal);
                    return (
                      <div key={meal.id} className="saved-meal">
                        <div className="meal-info">
                          <h3>{meal.name}</h3>
                          <div className="meal-macros">
                            <span className="macro"><strong>{mealMacros.calories.toFixed(0)}</strong> cal</span>
                            <span className="macro"><strong>{mealMacros.protein.toFixed(0)}</strong>p</span>
                            <span className="macro"><strong>{mealMacros.carbs.toFixed(0)}</strong>c</span>
                            <span className="macro"><strong>{mealMacros.fat.toFixed(0)}</strong>f</span>
                          </div>
                          {meal.description && <p className="meal-description">{meal.description}</p>}
                        </div>
                        <div className="meal-actions">
                          <button onClick={() => loadMealForEditing(meal)} className="edit-button">
                            Edit
                          </button>
                          <button onClick={() => deleteMeal(meal.id)} className="delete-button">
                            ×
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Meal Editor (when creating/editing) */}
          {(editingMealId || isCreatingMeal) && (
            <div className="meal-editor">
              <div className="meal-form">
                <input
                  type="text"
                  placeholder="Meal name (e.g., Mike's Breakfast)..."
                  value={mealName}
                  onChange={(e) => setMealName(e.target.value)}
                  className="meal-name-input"
                />
                <textarea
                  placeholder="Description (optional)..."
                  value={mealDescription}
                  onChange={(e) => setMealDescription(e.target.value)}
                  className="meal-description-input"
                  rows="2"
                />
                <div className="meal-form-actions">
                  <button onClick={() => saveMeal()} className="save-meal-btn">
                    {editingMealId ? '💾 Update Meal' : '💾 Save Meal'}
                  </button>
                  <button
                    onClick={() => {
                      if (mealItems.length > 0 && !window.confirm('Discard unsaved changes?')) return;
                      setIsCreatingMeal(false);
                      setEditingMealId(null);
                    }}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Search and Add Items to Meal */}
              <div className="meal-search-section">
                <h3>Add Ingredients & Recipes</h3>

                <div className="search-type-toggle">
                  <button
                    className={searchType === "ingredient" ? "active" : ""}
                    onClick={() => {
                      setSearchType("ingredient");
                      setPendingItem(null);
                      setPendingMeal(null);
                    }}
                  >
                    Ingredients
                  </button>
                  <button
                    className={searchType === "recipe" ? "active" : ""}
                    onClick={() => {
                      setSearchType("recipe");
                      setPendingItem(null);
                      setPendingMeal(null);
                    }}
                  >
                    Recipes
                  </button>
                </div>

                <div className="search-container">
                  <div className="search-bar">
                    <input
                      type="text"
                      placeholder={`Search ${searchType}s...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => searchQuery && setShowDropdown(true)}
                      onBlur={(e) => {
                        if (!e.relatedTarget?.closest('.search-results')) {
                          setTimeout(() => setShowDropdown(false), 200);
                        }
                      }}
                    />
                    {isSearching && <span className="loading-spinner">⟳</span>}
                  </div>

                  {showDropdown && searchResults.length > 0 && (
                    <div className="search-results">
                      {searchResults.map((result) => (
                        <div
                          key={result.ingredient_id || result.recipe_id || result.id}
                          className="search-result-item"
                          onClick={() => {
                            // Add item to meal directly (no pending form for meals)
                            addItemToMeal(result);
                          }}
                          tabIndex="0"
                        >
                          <div className="result-info">
                            <span className="result-name">{result.ingredient_name || result.name}</span>
                            {searchType === "ingredient" && (
                              <span className="result-macros">
                                <strong>{result.calories?.toFixed(0) || 0}</strong> cal •
                                <strong>{result.protein || 0}</strong>g protein •
                                <strong>{result.carbs || 0}</strong>g carbs •
                                <strong>{result.fat || 0}</strong>g fat
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showDropdown && searchQuery && searchResults.length === 0 && !isSearching && (
                    <div className="search-results">
                      <div className="no-results">No {searchType}s found</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="meal-editor-content">
                <h3>Meal Contents</h3>
                {mealItems.length === 0 ? (
                  <p className="empty-meal-message">Search for ingredients or recipes above to build your meal.</p>
                ) : (
                  <div className="meal-items-display">
                    {mealItems.filter(item => !item.fromRecipe).map((item) => (
                      <div key={item.id} className="meal-item">
                        <div className="item-row">
                          {!editingMealItems[item.id] ? (
                            // Display mode
                            <>
                              {item.type === "recipe" && (
                                <button
                                  className="expand-toggle-meal"
                                  onClick={() => {
                                    const recipeId = item.recipe_id || item.id;
                                    setMealExpandedRecipes(prev => ({
                                      ...prev,
                                      [recipeId]: !prev[recipeId]
                                    }));
                                  }}
                                  title={mealExpandedRecipes[item.recipe_id || item.id] ? "Collapse" : "Expand"}
                                >
                                  {mealExpandedRecipes[item.recipe_id || item.id] ? '▼' : '▶'}
                                </button>
                              )}
                              <span className="item-quantity-unit">
                                {item.quantity} {item.unit}
                              </span>
                              <span className="item-name">
                                {item.type === "recipe" ? `${item.name || item.ingredient_name}` : `${item.ingredient_name || item.name}`}
                              </span>
                              <div className="item-actions">
                                <button
                                  className="edit-item-btn"
                                  onClick={() => {
                                    setEditingMealItems({ ...editingMealItems, [item.id]: true });
                                    // Load units for this item if ingredient
                                    if (item.type === 'ingredient') {
                                      loadUnitsForMealItem(item);
                                    }
                                  }}
                                  title="Edit quantity/unit"
                                >
                                  ✏️
                                </button>
                                <button
                                  className="remove-item-btn"
                                  onClick={() => removeMealItem(item.id)}
                                  title="Remove from meal"
                                >
                                  ×
                                </button>
                              </div>
                            </>
                          ) : (
                            // Edit mode
                            <div className="ingredient-header-line">
                              <div className="item-edit-controls">
                                <input
                                  type="number"
                                  className="quantity-input-inline"
                                  value={item.quantity === '' ? '' : item.quantity}
                                  onChange={(e) => updateMealItemQuantity(item.id, e.target.value)}
                                  min="0"
                                  step="0.1"
                                  autoFocus
                                />
                                {item.availableUnits && item.availableUnits.length > 0 ? (
                                  <select
                                    value={item.unit}
                                    onChange={(e) => updateMealItemUnit(item.id, e.target.value)}
                                    className="unit-select-inline"
                                  >
                                    {item.availableUnits.map(unit => (
                                      <option key={unit.unit_id} value={unit.unit_name}>
                                        {unit.unit_name}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="unit-text-inline">{item.unit}</span>
                                )}
                                <span className="item-name-editing">
                                  {item.type === "recipe" ? `${item.name || item.ingredient_name}` : `${item.ingredient_name || item.name}`}
                                </span>
                              </div>
                              <button
                                className="save-item-btn"
                                onClick={() => {
                                  const newEditingMealItems = { ...editingMealItems };
                                  delete newEditingMealItems[item.id];
                                  setEditingMealItems(newEditingMealItems);
                                }}
                                title="Done editing"
                              >
                                ✓
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Recipe ingredients expansion */}
                        {item.type === 'recipe' && mealExpandedRecipes[item.recipe_id || item.id] && (
                          <div className="recipe-ingredients-display">
                            {mealItems
                              .filter(ing => ing.fromRecipe === (item.recipe_id || item.id))
                              .map(ing => (
                                <div key={ing.id} className="recipe-ingredient-row">
                                  <span className="ingredient-quantity">
                                    {ing.quantity} {ing.unit}
                                  </span>
                                  <span className="ingredient-name">{ing.ingredient_name}</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Meal Macros Summary */}
                    <div className="meal-macros-summary">
                      <strong>Total Macros:</strong>
                      <span>{calculateCurrentMealMacros().calories.toFixed(0)} cal</span>
                      <span>{calculateCurrentMealMacros().protein.toFixed(1)}p</span>
                      <span>{calculateCurrentMealMacros().carbs.toFixed(1)}c</span>
                      <span>{calculateCurrentMealMacros().fat.toFixed(1)}f</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* MACRO LOGS TAB CONTENT */}
      {activeTab === 'macroLogs' && (
        <div className="macro-logs-tab-content">
          {/* Sub-tabs for Macro Logs */}
          <div className="weight-sub-tabs">
            <button
              className={macroLogsViewMode === 'analytics' ? 'active' : ''}
              onClick={() => setMacroLogsViewMode('analytics')}
            >
              📈 Analytics
            </button>
            <button
              className={macroLogsViewMode === 'history' ? 'active' : ''}
              onClick={() => {
                setMacroLogsViewMode('history');
                fetchHistoryLogs();
              }}
            >
              📊 History
            </button>
          </div>

          {/* Analytics View */}
          {macroLogsViewMode === 'analytics' && <AnalyticsTab />}

          {/* History View */}
          {macroLogsViewMode === 'history' && (
            <>
              <div className="history-header">
                <h2>Daily Log History</h2>
                <button onClick={fetchHistoryLogs} className="refresh-button">
                  🔄 Refresh
                </button>
              </div>

          {isLoadingHistory ? (
            <div className="loading">Loading history...</div>
          ) : historyLogs.length === 0 ? (
            <div className="empty-state">
              <p>No saved logs yet.</p>
              <p>Start tracking your daily food intake and click "Save Day" to create your first log!</p>
            </div>
          ) : (
            <div className="history-list">
              {historyLogs.map(log => (
                <div key={log.id} className="history-log-card">
                  <div className="log-date">
                    📅 {formatLocalDate(log.log_date)}
                  </div>

                  {log.goals?.goal_type && (
                    <div className={`log-goal-type ${log.goals.goal_type}`}>
                      🎯 {formatGoalType(log.goals.goal_type)}
                    </div>
                  )}

                  {log.notes && (
                    <div className="log-notes">
                      📝 {log.notes}
                    </div>
                  )}

                  {/* Enhanced totals with goals */}
                  {log.goals ? (
                    <div className="log-totals-with-goals">
                      <span>
                        {Math.round(log.totals?.calories || 0)} / {Math.round(calculateCaloriesFromMacros(log.goals.protein, log.goals.carbs, log.goals.fat))} cal
                      </span>
                      <span className="separator">|</span>
                      <span>
                        {Math.round(log.totals?.protein || 0)} / {log.goals.protein}g protein
                      </span>
                      <span className="separator">|</span>
                      <span>
                        {Math.round(log.totals?.carbs || 0)} / {log.goals.carbs}g carbs
                      </span>
                      <span className="separator">|</span>
                      <span>
                        {Math.round(log.totals?.fat || 0)} / {log.goals.fat}g fat
                      </span>
                      {log.goals.fiber > 0 && (
                        <>
                          <span className="separator">|</span>
                          <span>
                            {Math.round(log.totals?.fiber || 0)} / {log.goals.fiber}g fiber
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="log-totals">
                      <span>Calories: {Math.round(log.totals?.calories || 0)}</span>
                      <span>Protein: {Math.round(log.totals?.protein || 0)}g</span>
                      <span>Carbs: {Math.round(log.totals?.carbs || 0)}g</span>
                      <span>Fat: {Math.round(log.totals?.fat || 0)}g</span>
                    </div>
                  )}

                  {!log.goals && (
                    <div className="no-goals-note">
                      (No goals set for this day)
                    </div>
                  )}

                  <div className="log-actions">
                    <button
                      onClick={() => loadLogIntoDaily(log.log_date)}
                      className="load-button"
                    >
                      📂 Load
                    </button>
                    <button
                      onClick={() => openEditDateModal(log.id, log.log_date)}
                      className="edit-button"
                    >
                      📅 Edit Date
                    </button>
                    <button
                      onClick={() => openEditGoalTypeModal(log.id, log.goals?.goal_type)}
                      className="edit-button"
                    >
                      🎯 Edit Goal Type
                    </button>
                    <button
                      onClick={() => deleteHistoryLog(log.id, log.log_date)}
                      className="delete-button"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* WEIGHT TAB CONTENT */}
      {activeTab === 'weight' && (
        <div className="weight-tab-content">
          <div className="weight-sub-tabs">
            <button
              className={weightViewMode === 'logs' ? 'active' : ''}
              onClick={() => setWeightViewMode('logs')}
            >
              📝 Log Weight
            </button>
            <button
              className={weightViewMode === 'goals' ? 'active' : ''}
              onClick={() => setWeightViewMode('goals')}
            >
              🎯 Goals
            </button>
            <button
              className={weightViewMode === 'analytics' ? 'active' : ''}
              onClick={() => setWeightViewMode('analytics')}
            >
              📊 Analytics
            </button>
          </div>

          {weightViewMode === 'logs' && (
            <div className="weight-logs-view">
                <div className="weight-logs-header">
                  <h2>Weight Log</h2>
                </div>

                {/* Inline weight log form */}
                <div
                  className="inline-weight-log-form"
                  style={{
                    marginBottom: '20px',
                    padding: '15px',
                    background: editingWeightLogId ? '#fff3e0' : '#f8f9fa',
                    borderRadius: '8px',
                    border: editingWeightLogId ? '2px solid #ff9800' : 'none'
                  }}
                >
                  <h4 style={{ marginBottom: '15px', marginTop: '0' }}>
                    {editingWeightLogId ? '✏️ Editing Weight Log' : 'Log Your Weight'}
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    <div className="goal-input-group" style={{ marginBottom: '0' }}>
                      <label htmlFor="weight-log-date-inline" style={{ fontSize: '0.9em', marginBottom: '5px', display: 'block' }}>Date</label>
                      <input
                        id="weight-log-date-inline"
                        type="date"
                        className="form-control"
                        value={weightLogDate}
                        onChange={(e) => setWeightLogDate(e.target.value)}
                        max={getTodayLocalDate()}
                        style={{ width: '100%' }}
                      />
                    </div>

                    <div className="goal-input-group" style={{ marginBottom: '0' }}>
                      <label htmlFor="weight-value-inline" style={{ fontSize: '0.9em', marginBottom: '5px', display: 'block' }}>Weight *</label>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <input
                          id="weight-value-inline"
                          type="number"
                          step="0.1"
                          className="form-control"
                          placeholder="185.5"
                          value={weightValue}
                          onChange={(e) => setWeightValue(e.target.value)}
                          style={{ flex: 2 }}
                        />
                        <select
                          className="form-control"
                          value={weightUnit}
                          onChange={(e) => setWeightUnit(e.target.value)}
                          style={{ flex: 1 }}
                        >
                          <option value="lbs">lbs</option>
                          <option value="kg">kg</option>
                        </select>
                      </div>
                    </div>

                    <div className="goal-input-group" style={{ marginBottom: '0' }}>
                      <label htmlFor="weight-time-inline" style={{ fontSize: '0.9em', marginBottom: '5px', display: 'block' }}>Time of Day</label>
                      <select
                        id="weight-time-inline"
                        className="form-control"
                        value={weightTimeOfDay}
                        onChange={(e) => setWeightTimeOfDay(e.target.value)}
                        style={{ width: '100%' }}
                      >
                        <option value="morning">Morning</option>
                        <option value="afternoon">Afternoon</option>
                        <option value="evening">Evening</option>
                        <option value="night">Night</option>
                      </select>
                    </div>

                    <div className="goal-input-group" style={{ marginBottom: '0', gridColumn: '1 / -1' }}>
                      <label htmlFor="weight-notes-inline" style={{ fontSize: '0.9em', marginBottom: '5px', display: 'block' }}>Notes (Optional)</label>
                      <textarea
                        id="weight-notes-inline"
                        className="form-control"
                        rows="2"
                        placeholder="e.g., After morning run, before breakfast..."
                        value={weightNotes}
                        onChange={(e) => setWeightNotes(e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                    <button
                      className="btn btn-primary"
                      onClick={saveWeightLog}
                      disabled={!weightValue}
                    >
                      {editingWeightLogId ? '✓ Update Weight Log' : '💾 Save Weight Log'}
                    </button>
                    {editingWeightLogId && (
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setEditingWeightLogId(null);
                          setWeightLogDate(getTodayLocalDate());
                          setWeightValue('');
                          setWeightTimeOfDay('morning');
                          setWeightNotes('');
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>

                {/* Recent logs table */}
                <div className="weight-logs-list">
                  <h3>Recent Weight Logs</h3>
                  {isLoadingWeight ? (
                    <div className="loading">Loading...</div>
                  ) : weightLogs.length === 0 ? (
                    <div className="empty-state">
                      <p>No weight logs yet.</p>
                      <p>Start tracking your weight to see trends and correlations with your macro adherence!</p>
                    </div>
                  ) : (
                    <div className="weight-logs-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Weight</th>
                            <th>Time of Day</th>
                            <th>Notes</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {weightLogs.map(log => (
                            <tr key={log.id} className="weight-log-row">
                              <td>{formatLocalDate(log.log_date)}</td>
                              <td className="weight-value">
                                {log.weight} {log.unit}
                              </td>
                              <td>{log.time_of_day ? formatTimeOfDay(log.time_of_day) : '-'}</td>
                              <td className="notes-cell">
                                {log.notes ? (
                                  <span className="notes-preview" title={log.notes}>
                                    {log.notes.substring(0, 30)}{log.notes.length > 30 ? '...' : ''}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="actions-cell">
                                <button
                                  onClick={() => editWeightLogInline(log)}
                                  className="edit-button-small"
                                  title="Edit"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => deleteWeightLog(log.id, log.log_date)}
                                  className="delete-button-small"
                                  title="Delete"
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>
            </div>
          )}
          {weightViewMode === 'goals' && <WeightGoalsView />}
          {weightViewMode === 'analytics' && <WeightAnalyticsView />}
        </div>
      )}

      {/* Goals Setting Modal */}
      {showGoalsModal && (
        <div className="goals-modal-overlay" onClick={() => setShowGoalsModal(false)}>
          <div className="goals-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="goals-modal-header">
              <h3>Set Daily Macro Goals</h3>
              <button className="modal-close-btn" onClick={() => setShowGoalsModal(false)}>×</button>
            </div>

            <div className="goals-form">
              {/* PRESET SELECTOR */}
              <div className="goal-preset-section">
                <div className="preset-selector-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label htmlFor="preset-select">Load Preset (Optional)</label>
                    {goalPresets.length > 0 && (
                      <button
                        className="manage-presets-toggle-btn"
                        onClick={() => setShowPresetManagement(!showPresetManagement)}
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                      >
                        {showPresetManagement ? '▼ Hide' : '▶ Manage Presets'}
                      </button>
                    )}
                  </div>
                  <div className="preset-selector-row">
                    <select
                      id="preset-select"
                      className="preset-select"
                      value={selectedPresetId || ''}
                      onChange={(e) => loadPreset(e.target.value)}
                    >
                      <option value="">-- None (Custom) --</option>
                      {goalPresets.map(preset => (
                        <option key={preset.id} value={preset.id}>
                          {preset.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedPresetId && (
                    <div className="preset-info">
                      Currently using: <strong>{goalPresets.find(p => p.id === selectedPresetId)?.name}</strong>
                    </div>
                  )}
                </div>

                {/* PRESET MANAGEMENT SECTION */}
                {showPresetManagement && goalPresets.length > 0 && (
                  <div className="preset-management-section" style={{ marginTop: '15px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '14px' }}>Manage Presets</h4>
                    <div className="preset-list">
                      {goalPresets.map((preset, index) => {
                        const isDragging = draggedPreset?.id === preset.id;
                        const isDragOver = dragOverPresetIndex === index;

                        return (
                          <div
                            key={preset.id}
                            className={`preset-item ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                            draggable={!editingPresetId}
                            onDragStart={(e) => handlePresetDragStart(e, preset, index)}
                            onDragOver={(e) => handlePresetDragOver(e, index)}
                            onDragEnd={handlePresetDragEnd}
                            onDrop={(e) => handlePresetDrop(e, index)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px',
                              marginBottom: '5px',
                              backgroundColor: isDragOver ? '#f0f0f0' : 'white',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              opacity: isDragging ? 0.5 : 1,
                              cursor: editingPresetId ? 'default' : 'move'
                            }}
                          >
                            {!editingPresetId && <span className="drag-handle" style={{ cursor: 'move', color: '#999' }}>⋮⋮</span>}
                            {editingPresetId === preset.id ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                <input
                                  type="text"
                                  value={editingPresetName}
                                  onChange={(e) => setEditingPresetName(e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && renamePreset(preset.id, editingPresetName)}
                                  autoFocus
                                  style={{ flex: 1, padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                                <button
                                  onClick={() => renamePreset(preset.id, editingPresetName)}
                                  className="confirm-btn"
                                  style={{ padding: '4px 8px' }}
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingPresetId(null);
                                    setEditingPresetName('');
                                  }}
                                  className="cancel-btn"
                                  style={{ padding: '4px 8px' }}
                                >
                                  ✗
                                </button>
                              </div>
                            ) : (
                              <>
                                <div style={{ flex: 1, fontSize: '14px' }}>
                                  <strong>{preset.name}</strong>
                                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                    P: {preset.goals.protein}g | C: {preset.goals.carbs}g | F: {preset.goals.fat}g | Fiber: {preset.goals.fiber}g
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    setEditingPresetId(preset.id);
                                    setEditingPresetName(preset.name);
                                  }}
                                  className="edit-preset-btn"
                                  title="Rename preset"
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => deletePreset(preset.id)}
                                  className="delete-preset-btn"
                                  title="Delete preset"
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                >
                                  🗑️
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* MACRO INPUT FIELDS */}
              <div className="goal-input-group">
                <label htmlFor="protein-goal">Protein (g)</label>
                <input
                  id="protein-goal"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g., 150"
                  value={tempGoals.protein}
                  onChange={(e) => {
                    setTempGoals({...tempGoals, protein: e.target.value});
                    if (selectedPresetId) setSelectedPresetId(null);
                  }}
                />
              </div>

              <div className="goal-input-group">
                <label htmlFor="carbs-goal">Carbs (g)</label>
                <input
                  id="carbs-goal"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g., 200"
                  value={tempGoals.carbs}
                  onChange={(e) => {
                    setTempGoals({...tempGoals, carbs: e.target.value});
                    if (selectedPresetId) setSelectedPresetId(null);
                  }}
                />
              </div>

              <div className="goal-input-group">
                <label htmlFor="fat-goal">Fat (g)</label>
                <input
                  id="fat-goal"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g., 60"
                  value={tempGoals.fat}
                  onChange={(e) => {
                    setTempGoals({...tempGoals, fat: e.target.value});
                    if (selectedPresetId) setSelectedPresetId(null);
                  }}
                />
              </div>

              <div className="goal-input-group">
                <label htmlFor="fiber-goal">Fiber (g)</label>
                <input
                  id="fiber-goal"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g., 30"
                  value={tempGoals.fiber}
                  onChange={(e) => {
                    setTempGoals({...tempGoals, fiber: e.target.value});
                    if (selectedPresetId) setSelectedPresetId(null);
                  }}
                />
              </div>

              {/* Calculated Calories Preview */}
              {(tempGoals.protein || tempGoals.carbs || tempGoals.fat) && (
                <div className="calculated-calories-preview">
                  <div className="preview-label">Calculated Daily Calories:</div>
                  <div className="preview-value">
                    {calculateCaloriesFromMacros(
                      parseFloat(tempGoals.protein) || 0,
                      parseFloat(tempGoals.carbs) || 0,
                      parseFloat(tempGoals.fat) || 0
                    ).toFixed(0)} kcal
                  </div>
                  <div className="preview-hint">
                    Based on: Protein × 4 + Carbs × 4 + Fat × 9
                  </div>
                </div>
              )}
            </div>

            <div className="goals-modal-actions">
              <button className="save-btn" onClick={saveGoals}>
                Save Goals
              </button>
              <button className="save-preset-btn" onClick={openSavePresetModal}>
                💾 Save as Preset
              </button>
              {macroGoals && (
                <button className="clear-btn" onClick={clearGoals}>
                  Clear Goals
                </button>
              )}
              <button className="cancel-btn" onClick={() => setShowGoalsModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PRESET SAVE MODAL */}
      {showPresetSaveModal && (
        <div className="goals-modal-overlay" onClick={() => setShowPresetSaveModal(false)}>
          <div className="preset-save-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="preset-modal-header">
              <h3>{isEditingPreset ? 'Update Preset' : 'Save New Preset'}</h3>
              <button className="modal-close-btn" onClick={() => setShowPresetSaveModal(false)}>×</button>
            </div>

            <div className="preset-save-form">
              <div className="preset-name-group">
                <label htmlFor="preset-name-input">Preset Name</label>
                <input
                  id="preset-name-input"
                  type="text"
                  placeholder="e.g., Cutting, Bulking, Maintenance"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  maxLength="100"
                  autoFocus
                />
                <div className="preset-name-hint">
                  {isEditingPreset
                    ? 'Update the name and/or values for this preset'
                    : 'Give your preset a memorable name'}
                </div>
              </div>

              <div className="preset-values-preview">
                <div className="preview-label">Preset will save:</div>
                <div className="preview-macros">
                  <span>Protein: {tempGoals.protein || 0}g</span>
                  <span>Carbs: {tempGoals.carbs || 0}g</span>
                  <span>Fat: {tempGoals.fat || 0}g</span>
                  <span>Fiber: {tempGoals.fiber || 0}g</span>
                </div>
              </div>
            </div>

            <div className="preset-modal-actions">
              <button className="save-btn" onClick={savePreset}>
                {isEditingPreset ? 'Update Preset' : 'Save Preset'}
              </button>
              <button className="cancel-btn" onClick={() => setShowPresetSaveModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SAVE DAY MODAL */}
      {showSaveDayModal && (
        <div className="goals-modal-overlay" onClick={() => setShowSaveDayModal(false)}>
          <div className="goals-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="goals-modal-header">
              <h3>Save Daily Log</h3>
              <button className="modal-close-btn" onClick={() => setShowSaveDayModal(false)}>×</button>
            </div>

            <div className="goals-form">
              {/* DATE PICKER */}
              <div className="goal-input-group">
                <label htmlFor="log-date">Date</label>
                <input
                  id="log-date"
                  type="date"
                  className="form-control"
                  value={saveModalDate}
                  onChange={(e) => setSaveModalDate(e.target.value)}
                  max={getTodayLocalDate()}
                />
              </div>

              {/* NOTES */}
              <div className="goal-input-group">
                <label htmlFor="log-notes">Notes (Optional)</label>
                <textarea
                  id="log-notes"
                  className="form-control"
                  rows="3"
                  placeholder="e.g., 15 mile run, Birthday party, Rest day..."
                  value={saveModalNotes}
                  onChange={(e) => setSaveModalNotes(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>

              {/* GOAL TYPE SELECTOR */}
              <div className="goal-input-group">
                <label htmlFor="log-goal-type">Goal Type</label>
                <select
                  id="log-goal-type"
                  className="form-control"
                  value={saveModalGoalType}
                  onChange={(e) => setSaveModalGoalType(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="maintenance">Maintenance (±10%)</option>
                  <option value="fat_loss">Fat Loss (okay to be under)</option>
                  <option value="performance">Performance (must hit target)</option>
                  <option value="bulking">Bulking (okay to be over)</option>
                </select>
                <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                  This affects how goal adherence is calculated in analytics.
                </small>
              </div>

              {/* TOTALS PREVIEW */}
              <div className="totals-preview" style={{ marginTop: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '8px' }}>
                <h5 style={{ marginBottom: '10px' }}>Daily Totals</h5>
                <div className="macro-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                  <div><strong>Calories:</strong> {Math.round(calculateTotalMacros().calories)}</div>
                  <div><strong>Protein:</strong> {Math.round(calculateTotalMacros().protein)}g</div>
                  <div><strong>Carbs:</strong> {Math.round(calculateTotalMacros().carbs)}g</div>
                  <div><strong>Fat:</strong> {Math.round(calculateTotalMacros().fat)}g</div>
                  <div><strong>Fiber:</strong> {Math.round(calculateTotalMacros().fiber)}g</div>
                  <div><strong>Sugar:</strong> {Math.round(calculateTotalMacros().sugar)}g</div>
                </div>
              </div>

              {/* WARNING IF LOG EXISTS */}
              {logExistsForDate && (
                <div className="alert alert-warning" style={{ marginTop: '15px', padding: '10px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404' }}>
                  ⚠️ A log already exists for {saveModalDate}. Saving will overwrite it.
                </div>
              )}
            </div>

            <div className="goals-modal-actions">
              <button
                className="save-btn"
                onClick={handleSaveDayLog}
                disabled={isSavingDayLog || selectedItems.length === 0}
              >
                {isSavingDayLog ? 'Saving...' : 'Save Log'}
              </button>
              <button className="cancel-btn" onClick={() => setShowSaveDayModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT DATE MODAL */}
      {showEditDateModal && (
        <div className="goals-modal-overlay" onClick={() => setShowEditDateModal(false)}>
          <div className="goals-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="goals-modal-header">
              <h3>Edit Log Date</h3>
              <button className="modal-close-btn" onClick={() => setShowEditDateModal(false)}>×</button>
            </div>

            <div className="goals-form">
              <div className="goal-input-group">
                <label htmlFor="edit-log-date">New Date</label>
                <input
                  id="edit-log-date"
                  type="date"
                  className="form-control"
                  value={editLogDate}
                  onChange={(e) => setEditLogDate(e.target.value)}
                  max={getTodayLocalDate()}
                />
              </div>

              <div style={{ marginTop: '15px', padding: '10px', background: '#e7f3ff', border: '1px solid #2196F3', borderRadius: '4px', color: '#0d47a1' }}>
                ℹ️ This will change the date for this log entry. The log content will remain the same.
              </div>
            </div>

            <div className="goals-modal-actions">
              <button
                className="save-btn"
                onClick={handleUpdateLogDate}
              >
                Update Date
              </button>
              <button className="cancel-btn" onClick={() => setShowEditDateModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Goal Type Modal */}
      {showEditGoalTypeModal && (
        <div className="goals-modal-overlay" onClick={() => setShowEditGoalTypeModal(false)}>
          <div className="goals-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="goals-modal-header">
              <h3>Edit Goal Type</h3>
              <button className="modal-close-btn" onClick={() => setShowEditGoalTypeModal(false)}>×</button>
            </div>

            <div className="goals-form">
              <div className="goal-input-group">
                <label htmlFor="edit-goal-type">Goal Type</label>
                <select
                  id="edit-goal-type"
                  className="form-control"
                  value={editingGoalType}
                  onChange={(e) => setEditingGoalType(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="maintenance">Maintenance (±10%)</option>
                  <option value="fat_loss">Fat Loss (okay to be under)</option>
                  <option value="performance">Performance (must hit target)</option>
                  <option value="bulking">Bulking (okay to be over)</option>
                </select>
                <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                  This affects how goal adherence is calculated in analytics.
                </small>
              </div>

              <div style={{ marginTop: '15px', padding: '10px', background: '#e7f3ff', border: '1px solid #2196F3', borderRadius: '4px', color: '#0d47a1' }}>
                ℹ️ Changing the goal type will update how this day's adherence is calculated in the Analytics tab.
              </div>
            </div>

            <div className="goals-modal-actions">
              <button
                className="save-btn"
                onClick={handleUpdateGoalType}
              >
                Update Goal Type
              </button>
              <button className="cancel-btn" onClick={() => setShowEditGoalTypeModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Weight Log Modal */}
      {showWeightLogModal && (
        <div className="goals-modal-overlay" onClick={() => setShowWeightLogModal(false)}>
          <div className="goals-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="goals-modal-header">
              <h3>{editingWeightLogId ? 'Edit Weight Log' : 'Log Weight'}</h3>
              <button className="modal-close-btn" onClick={() => setShowWeightLogModal(false)}>×</button>
            </div>

            <div className="goals-form">
              <div className="goal-input-group">
                <label htmlFor="weight-log-date">Date</label>
                <input
                  id="weight-log-date"
                  type="date"
                  className="form-control"
                  value={weightLogDate}
                  onChange={(e) => setWeightLogDate(e.target.value)}
                  max={getTodayLocalDate()}
                />
              </div>

              <div className="goal-input-group">
                <label htmlFor="weight-value">Weight *</label>
                <div className="weight-input-group">
                  <input
                    id="weight-value"
                    type="number"
                    step="0.1"
                    className="form-control"
                    placeholder="185.5"
                    value={weightValue}
                    onChange={(e) => setWeightValue(e.target.value)}
                    style={{ flex: 2 }}
                  />
                  <select
                    className="form-control"
                    value={weightUnit}
                    onChange={(e) => setWeightUnit(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="lbs">lbs</option>
                    <option value="kg">kg</option>
                  </select>
                </div>
              </div>

              <div className="goal-input-group">
                <label htmlFor="weight-time">Time of Day</label>
                <select
                  id="weight-time"
                  className="form-control"
                  value={weightTimeOfDay}
                  onChange={(e) => setWeightTimeOfDay(e.target.value)}
                >
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                  <option value="evening">Evening</option>
                  <option value="night">Night</option>
                </select>
              </div>

              <div className="goal-input-group">
                <label htmlFor="weight-notes">Notes (Optional)</label>
                <textarea
                  id="weight-notes"
                  className="form-control"
                  rows="3"
                  placeholder="e.g., After morning run, before breakfast..."
                  value={weightNotes}
                  onChange={(e) => setWeightNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="goals-modal-actions">
              <button
                className="save-btn"
                onClick={saveWeightLog}
                disabled={!weightValue}
              >
                {editingWeightLogId ? 'Update' : 'Save'}
              </button>
              <button className="cancel-btn" onClick={() => setShowWeightLogModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Weight Goal Modal */}
      {showWeightGoalModal && (
        <div className="goals-modal-overlay" onClick={() => setShowWeightGoalModal(false)}>
          <div className="goals-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="goals-modal-header">
              <h3>{editingWeightGoalId ? 'Edit Weight Goal' : 'New Weight Goal'}</h3>
              <button className="modal-close-btn" onClick={() => setShowWeightGoalModal(false)}>×</button>
            </div>

            <div className="goals-form">
              <div className="goal-input-group">
                <label htmlFor="goal-weight">Goal Weight *</label>
                <div className="weight-input-group">
                  <input
                    id="goal-weight"
                    type="number"
                    step="0.1"
                    className="form-control"
                    placeholder="175.0"
                    value={goalWeight}
                    onChange={(e) => setGoalWeight(e.target.value)}
                    style={{ flex: 2 }}
                  />
                  <select
                    className="form-control"
                    value={weightUnit}
                    onChange={(e) => setWeightUnit(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="lbs">lbs</option>
                    <option value="kg">kg</option>
                  </select>
                </div>
              </div>

              <div className="goal-input-group">
                <label htmlFor="goal-target-date">Target Date *</label>
                <input
                  id="goal-target-date"
                  type="date"
                  className="form-control"
                  value={goalTargetDate}
                  onChange={(e) => setGoalTargetDate(e.target.value)}
                  min={getTodayLocalDate()}
                />
              </div>

              <div className="goal-input-group">
                <label htmlFor="goal-start-date">Start Date</label>
                <input
                  id="goal-start-date"
                  type="date"
                  className="form-control"
                  value={goalStartDate}
                  onChange={(e) => setGoalStartDate(e.target.value)}
                  max={getTodayLocalDate()}
                />
              </div>

              <div className="goal-input-group">
                <label htmlFor="goal-start-weight">Starting Weight (Optional)</label>
                <div className="weight-input-group">
                  <input
                    id="goal-start-weight"
                    type="number"
                    step="0.1"
                    className="form-control"
                    placeholder="185.5"
                    value={goalStartWeight}
                    onChange={(e) => setGoalStartWeight(e.target.value)}
                    style={{ flex: 2 }}
                  />
                  <span style={{ flex: 1, padding: '8px', color: '#666' }}>{weightUnit}</span>
                </div>
              </div>

              <div className="goal-input-group">
                <label htmlFor="goal-notes">Notes (Optional)</label>
                <textarea
                  id="goal-notes"
                  className="form-control"
                  rows="2"
                  placeholder="e.g., Summer goal, marathon prep..."
                  value={goalNotes}
                  onChange={(e) => setGoalNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="goals-modal-actions">
              <button
                className="save-btn"
                onClick={saveWeightGoal}
                disabled={!goalWeight || !goalTargetDate}
              >
                {editingWeightGoalId ? 'Update Goal' : 'Create Goal'}
              </button>
              <button className="cancel-btn" onClick={() => setShowWeightGoalModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MacroCalculator;